/* app/console.jsx - Stage 4: Account console (account-level, tabbed; renamed from "Workspace console" per SUITE-BOARD SD-001 - "workspace" now only means the ImyBoss suite container).
   Sites · Reports · Users · Approvals · Settings · Documentation · Pricing ·
   Billing · Organisation. Built from Huddle DS + Marketlens additions.
   Sites / Reports / Users are wired to /api/account/{reports,members}; the
   rest (Approvals, Billing, Pricing, Org) remain design previews - see
   docs/DESIGN-GAPS.md. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  const fmt = (n) => Number(n || 0).toLocaleString();
  const compact = (n) => { n = Number(n || 0); return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(n); };
  const timeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return s + "s ago";
    const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
    const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
    const d = Math.floor(h / 24); if (d < 30) return d + "d ago";
    return new Date(iso).toLocaleDateString();
  };
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;

  // The kinds of site that authenticate with a username and an Application
  // Password, which is the only reason those two fields ever appear. Named
  // here rather than written as "not crawl" in each `when`, because the list
  // stopped being the complement of one kind the moment SiteAtlas became its
  // own: SiteAtlas is a WordPress install and still must never be asked for a
  // password, since pairing is how it connects.
  const WP_KINDS = ["single", "wp_network"];

  // Tiny GET hook: returns { data, err, loading }. Re-runs when `path` changes.
  function useApi(path) {
    const [state, setState] = useState({ data: null, err: null, loading: true });
    useEffect(() => {
      let alive = true;
      setState({ data: null, err: null, loading: true });
      api(path).then(d => alive && setState({ data: d, err: null, loading: false }))
               .catch(e => alive && setState({ data: null, err: e, loading: false }));
      return () => { alive = false; };
    }, [path]);
    return state;
  }
  const Loading = ({ label = "Loading…" }) => <div style={{ padding: "32px 0", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>{label}</div>;
  const ErrorNote = ({ err }) => React.createElement(DS().Banner, { tone: "danger", title: "Couldn't load" }, (err && err.message) || "Request failed.");

  // App-styled modals (reuse the BI design-system modal shell) so dialogs match
  // the app instead of the browser's native prompt/confirm/alert.
  const Modal = (p) => React.createElement(window.MlBiModals._Modal, p);
  const ConfirmDialog = (p) => React.createElement(window.MlBiModals.ConfirmDialog, p);
  // `sub`, and a field's `label`, `hint` and `placeholder`, may each be a
  // function of the current values, and a field may carry `when(vals)` to show
  // itself only for some of them. Both exist for one dialog: adding a site now
  // means choosing between three KINDS of site, and a site with no CMS has no
  // credentials to ask for. Without conditional fields that dialog would have
  // to put the credential-less kind behind its own button, which is where it
  // was and where nobody looked for it: a person adding a site opens "Add
  // site" and expects the kinds to be in there.
  // `extraAction` is a node placed at the START of the footer, away from
  // Cancel and Save. Destructive actions live there: a delete belongs with the
  // thing it deletes, which is the edit dialog, and not as a second icon on a
  // card where it sits one slip away from Open.
  function PromptModal({ title, sub, fields, confirmLabel = "Save", onSubmit, onClose, extraAction = null }) {
    const { Field, Input, Banner, Select } = DS();
    const [vals, setVals] = useState(() => Object.fromEntries(fields.map(f => [f.key, f.value != null ? f.value : (f.type === "select" && f.options && f.options[0] ? f.options[0].value : "")])));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const set = (k, v) => setVals(s => ({ ...s, [k]: v }));
    const submit = async () => {
      setBusy(true); setErr(null);
      try { await onSubmit(vals); } catch (e) { setErr((e && e.message) || "Something went wrong"); setBusy(false); }
    };
    const dyn = (v) => (typeof v === "function" ? v(vals) : v);
    // Hidden fields keep their value in `vals` rather than being cleared: a
    // typed password surviving a glance at another kind of site is what a
    // person expects, and onSubmit decides which keys it actually reads.
    const shown = fields.filter(f => (f.when ? f.when(vals) : true));
    return (
      <Modal sm title={title} sub={dyn(sub)} onClose={busy ? () => {} : onClose}
        foot={<>
          {extraAction ? <span style={{ marginRight: "auto" }}>{extraAction}</span> : null}
          <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="bi-btn bi-btn--primary" onClick={submit} disabled={busy}>{busy ? "Working…" : dyn(confirmLabel)}</button>
        </>}>
        <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
          {err && <Banner tone="danger" title="Error">{err}</Banner>}
          {shown.map((f, i) => (
            <Field key={f.key} label={dyn(f.label)} hint={dyn(f.hint)}>
              {f.type === "select"
                ? <Select value={vals[f.key]} onChange={e => set(f.key, e.target.value)}>{(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
                : <Input type={f.type || "text"} value={vals[f.key]} autoFocus={i === 0} leadingIcon={f.icon} placeholder={dyn(f.placeholder)} onChange={e => set(f.key, e.target.value)} />}
            </Field>
          ))}
        </div>
      </Modal>
    );
  }
  function NoticeModal({ title, body, onClose }) {
    return (
      <Modal sm title={title} onClose={onClose} foot={<button className="bi-btn bi-btn--primary" onClick={onClose}>OK</button>}>
        <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8, whiteSpace: "pre-wrap", fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5 }}>{body}</div>
      </Modal>
    );
  }
  // Pair the WP plugin without typing a password anywhere: mint a short-lived
  // code, show it, and poll until the plugin's Settings page exchanges it
  // (the plugin creates its own Application Password and connects the site).
  function PairPluginModal({ onClose, onPaired }) {
    const { Banner } = DS();
    const [minted, setMinted] = useState(null);   // { id, code, expires_at }
    const [err, setErr] = useState(null);
    const [status, setStatus] = useState("pending"); // pending | used | expired
    const [result, setResult] = useState(null);
    const [tick, setTick] = useState(0);          // re-mint counter ("New code")
    useEffect(() => {
      let alive = true;
      setMinted(null); setErr(null); setStatus("pending"); setResult(null);
      api("/account/sites/pair-code", { method: "POST", body: "{}" })
        .then(r => { if (alive) setMinted(r); })
        .catch(e => { if (alive) setErr((e && e.message) || "Could not create a pairing code."); });
      return () => { alive = false; };
    }, [tick]);
    useEffect(() => {
      if (!minted || status !== "pending") return;
      const t = setInterval(() => {
        api(`/account/sites/pair-code/${minted.id}`).then(r => {
          setStatus(r.status);
          if (r.status === "used") { setResult(r.result); onPaired && onPaired(); }
        }).catch(() => { /* transient poll error: keep trying */ });
      }, 3000);
      return () => clearInterval(t);
    }, [minted, status]); // eslint-disable-line react-hooks/exhaustive-deps
    const connected = result ? (Number(result.added || 0) + Number(result.updated || 0)) : 0;
    return (
      <Modal sm title="Pair the WordPress plugin" sub="No password to copy anywhere: the plugin creates its own and connects this account."
        onClose={onClose}
        foot={<>
          {status === "expired" && <button className="bi-btn" onClick={() => setTick(t => t + 1)}>New code</button>}
          <button className="bi-btn bi-btn--primary" onClick={onClose}>{status === "used" ? "Done" : "Close"}</button>
        </>}>
        <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4, paddingBottom: 8, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          {err && <Banner tone="danger" title="Error">{err}</Banner>}
          {status === "used" ? (
            <Banner tone="success" title="Paired">{`Connected ${connected} site${connected === 1 ? "" : "s"}${result && result.source === "siteatlas" ? " (SiteAtlas)" : ""}. They are in the list now.`}</Banner>
          ) : (
            <>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Install and activate the Marketlens Connector plugin on your WordPress (v1.2.0 or later).</li>
                <li>In WordPress, open <b>Settings &gt; MarketLens</b>.</li>
                <li>Enter this code and click <b>Pair</b>. This page updates by itself.</li>
              </ol>
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                {minted
                  ? <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: ".3em", color: "var(--text-primary)", fontFamily: "var(--font-mono, monospace)" }}>{minted.code}</span>
                  : <span>Generating code…</span>}
              </div>
              {status === "expired"
                ? <Banner tone="warning" title="Code expired">Pairing codes last 15 minutes. Generate a new one.</Banner>
                : minted && <p style={{ margin: 0, textAlign: "center", color: "var(--text-muted)" }}>Waiting for the plugin… The code works once and expires in 15 minutes.</p>}
            </>
          )}
        </div>
      </Modal>
    );
  }
  // App-styled 2FA enablement: scan the server-rendered QR, verify a code, then
  // show one-time recovery codes.
  function TwoFaSetupModal({ onClose, onDone }) {
    const { Field, Input, Banner } = DS();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [recovery, setRecovery] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    useEffect(() => { api("/account/2fa/setup", { method: "POST", body: "{}" }).then(setSetup).catch(e => setErr((e && e.message) || "Setup failed")); }, []);
    const enable = async () => {
      if (!code.trim()) { setErr("Enter the 6-digit code."); return; }
      setBusy(true); setErr(null);
      try { const r = await api("/account/2fa/enable", { method: "POST", body: JSON.stringify({ code: code.trim() }) }); setRecovery(r.recovery_codes || []); }
      catch (e) { setErr((e && e.message) || "Invalid code"); setBusy(false); }
    };
    const finish = () => { onDone && onDone(); onClose(); };
    if (recovery) {
      return (
        <Modal sm title="Two-factor authentication enabled" onClose={finish} foot={<button className="bi-btn bi-btn--primary" onClick={finish}>Done</button>}>
          <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            <p style={{ marginTop: 0 }}>Save these recovery codes somewhere safe - each works once if you lose your authenticator.</p>
            <pre style={{ background: "var(--bg-surface)", padding: 12, borderRadius: 8, fontSize: 13, whiteSpace: "pre-wrap" }}>{recovery.join("\n")}</pre>
          </div>
        </Modal>
      );
    }
    return (
      <Modal sm title="Enable two-factor authentication" onClose={busy ? () => {} : onClose}
        foot={<><button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={enable} disabled={busy || !setup}>{busy ? "Verifying…" : "Enable"}</button></>}>
        <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4, paddingBottom: 8 }}>
          {err && <Banner tone="danger" title="Error">{err}</Banner>}
          {!setup ? <Loading label="Preparing…" /> : (
            <>
              {setup.qr && <img src={setup.qr} alt="2FA QR code" style={{ width: 176, height: 176, alignSelf: "center", borderRadius: 8 }} />}
              <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Scan the QR with your authenticator app, or enter this secret manually:</div>
              <code style={{ wordBreak: "break-all", fontSize: 13, background: "var(--bg-surface)", padding: "6px 8px", borderRadius: 6 }}>{setup.secret}</code>
              <Field label="6-digit code"><Input value={code} inputMode="numeric" placeholder="123456" autoFocus onChange={e => setCode(e.target.value)} /></Field>
            </>
          )}
        </div>
      </Modal>
    );
  }

  function Brand() {
    return (
      <span className="ml-brand">
        <span className="ml-brand__mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><circle cx="11" cy="11" r="2.6" /><path d="M16.5 16.5 21 21" /></svg>
        </span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}><span className="ml-brand__name">MarketLens</span><span className="ml-brand__by">by IMYBOSS</span></span>
      </span>
    );
  }

  // Top-right account avatar + menu (real initials), mirrors the app shell.
  function ConsoleAccount({ user, onLogout, onExit, onFoundations, go }) {
    const [open, setOpen] = useState(false);
    const name = (user && user.name) || (user && user.email) || "Account";
    const email = (user && user.email) || "";
    const initials = (name.match(/\b\w/g) || ["?"]).join("").slice(0, 2).toUpperCase();
    const nav = (fn, arg) => { setOpen(false); fn && fn(arg); };
    return (
      <div style={{ position: "relative" }}>
        <button className="ml-topavatar" onClick={() => setOpen(v => !v)} title={name} aria-label="Account menu"><span className="hds-avatar hds-avatar--sm" aria-hidden="true">{initials}</span></button>
        {open && <>
          <div className="ml-scrim" onClick={() => setOpen(false)} />
          <div className="ml-menu" style={{ top: 46, right: 0, minWidth: 240 }} role="menu">
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px 10px" }}><span className="hds-avatar hds-avatar--sm" aria-hidden="true">{initials}</span><span className="ml-account__meta"><span className="ml-account__name">{name}</span>{email && <span className="ml-account__email">{email}</span>}</span></div>
            <div className="ml-menu__sep" />
            <button className="ml-menu__item" onClick={() => nav(go, "settings")}><MlIcon name="user" size={16} /> Profile & credentials</button>
            <button className="ml-menu__item" onClick={() => nav(go, "settings")}><MlIcon name="key" size={16} /> Sessions & 2FA</button>
            <div className="ml-menu__sep" />
            <button className="ml-menu__item" onClick={() => nav(onExit)}><MlIcon name="chevron-left" size={16} /> Back to app</button>
            {onFoundations && <button className="ml-menu__item" onClick={() => nav(onFoundations)}><MlIcon name="palette" size={16} /> Design foundations</button>}
            <div className="ml-menu__sep" />
            <button className="ml-menu__item ml-menu__item--danger" onClick={() => nav(onLogout)}><MlIcon name="log-out" size={16} /> Sign out</button>
          </div>
        </>}
      </div>
    );
  }

  function ConsoleTopBar({ onExit, user, onLogout, onFoundations, go, readOnly, assist, assistOpen, onToggleAssist, back, up }) {
    const { IconButton } = DS();
    const AiAssistTrigger = window.MlAiDrawer && window.MlAiDrawer.AiAssistTrigger;
    return (
      <header className="hds-topbar">
        {/* The left of the bar was an empty spacer. A screen one level deeper
            than its tab offers its way out here, in the same place and the
            same clothes as Back to app, because it is the same kind of move. */}
        <div className="hds-topbar__spacer">
          {back && (
            <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" onClick={back.onClick}>
              <MlIcon name="chevron-left" size={16} />{back.label}
            </button>
          )}
        </div>
        <div className="hds-topbar__actions">
          {readOnly && <span className="ml-ro-badge" title="Your role has read-only access"><MlIcon name="eye" size={14} />Read-only</span>}
          {up
            ? <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" onClick={up.onClick} title={up.title}>
                <MlIcon name="chevron-left" size={16} />{up.label}
              </button>
            : <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" onClick={onExit}><MlIcon name="chevron-left" size={16} />Back to app</button>}
          {/* Only the trigger lives here; the panel is a COLUMN of the shell
              below, which is what lets it sit beside the screen rather than
              over it. It appears only while a screen has offered a site to
              point it at (the property screen does), and never for a
              read-only role: asking costs a POST, which api() refuses. */}
          {AiAssistTrigger && assist && !readOnly && (
            <AiAssistTrigger site={assist.site} moduleId={assist.moduleId} open={assistOpen} onToggle={onToggleAssist} />
          )}
          <IconButton icon="bell" label="Notifications" bordered size="sm" />
          <span className="hds-topbar__divider" />
          <ConsoleAccount user={user} onLogout={onLogout} onExit={onExit} onFoundations={onFoundations} go={go} />
        </div>
      </header>
    );
  }


  function roleBadge(r) {
    const { Badge } = DS();
    const tone = r === "Owner" ? "primary" : r === "Admin" ? "info" : "neutral";
    return <Badge tone={tone}>{r}</Badge>;
  }

  // ---- Add a property (the guided flow) ----
  // A property is an entire website, and its PUBLIC URL is the field the rest
  // of the model waits on (docs/PROPERTY-MODEL.md 4.3): a headless connection
  // tells us where pages are edited and never where they are published. So
  // question one is that URL, asked as a question rather than migrated in.
  //
  // Then: which COUNTRIES does the website target. A market is a country (3.8)
  // because MarketLens runs web searches in a market and search is geographic;
  // a competitor set, a volume and a SERP all answer "in which country". The
  // language rides along because content has one, but it does not decide.
  //
  // Section 7 sets two rules that shape every line below.
  //   Short for the common case: one website in one country is a URL, one
  //   prefilled market and a confirm. It must never feel like an interrogation.
  //   Suggest, never assume: the probe PREFILLS step 2 and says which rows it
  //   filled; the customer still declares the structure and can change it all.

  // A convenience list, not a closed set: the API accepts any country name, so
  // this exists only to save typing for the markets this product plausibly
  // serves. Each country carries the language it is most often served in, which
  // prefills the language on the row and is overridable there.
  const COUNTRIES = [
    { code: "DZ", name: "Algeria", lang: "ar" },
    { code: "AU", name: "Australia", lang: "en" },
    { code: "AT", name: "Austria", lang: "de" },
    { code: "BE", name: "Belgium", lang: "nl" },
    { code: "BR", name: "Brazil", lang: "pt-BR" },
    { code: "CA", name: "Canada", lang: "en" },
    { code: "CY", name: "Cyprus", lang: "el" },
    { code: "DK", name: "Denmark", lang: "da" },
    { code: "EG", name: "Egypt", lang: "ar" },
    { code: "EE", name: "Estonia", lang: "et" },
    { code: "FI", name: "Finland", lang: "fi" },
    { code: "FR", name: "France", lang: "fr" },
    { code: "DE", name: "Germany", lang: "de" },
    { code: "GR", name: "Greece", lang: "el" },
    { code: "IS", name: "Iceland", lang: "is" },
    { code: "IN", name: "India", lang: "en" },
    { code: "IE", name: "Ireland", lang: "en" },
    { code: "IT", name: "Italy", lang: "it" },
    { code: "JP", name: "Japan", lang: "ja" },
    { code: "LV", name: "Latvia", lang: "lv" },
    { code: "LI", name: "Liechtenstein", lang: "de" },
    { code: "LT", name: "Lithuania", lang: "lt" },
    { code: "LU", name: "Luxembourg", lang: "fr" },
    { code: "MT", name: "Malta", lang: "en" },
    { code: "MX", name: "Mexico", lang: "es" },
    { code: "MC", name: "Monaco", lang: "fr" },
    { code: "MA", name: "Morocco", lang: "fr" },
    { code: "NL", name: "Netherlands", lang: "nl" },
    { code: "NZ", name: "New Zealand", lang: "en" },
    { code: "NO", name: "Norway", lang: "nb" },
    { code: "PT", name: "Portugal", lang: "pt" },
    { code: "SA", name: "Saudi Arabia", lang: "ar" },
    { code: "SG", name: "Singapore", lang: "en" },
    { code: "ES", name: "Spain", lang: "es" },
    { code: "SE", name: "Sweden", lang: "sv" },
    { code: "CH", name: "Switzerland", lang: "de" },
    { code: "TN", name: "Tunisia", lang: "fr" },
    { code: "AE", name: "United Arab Emirates", lang: "ar" },
    { code: "GB", name: "United Kingdom", lang: "en" },
    { code: "US", name: "United States", lang: "en" },
  ];

  // Which country a bare language code most often means. This only turns a
  // DETECTED language into a SUGGESTED market, so a single-language site
  // arrives at step 2 already answered. It is wrong by construction for a
  // language spoken in many countries, which is exactly why the row carries a
  // "detected" badge and a country the user can change.
  const LANG_HOME = {
    en: "US", fr: "FR", de: "DE", es: "ES", it: "IT", nl: "NL", pt: "PT",
    sv: "SE", da: "DK", nb: "NO", nn: "NO", no: "NO", fi: "FI", is: "IS",
    et: "EE", lv: "LV", lt: "LT", el: "GR", ar: "AE", ja: "JP",
  };

  // How a market is reached inside the property. The values are the API's own
  // enum; the labels are what a customer would say out loud.
  const ADDRESSING = [
    { value: "none", label: "No separate address" },
    { value: "path", label: "A path prefix" },
    { value: "subdomain", label: "A subdomain" },
    { value: "domain", label: "A separate domain" },
  ];
  const ADDRESS_HINT = { path: "/fr", subdomain: "fr.example.com", domain: "example.fr" };
  // TWO steps, where there were five. Markets, Publishing and Sections are
  // gone from here, and the reason is the four coverages: each of them is a
  // coverage of the property measured against the whole of it, and a coverage
  // is declared in its own workflow rather than as a step in the one that
  // CREATES the thing being covered. Publishing and Sections were pure prose
  // anyway, two screens with nothing to fill in.
  //
  // What is left is the property itself: where it is published, what to call
  // it, and where its sitemap is. Nothing else is true of a property before
  // anything has been connected to it.
  const WIZARD_STEPS = [[1, "Website"], [2, "Review"]];

  // Rows need an identity that survives removal: a React key taken from the
  // array index remounts the inputs of every row after the removed one, which
  // loses focus and the caret mid-typing.
  let marketSeq = 0;
  const blankMarket = (o) => ({
    key: ++marketSeq, country: "", country_name: "", language: "",
    addressing: "none", address: "", is_default: false, detected: false, ...o,
  });
  const marketReady = (m) => !!m.country_name && (m.addressing === "none" || !!String(m.address || "").trim());
  const domainOf = (u) => String(u || "").trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").split(/[/?#]/)[0];
  // The API wants http(s). Typing "example.com" is what people do, so assume
  // the scheme rather than rejecting the input for missing it.
  const normalizeUrl = (s) => {
    const t = String(s || "").trim();
    if (!t) return "";
    return /^https?:\/\//i.test(t) ? t.replace(/\/+$/, "") : "https://" + t.replace(/^\/+/, "").replace(/\/+$/, "");
  };

  // A detected language becomes a suggested market: a region subtag names the
  // country outright (fr-CA), otherwise LANG_HOME guesses one. A language that
  // came with a path prefix is a market reached by that path, which is the
  // multilingual shape we see most often.
  function marketFromLanguage(l) {
    const code = String((l && l.language) || "").toLowerCase();
    const base = code.split("-")[0];
    const region = (code.split("-")[1] || "").toUpperCase();
    const c = (region && COUNTRIES.find(x => x.code === region)) || COUNTRIES.find(x => x.code === LANG_HOME[base]) || null;
    const prefix = (l && l.path_prefix) || "";
    return blankMarket({
      country: c ? c.code : "",
      country_name: c ? c.name : "",
      language: code,
      addressing: prefix ? "path" : "none",
      address: prefix,
      detected: true,
    });
  }

  // What the probe found, turned into editable rows. Declared languages win,
  // because a site saying "I serve fr and en" is a stronger fact than a path
  // that happens to look like a language code.
  function marketsFromProbe(p) {
    const det = (p && p.detected) || {};
    const langs = det.languages || [];
    const prefixes = det.path_prefixes || [];
    if (langs.length) {
      return langs.map(l => {
        const m = marketFromLanguage(l);
        // hreflang pointed at no prefix, but the sitemap carries one for that
        // language: the sitemap is the page universe, so it is the better
        // address of the two.
        if (m.addressing === "none") {
          const guess = "/" + String(l.language || "").toLowerCase().split("-")[0];
          if (prefixes.some(x => x.prefix === guess)) { m.addressing = "path"; m.address = guess; }
        }
        return m;
      });
    }
    if (prefixes.length) return prefixes.map(x => marketFromLanguage({ language: x.prefix.replace(/^\//, ""), path_prefix: x.prefix }));
    // Nothing found, which is the usual answer for a single-market website:
    // one blank row to name a country in, not an invented market.
    return [blankMarket()];
  }

  // The sitemap a property's crawl should read, derived from its public
  // address. Derived and not guessed at: /sitemap.xml is where the standard
  // puts it, it is what the crawler already falls back to when robots.txt
  // names none, and it is offered as an EDITABLE default precisely because a
  // real website is allowed to disagree. A probe that reads robots.txt and
  // finds the real one overrides this, which is a fact beating a convention.
  const defaultSitemap = (base) => {
    const b = String(base || '').trim();
    if (!/^https?:\/\//i.test(b)) return "";
    try { return new URL("/sitemap.xml", b).href; } catch (e) { return ""; }
  };

  // The same questions, used twice. With `property` it runs over one that
  // already exists, which is the only way an owner can repair a property made
  // before this flow existed: no public base, no markets and no crawl of its
  // own, so the product behaves two different ways depending on the day the
  // property was created. Editing is therefore not a convenience, it is how
  // 3.1 and 3.8 reach the properties that predate them.
  function AddPropertyWizard({ onClose, onSaved, onDelete, property = null }) {
    const { Field, Input, Select, Banner, Badge, Radio, Button } = DS();
    const editing = !!property;
    // A property with no public base still has a domain, and the address the
    // public sees is almost always www on it. Offering that as the prefill is
    // the whole point: the field is empty precisely for the properties being
    // repaired, and making the owner retype a URL we can infer is hostile.
    const initialUrl = editing ? (property.public_base || ("https://www." + property.root_domain)) : "";
    const propSites = (property && property.sites) || [];

    const [step, setStep] = useState(1);
    const [url, setUrl] = useState(initialUrl);
    const [name, setName] = useState(editing ? (property.name || "") : "");
    // A name that came off an existing property is a name somebody chose, so
    // it is treated as typed and never silently replaced by a changed domain.
    const [namedByHand, setNamedByHand] = useState(editing);
    const [probe, setProbe] = useState(null);       // the probe response, or null
    const [probedFor, setProbedFor] = useState(""); // which URL that response is for
    const [probing, setProbing] = useState(false);
    // The sitemap, and whether a person has taken it over. An answer typed by
    // hand is never replaced by a later derivation, including the probe's:
    // once somebody states where the sitemap is, re-reading the website is not
    // allowed to disagree with them silently.
    const [sitemap, setSitemap] = useState(() => (editing && property.sitemap_url) || defaultSitemap(initialUrl));
    const [sitemapByHand, setSitemapByHand] = useState(!!(editing && property.sitemap_url));
    const [primarySite, setPrimarySite] = useState(() => String((property && property.primary_site_id) || ""));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const target = normalizeUrl(url);
    const unchanged = editing && target === normalizeUrl(initialUrl);
    // With no probe (an unreachable website, or the user chose to skip the
    // check) the typed URL is the public base: setup only requires http(s).
    const publicBase = (probe && probe.public_base) || target;
    const detected = (probe && probe.detected) || {};
    const rootDomain = (probe && probe.root_domain) || (editing ? property.root_domain : "");
    // The probe answers "is this domain already a property here", which in edit
    // mode is YES and is the property being edited. Firing the warning on it
    // would refuse to save a property for being itself. It still fires for a
    // domain some OTHER property owns, which is the clash worth stopping.
    const sameProperty = (r) => !!(editing && r && r.root_domain === property.root_domain);
    const exists = !!(probe && probe.already_exists && !sameProperty(probe));

    // Editing the URL invalidates everything read from the old one, and the
    // sitemap follows the address unless somebody has typed their own: a
    // derived default that stayed behind after the address changed would point
    // the crawl at the previous website.
    const typeUrl = (v) => {
      setUrl(v); setProbe(null); setProbedFor(""); setErr(null);
      if (!namedByHand) setName(domainOf(v));
      if (!sitemapByHand) setSitemap(defaultSitemap(normalizeUrl(v)));
    };

    const canNext = step === 1 ? (!!target && !exists && !probing) : true;

    const next = async () => {
      setErr(null);
      if (step !== 1) { setStep(step + 1); return; }
      // Same URL as the last probe: keep the answer, and any edits made to it.
      if (probe && probedFor === target) { setStep(2); return; }
      // Edit mode with the address untouched: the property already knows its
      // own answer, so Next goes straight through. Reading the website again is
      // allowed, by changing the URL, but it is never the toll for saving a
      // property that was already set up.
      if (editing && unchanged) { setStep(2); return; }
      setProbing(true);
      try {
        const r = await api("/account/properties/probe", { method: "POST", body: JSON.stringify({ url: target }) });
        setProbe(r); setProbedFor(target);
        if (!namedByHand && r.root_domain) setName(r.root_domain);
        if (!(r.already_exists && !sameProperty(r))) {
          // A sitemap the website ITSELF names in robots.txt beats the derived
          // default, because it is a fact about this website rather than a
          // convention about websites. It still loses to an answer somebody
          // typed: see sitemapByHand.
          if (!sitemapByHand && r.detected && r.detected.sitemap_url) setSitemap(r.detected.sitemap_url);
          setStep(2);
        }
      } catch (e) {
        // The server distinguishes not-http, unreachable and a bad status, and
        // says which. Paraphrasing that throws away the diagnosis.
        setErr((e && e.message) || "Could not read that URL.");
      }
      setProbing(false);
    };

    // A website we cannot read is still a property: the crawl may be blocked,
    // or it may not be live yet. The probe only ever offered suggestions, so
    // skipping it costs the prefill and nothing else.
    const skipProbe = () => { setErr(null); setStep(2); };

    // A crawl site the customer already added that sits at the public address.
    // It is the property's pages, but it is NOT the property's own crawl until
    // somebody says so: adopting it makes it unbillable and makes it disappear
    // when the property does, and neither should happen because two URLs match.
    const adoptable = (!editing || !property || property.own_crawl_site_id) ? null
      : (property.sites || []).find(s =>
          s.source === "crawl" && !s.own_crawl &&
          String(s.url || "").trim().replace(/\/+$/, "").toLowerCase()
            === String(publicBase || "").trim().replace(/\/+$/, "").toLowerCase()) || null;
    const [adopt, setAdopt] = useState(true);

    const save = async () => {
      setBusy(true); setErr(null);
      // NO `markets` key, and its absence is load bearing. PATCH setup
      // replaces markets wholesale when it is given them, so sending an empty
      // list from a window that no longer asks about markets would DELETE every
      // market the property has. Absent means untouched; the market workflow
      // sends them when it exists.
      const body = JSON.stringify({
        public_base: publicBase,
        name: (name || "").trim() || undefined,
        // Empty means "no answer", which is not the same as the empty string:
        // the route reads null as nothing declared and falls back to finding
        // the sitemap the way it always has.
        sitemap_url: String(sitemap || "").trim() || null,
        adopt_crawl_site_id: (adoptable && adopt) ? adoptable.site_id : undefined,
      });
      try {
        if (editing) {
          await api(`/account/properties/${property.id}/setup`, { method: "PATCH", body });
          // The primary site is not part of setup, so it is its own call. Only
          // when it changed: the value shown may be the one DERIVED for a
          // property that never designated one, and sending it back would
          // silently turn that automatic choice into a fixed one.
          if (propSites.length && primarySite !== String(property.primary_site_id || "")) {
            await api(`/account/properties/${property.id}`, {
              method: "PATCH",
              body: JSON.stringify({ primary_site_id: primarySite ? Number(primarySite) : null }),
            });
          }
        } else {
          await api("/account/properties/setup", { method: "POST", body });
        }
        onSaved();
      } catch (e) {
        setErr((e && e.message) || (editing ? "Could not save the property." : "Could not create the property."));
        setBusy(false);
      }
    };

    // Back and Delete share the far left, away from Cancel and Save. A delete
    // belongs with the thing it deletes, which is this window now that the
    // pencil opens it, and not as a second icon on a card where it sits one
    // slip away from Open.
    const foot = (
      <>
        {(editing || step > 1) && (
          <span className="ml-wiz__foot-l">
            {editing && (
              <button type="button" className="bi-btn" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={() => onDelete && onDelete()} disabled={busy}>
                <MlIcon name="trash" size={15} />Delete property
              </button>
            )}
            {step > 1 && <button className="bi-btn" onClick={() => { setErr(null); setStep(step - 1); }} disabled={busy}>Back</button>}
          </span>
        )}
        {step === 1 && err && !probing && <button className="bi-btn" onClick={skipProbe}>Continue without checking</button>}
        <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
        {step < 2
          ? <button className="bi-btn bi-btn--primary" onClick={next} disabled={!canNext}>{probing ? "Reading the site…" : "Next"}</button>
          : <button className="bi-btn bi-btn--primary" onClick={save} disabled={busy}>{busy ? (editing ? "Saving…" : "Creating…") : (editing ? "Save property" : "Create property")}</button>}
      </>
    );

    return (
      <Modal wide
        title={editing ? `Edit ${property.name || property.root_domain}` : "Add a property"}
        sub={editing
          ? "The same questions, over a property that already exists: where it is published, what to call it, and where its sitemap is. Its markets are not touched here."
          : "A property is an entire website: one public address, a name, and the sitemap its pages are read from."}
        onClose={busy ? () => {} : onClose} foot={foot}>
        <div className="bi-modal__body">
          <div className="ml-steps ml-steps--wiz">
            {WIZARD_STEPS.map(([n, label], i) => (
              <React.Fragment key={n}>
                <div className={["ml-step", step === n ? "ml-step--active" : "", step > n ? "ml-step--done" : ""].filter(Boolean).join(" ")}>
                  <span className="ml-step__dot">{step > n ? <MlIcon name="check" size={13} /> : n}</span>
                  <span className="ml-step__name">{label}</span>
                </div>
                {i < WIZARD_STEPS.length - 1 && <span className="ml-step__line" />}
              </React.Fragment>
            ))}
          </div>
          <div className="ml-wiz">
            {err && <Banner tone="danger" title="That did not work">{err}</Banner>}

            {step === 1 && (
              <>
                <p className="ml-wiz__lede">The address the public sees. Everything else hangs off it, so for a headless website this is where the pages are PUBLISHED, not the CMS where they are edited.</p>
                {/* The repair, said out loud. A property made before this flow
                    existed has no public base, which is the field everything
                    else waits on (4.3), so the one thing it is missing is the
                    first thing it is asked. */}
                {editing && !property.public_base && (
                  <Banner tone="info" title="This property has no public address yet.">
                    It was created before these questions were asked, so nothing knows where its pages are published. We have filled in the usual answer for {property.root_domain}. Correct it if it is wrong, then carry on: saving also gives the property the crawl of its own pages it should have had.
                  </Banner>
                )}
                <Field label="Public website address" required hint={editing ? "Changing it moves the property to that address. We read the page and its sitemap again when you do." : "One property per website. We read this page and its sitemap to offer you a starting point."}>
                  <Input leadingIcon="globe" value={url} autoFocus placeholder="https://example.com"
                    onChange={e => typeUrl(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && canNext) next(); }} />
                </Field>
                <Field label="Display name" hint="Optional. What this website is called inside MarketLens.">
                  {/* No leadingIcon. `layers` lives in icons.jsx EXTRA and not
                      in the design system's own set, so passing it here
                      rendered an empty 16px svg: a blank space beside the
                      text, correctly sized and completely invisible. Reported
                      on sight, and the fifth time this class of bug has been
                      found. Verified by measuring the rendered svg, which had
                      zero shapes in it. */}
                  <Input value={name} placeholder={domainOf(url) || "example.com"}
                    onChange={e => { setNamedByHand(true); setName(e.target.value); }} />
                </Field>
                {/* THE THIRD FIELD, and the only one with a default worth
                    explaining. The crawl of this property reads this sitemap,
                    and the sitemap IS the page universe every coverage is
                    counted against, so an address pointing at the wrong one
                    does not produce a small error, it produces a different
                    website. Filled from the public address, overridden by what
                    robots.txt actually names, and editable over both. */}
                <Field label="Sitemap"
                  hint={sitemapByHand
                    ? "Yours. Re-reading the website will not change it."
                    : "Filled in from the address above. If the website names a different one in robots.txt we use that instead, and you can always type your own."}>
                  <Input leadingIcon="file-text" value={sitemap} placeholder={defaultSitemap(target) || "https://example.com/sitemap.xml"}
                    onChange={e => { setSitemapByHand(true); setSitemap(e.target.value); }} />
                </Field>
                {probing && (
                  <div className="ml-wiz__busy"><span className="ml-spinner ml-spinner--sm" />Reading the page and looking for a sitemap. This takes a few seconds.</div>
                )}
                {/* An alias domain that permanently redirects is the SAME
                    website, so answering about the destination is right. Doing
                    it without mentioning the redirect is not: reported on the
                    first real use, where capitaineagile.com was refused as
                    "capitaineagile.fr is already here", which is true and
                    reads exactly like a bug. */}
                {exists && (
                  <Banner tone="warning" title="Already here.">
                    {probe.redirected
                      ? `${domainOf(probe.requested_url)} redirects to ${domainOf(probe.public_base)}, so it is another address for that same website rather than a separate one, and a property for ${probe.root_domain} is already in this account. Open it from the Properties list instead, or enter a different website.`
                      : `A property for ${probe.root_domain} is already in this account. Open it from the Properties list instead, or enter a different website.`}
                  </Banner>
                )}
                {/* Not an error, but it must not be a surprise either: the
                    property is about to be created under a domain the customer
                    did not type. */}
                {!exists && probe && probe.redirected && (
                  <Banner tone="info" title="That address redirects.">
                    {`${domainOf(probe.requested_url)} sends visitors to ${domainOf(probe.public_base)}, so the property will be ${editing ? "saved" : "created"} for ${probe.root_domain}. That is usually what you want: one property per website, whatever addresses point at it.`}
                  </Banner>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <p className="ml-wiz__lede">This is the declaration about to be saved.</p>
                <div className="ml-wiz__review">
                  <div className="ml-wiz__kv"><span>Public URL</span><b>{publicBase}</b></div>
                  <div className="ml-wiz__kv"><span>Name</span><b>{(name || "").trim() || domainOf(publicBase)}</b></div>
                  {rootDomain && <div className="ml-wiz__kv"><span>Domain</span><b>{rootDomain}</b></div>}
                  <div className="ml-wiz__kv"><span>Sitemap</span><b>{String(sitemap || "").trim() || "none declared, so it will be looked for"}</b></div>
                  {detected.pages != null && detected.sitemap_url && String(detected.sitemap_url) === String(sitemap || "").trim() && (
                    <div className="ml-wiz__kv"><span>Pages listed</span><b>{fmt(detected.pages)}</b></div>
                  )}
                </div>
                {/* Which site opens first is the one property setting that was
                    never a question in these five steps, and it used to live in
                    a dialog this window replaced. It belongs on Review, beside
                    everything else about to be saved. */}
                {editing && propSites.length > 0 && (
                  <Field label="Primary site" hint="The connected site that opens when you open this property. The in-app switcher then moves between this property's sites.">
                    <Select value={primarySite} onChange={e => setPrimarySite(e.target.value)}>
                      {propSites.map(s => (
                        <option key={s.site_id} value={String(s.site_id)}>
                          {(s.name || s.url) + ", " + String(s.url || "").replace(/^https?:\/\//, "")}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {/* Every property keeps a crawl of its own pages, and this one
                    already has a site crawling exactly that address. Adopting
                    it avoids a pointless second crawl of the same sitemap, but
                    it is asked rather than assumed: adopting makes the site
                    unbillable and makes it disappear when the property does,
                    and neither should follow from two URLs matching. */}
                {adoptable && (
                  <Field label="The crawl of this website"
                    hint="Adopting changes nothing about the pages. It records that this crawl belongs to the property rather than being a site you manage separately.">
                    <label className="ml-wiz__adopt">
                      <input type="checkbox" checked={adopt} onChange={e => setAdopt(e.target.checked)} />
                      <span>
                        <b>{adoptable.name}</b> already crawls {String(publicBase || "").replace(/^https?:\/\//, "")}.
                        Use it as this property's own crawl, so it stops counting against your plan
                        and is removed if the property is ever deleted.
                        {!adopt && <><br />Left unticked it stays a site you manage, and this property keeps no crawl of its own.</>}
                      </span>
                    </label>
                  </Field>
                )}
                <ul className="ml-wiz__opts">
                  <li className="ml-wiz__opt"><MlIcon name="globe" /><span>{editing
                    ? <><b>Only these three answers change.</b> No market, connected site, content, keyword or history is affected.</>
                    : <><b>The property is created.</b> Nothing else in the account changes.</>}</span></li>
                  {editing && !property.own_crawl_site_id && (
                    <li className="ml-wiz__opt"><MlIcon name="search" /><span><b>It also gets the crawl of its own pages it never had.</b> Every property keeps one, because that crawl IS its page universe and coverage becomes arithmetic instead of guesswork. It starts reading as soon as you save.</span></li>
                  )}
                  <li className="ml-wiz__opt"><MlIcon name="flag" /><span><b>Markets are declared separately.</b> A market is one of the four coverages of this property, so it has its own workflow rather than a step in here, and Business Intelligence still lives per market.</span></li>
                  <li className="ml-wiz__opt"><MlIcon name="plus" /><span><b>Connect whenever you like.</b> One WordPress, a SiteAtlas install, a multisite network, or a site with no CMS at all, from <b>Add site</b> on the property itself.</span></li>
                </ul>
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // ---- tabs ----
  // Properties: one card per domain root (e.g. capitaineagile.fr) grouping all
  // its sites (cms.x.fr, x.fr/blog, …). Opening a property enters the app on
  // its primary site; the in-app switcher then moves between the property's
  // sites only. Metrics are the per-site /account/reports rollups, summed.
  // Sites the customer ADDED, which excludes the crawl a property keeps of
  // itself. Only these block a delete, because only these were a choice.
  const addedSites = (p) => (p.sites || []).filter(s => Number(s.site_id) !== Number(p.own_crawl_site_id));

  // ---- One property, opened ----
  // The Properties card used to leave the console for the Sites tab. A
  // property is the unit a customer calls "my website", so it gets a screen of
  // its own: what it IS, the countries it targets, the pieces it is made of,
  // and the crawl it keeps of itself.
  //
  // Everything here comes from GET /properties/:id/structure and nothing is
  // invented on top of it. Two limits from docs/PROPERTY-MODEL.md 2b are
  // written into the copy rather than hidden by it: SECTIONS are not modelled,
  // so a blog is recognised from its path and labelled detected wherever it
  // appears; and nothing is scoped BY market yet, so the markets block is a
  // declaration and never a filter on keywords, topics or BI.

  // The setup of one node, in the words a customer would use.
  const SETUP_WORDS = {
    property_crawl: "The crawl this property keeps of itself",
    standalone: "Read by crawling, no CMS",
    siteatlas: "SiteAtlas directory",
    wp_network: "Site in a WordPress network",
    wp_single: "WordPress",
  };
  // The same kinds as the parts of the one sentence saying what this website
  // IS. `property_crawl` is deliberately absent: every property has one by
  // construction (PROPERTY-MODEL.md 3.1), so naming it would describe the
  // product rather than the customer's website. The crawl block reports it.
  const SETUP_PARTS = {
    siteatlas : (n) => (n === 1 ? "a SiteAtlas directory" : n + " SiteAtlas directories"),
    wp_network: (n) => (n === 1 ? "one site in a WordPress network" : n + " sites in a WordPress network"),
    wp_single : (n) => (n === 1 ? "one WordPress site" : n + " WordPress sites"),
    standalone: (n) => (n === 1 ? "a front end with no CMS, read by crawling" : n + " front ends with no CMS, read by crawling"),
  };
  const SETUP_ORDER = ["siteatlas", "wp_network", "wp_single", "standalone"];
  const joinWords = (xs) => (xs.length < 2 ? (xs[0] || "") : xs.slice(0, -1).join(", ") + " and " + xs[xs.length - 1]);
  const hostOnly = (u) => { try { return new URL(/^https?:\/\//i.test(u) ? u : "https://" + u).hostname.toLowerCase(); } catch { return null; } };

  // One honest sentence, and a sentence about a website has to carry the
  // RELATIONSHIP between its pieces. Listing "2 SiteAtlas directories and a
  // front end with no CMS" as peers describes three websites; the customer
  // has one. What makes it one website is that the directories are edited in
  // one place and published in another, and the second place is what the
  // crawl reads.
  //
  // The relationship is only ASSERTED where it is known. "Publishing into"
  // needs every piece to have said where it publishes; until then the pieces
  // are named next to each other and the coverage block asks the question,
  // because a sentence that quietly assumes the answer is how this screen
  // came to describe a setup the customer did not have.
  function setupSentence(setups, nodes, publicBase) {
    const cms     = nodes.filter(n => n.can_publish);
    const crawled = nodes.filter(n => !n.can_publish);
    const frontEnds = crawled.length > 1
      ? crawled.length + " front ends with no CMS that MarketLens reads by crawling"
      : "a front end with no CMS that MarketLens reads by crawling";

    if (!cms.length) {
      if (!crawled.length) return "Nothing is connected to this website yet, and it has no crawl of its own either.";
      return crawled.some(n => n.setup === "property_crawl") && crawled.length === 1
        ? "Nothing is connected to this website yet, so the only thing reading it is the crawl it keeps of its own pages."
        : "This website is " + frontEnds + ".";
    }

    const kinds = SETUP_ORDER.filter(k => cms.some(n => n.setup === k))
      .map(k => SETUP_PARTS[k](cms.filter(n => n.setup === k).length));
    let s = "This website is " + joinWords(kinds);

    // Where the content is edited, which is what site_url holds. One shared
    // host that is not the public address is a headless setup, and saying so
    // is the difference between two directories and one CMS serving two.
    const editHosts = [...new Set(cms.map(n => hostOnly(n.url)).filter(Boolean))];
    const pub = hostOnly(publicBase);
    if (editHosts.length === 1 && pub && editHosts[0] !== pub) {
      s += cms.length > 1
        ? ", both edited on one headless WordPress at " + editHosts[0]
        : ", edited on a headless WordPress at " + editHosts[0];
      if (cms.length > 2) s = s.replace(", both edited on", ", all edited on");
    }
    if (crawled.length) {
      s += cms.every(n => n.published_base) ? ", publishing into " + frontEnds : ", alongside " + frontEnds;
    }
    return s + ".";
  }

  // How a market is reached, in the same vocabulary the wizard collected it in.
  const ADDRESSING_WORDS = {
    none: "No separate address",
    path: "A path prefix",
    subdomain: "A subdomain",
    domain: "A separate domain",
  };
  const SECTION_WORDS = { blog: "Blog", documentation: "Documentation", help: "Help", shop: "Shop" };
  const SECTION_ICONS = { blog: "file-text", documentation: "file", help: "message-circle", shop: "shopping-bag" };
  // A BCP-47 tag as a language name, with the tag as the fallback: the tag is
  // what is stored, and it is never wrong, only terse.
  const languageName = (tag) => {
    const t = String(tag || "").trim();
    if (!t) return "";
    try { return new Intl.DisplayNames([navigator.language || "en"], { type: "language" }).of(t) || t; }
    catch { return t; }
  };
  const hostPath = (u) => String(u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");

  // The house pattern for a list (feedback-compact-ui-lines-drawers, with
  // app/report-drawer.jsx as the precedent): the line carries the facts and
  // the one action, the drawer carries everything that explains them. The line
  // here holds more than DetailLine's single title can, so this uses the
  // design system's own Drawer directly and adds the same Escape handling
  // DetailLine does, because the DS drawer does not listen for it.
  function OpenLine({ icon = "circle", tone = "neutral", lead, action, drawerTitle, drawerSub, children }) {
    const [open, setOpen] = useState(false);
    const ds = DS();
    const Drawer = ds && ds.Drawer;
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    return (
      <>
        <div className={"ds-line ds-line--" + tone} role="button" tabIndex={0}
             aria-expanded={open} title="Open the details"
             onClick={() => setOpen(true)}
             onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } }}>
          <span className="ds-card__icon"><MlIcon name={icon} size={15} /></span>
          <span className="ds-line__title ml-propline">{lead}</span>
          {/* The action keeps its own click: reaching it must never cost the
              user a trip through the drawer. */}
          {action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
          <span className="ds-card__icon ml-rdrawer-chev"><MlIcon name="chevron-right" size={15} /></span>
        </div>
        {open && (Drawer
          ? <Drawer title={drawerTitle} subtitle={drawerSub} onClose={() => setOpen(false)}>{children}</Drawer>
          : <div className="hds-card hds-card--pad">{children}</div>)}
      </>
    );
  }
  // A key and its value: the shape every drawer in here uses.
  const Fact = ({ k, children }) => (
    <div className="ml-usage__row"><span>{k}</span><b className="ml-propfact__v">{children}</b></div>
  );

  // One filter chip. The count is part of the label, and a chip counting zero
  // is DISABLED rather than hidden: "no blog on this property" is a fact worth
  // seeing, and a chip that quietly disappears says nothing at all.
  const FilterChip = ({ id, label, count, hint, active, onPick }) => (
    <button type="button" className={"ml-chip" + (active ? " ml-chip--selected" : "")}
      disabled={!count} aria-pressed={!!active}
      title={hint ? label + ": detected from each address, not declared" : label}
      onClick={() => onPick(id)}>
      {label}
      {hint && <span className="ml-chip__hint">{hint}</span>}
      <span className="ml-chip__count">{count}</span>
    </button>
  );

  // One gap, as a number, a reason and the one thing that changes it. A
  // percentage on its own tells somebody they are at 64% and leaves them
  // there; this turns the same figure into work. The action is optional,
  // because some gaps are closed somewhere else in the app and inventing a
  // button that goes nowhere is worse than naming the screen.
  function GapLine({ n, what, why, action = null }) {
    return (
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ minWidth: 64, fontSize: "var(--text-lg)", fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.2 }}>
          {fmt(n)}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{what}</div>
          <div className="ml-section__sub">{why}</div>
          {action ? <div style={{ marginTop: 2 }}>{action}</div> : null}
        </div>
      </div>
    );
  }

  // ── The property level database migration ──────────────────
  //
  // docs/PROPERTY-DB-MIGRATION.md is being run against a live database one
  // step at a time. The three libraries and the four endpoints behind
  // /api/property-db are committed and tested, and until this panel existed
  // nothing in the app could reach them: the only way to run a step was curl,
  // which means the person who owns the data could not run it and could not
  // read what came back. This is that place.
  //
  // IT LIVES ON THE PROPERTY SCREEN, and nowhere else. Every one of the four
  // endpoints takes a property id and refuses without one, the reports name
  // this website's own pieces, and the console is where properties live. It is
  // deliberately NOT in the app's sidebar: a sidebar entry would make this
  // ordinary product rather than the temporary operator surface
  // routes/property-db.js says it is, and section 5 of the document ends with
  // all of it being deleted in one commit.
  //
  // THE RULE THIS PANEL IS WRITTEN AGAINST, because it is the rule the whole
  // migration is written against: a report may not be summarised. Every one of
  // these reports is built to say what it could NOT determine, which rows are
  // not derivable and why, which lists were capped and what the true total
  // was. A tick, a percentage or a single number in place of one of those
  // sentences throws away the only thing the report was written for, and the
  // operator who then adds a unique key at boot on the strength of it gets an
  // outage. So: every count on screen is the field the API provided for it and
  // never the length of a list that arrived capped, and "could not be checked"
  // is never drawn the way "clear" is.
  //
  // Three lines, one per step, each opening its own report. One of them writes
  // and says so; the other two read and say so.

  // What the backfill did to one table, in words. The library's own state name
  // is kept beside them in the drawer rather than translated away: `capped` is
  // the word the report uses, and an operator comparing the screen with a
  // response should meet the same word twice.
  const BF_STATE_WORDS = {
    done  : "Filled in",
    capped: "Cut short by this call's row limit",
    empty : "Read and empty",
    absent: "Not in this database",
    error : "Could not be read",
  };

  // A verdict is three valued and stays three valued on screen. Clear, blocked
  // and unknown are different answers and only ONE of them is permission to
  // add a unique key, so none of them may be drawn as another. Unknown carries
  // the loudest badge on purpose: the conflict report ranks it above blocked
  // because an unchecked table is not permission, while blocked is at least a
  // known no.
  const VERDICT_WORDS = {
    clear  : { word: "Clear",               badge: "success" },
    blocked: { word: "Clashes found",        badge: "warning" },
    unknown: { word: "Could not be checked", badge: "danger"  },
  };

  // Which of the two readings found a row, and what that fact means. They are
  // never merged into "differs": a row only the old reading finds has not been
  // reached by the new columns, and a row only the new reading finds has
  // changed section. Those are different findings with different repairs.
  const SIDE_WORDS = {
    site_scoped    : "found only by the old reading, so the new columns have not reached it",
    property_scoped: "found only by the new reading, so its section has changed",
  };

  function PropertyDbPanel({ property, nodes }) {
    const { Banner, Button, Field, Select } = DS();

    // ── Who may run the write ──
    //
    // PropertyScreen is handed a property and nothing at all about the signed
    // in person, so the role has to be asked for. GET /account answers `role`
    // straight off req.auth.role, which is the EXACT value the backfill
    // endpoint gates on (`['owner','admin'].includes(req.auth.role)`), so the
    // button and the endpoint cannot reach different conclusions about one
    // person. It is also the idiom this console already uses twice: Users and
    // Settings both read acct.role and hide their actions with it.
    //
    // Until the answer arrives nothing is assumed, and a FAILED read is read
    // as neither permission nor refusal: the line says so and offers no
    // button, because a Run button that can only answer 403 is worse than no
    // Run button at all.
    const [who, setWho] = useState(null);          // null while reading | { role } | { error }
    useEffect(() => {
      let alive = true;
      api("/account")
        .then(d => { if (alive) setWho({ role: (d && d.role) || null }); })
        .catch(e => { if (alive) setWho({ error: (e && e.message) || "your role could not be read" }); });
      return () => { alive = false; };
    }, []);
    const mayRun = !!(who && (who.role === "owner" || who.role === "admin"));

    // ── The tables this migration touches, from the server ──
    //
    // The dual read has to offer a choice of table, and the list of tables is
    // BACKFILL_TABLES in backend/lib/property-backfill.js: twenty three names
    // maintained by hand on purpose. Copying them into this file would make a
    // second source of truth that drifts the first time a table is added, so
    // the list is read from the server instead.
    //
    // It is read from a REFUSAL, which is the deliberate part. The reconcile
    // endpoint has no default table ("the answer would come back looking like
    // a reconciliation of whatever the caller had in mind"), and its refusal
    // carries the list under `known` for exactly this reason. The request
    // reads nothing and writes nothing: it is refused before a row is touched.
    const [tables, setTables] = useState(null);    // null while reading | string[] | { error }
    useEffect(() => {
      let alive = true;
      api(`/property-db/properties/${property.id}/reconcile`)
        .then(() => { if (alive) setTables({ error: "the list of tables could not be read: the request that should have been refused was answered instead." }); })
        .catch(e => {
          if (!alive) return;
          const known = e && e.data && Array.isArray(e.data.known) ? e.data.known : null;
          setTables(known || { error: (e && e.message) || "the list of tables could not be read." });
        });
      return () => { alive = false; };
    }, [property.id]);
    const tableList = Array.isArray(tables) ? tables : null;

    // A section id as a name. The property's own pieces are the sections
    // during the identity period, so the list this screen already has IS the
    // lookup. An id that is not among them is named rather than hidden: it
    // means the column holds a connection that has since been disconnected, or
    // an id the backfill wrote and nothing owns, and both are findings.
    const sectionName = (id) => {
      if (id === null || id === undefined) return "No section, so it belongs to this website and to none of its pieces";
      const n = nodes.find(x => Number(x.site_id) === Number(id));
      return n ? n.name : `Section ${id}, which is not one of the pieces above`;
    };

    // ── Step 2: the backfill, the only write on this panel ──
    //
    // `pending` is what the last answer said is left to do, and it is held in
    // state and never stored. Resuming from the cursor skips rows an earlier
    // call already visited, which is right while nothing about the property
    // has changed and WRONG the moment a publish address is declared or
    // edited, because pages that claimed no section before may claim one now
    // and they sit behind the cursor. That is a judgement about what the owner
    // just did, so the panel offers both and says which is which rather than
    // choosing for them.
    //
    // It holds `more` AND `cursor` because they are not the same fact. A table
    // whose rows are filled in by a plain statement has no cursor: it only
    // ever selects rows with no value, so running it again continues by
    // itself. Only the crawl needs one, because a page no section claims is
    // left exactly as it was found and would be re-read forever without it.
    // Keeping the two apart is what stops the button saying "carry on" while
    // quietly starting over.
    const [bf, setBf] = useState(null);            // null | { busy } | { data, resumed } | { error, code }
    const [pending, setPending] = useState(null);  // null | { more, cursor }
    const runBackfill = async (cursor) => {
      setBf({ busy: true });
      try {
        // No max_rows_per_table. The route's own default is one chunk per
        // table per call, which is the smallest unit of real progress and a
        // shape of request that always answers; sending a number from here
        // would be this screen inventing a second default in a third place.
        const body = cursor ? { resume_after: cursor } : {};
        const d = await api(`/property-db/properties/${property.id}/backfill`, {
          method: "POST", body: JSON.stringify(body),
        });
        setBf({ data: d, resumed: !!cursor });
        const cont = (d && d.continuation) || null;
        const next = cont && cont.resume_after && Object.keys(cont.resume_after).length ? cont.resume_after : null;
        setPending(cont && cont.more ? { more: true, cursor: next } : null);
      } catch (e) {
        // `pending` is deliberately left as it was: it describes the last run
        // that actually answered, and a failed request did not change what is
        // left to do.
        setBf({ error: (e && e.message) || "the fill could not be run.", code: e && e.data && e.data.code });
      }
    };

    // ── Step 4: the conflict report ──
    //
    // Asked for rather than loaded with the screen. It is ten full table
    // aggregates over a live customer database, run sequentially on purpose,
    // and a report of that weight firing every time somebody opens a property
    // is a cost nobody asked for. Not checked yet is a state and it is said on
    // the line.
    const [cf, setCf] = useState(null);            // null | { busy } | { data } | { error }
    const runConflicts = async () => {
      setCf({ busy: true });
      try {
        const d = await api(`/property-db/properties/${property.id}/conflicts`);
        setCf({ data: d });
      } catch (e) {
        setCf({ error: (e && e.message) || "the clash report could not be run.", code: e && e.data && e.data.code });
      }
    };

    // ── Step 3: the dual read ──
    //
    // Two answers to one question, and the table has NO default for the same
    // reason the endpoint refuses to invent one. The section does: the empty
    // value means the whole website, which is the property scope, and that is
    // a real choice rather than a missing one.
    const [table, setTable] = useState("");
    const [section, setSection] = useState("");
    const [du, setDu] = useState(null);            // null | { busy } | { data } | { error, refusal }
    const runReconcile = async () => {
      if (!table) return;
      setDu({ busy: true });
      try {
        const q = section ? `&section_id=${encodeURIComponent(section)}` : "";
        const d = await api(`/property-db/properties/${property.id}/reconcile?table=${encodeURIComponent(table)}${q}`);
        setDu({ data: d, table, section });
      } catch (e) {
        // A refusal carries a sentence the screen must not paraphrase: for a
        // connection whose property has not been derived yet it names the
        // backfill to run, which is the whole answer.
        setDu({
          error  : (e && e.message) || "the comparison could not be run.",
          refusal: (e && e.data && e.data.refusal) || null,
          table, section,
        });
      }
    };

    const bfReport = bf && bf.data ? bf.data.report : null;
    const bfCont   = bf && bf.data ? bf.data.continuation : null;
    const bfBounds = bf && bf.data ? bf.data.bounds : null;
    const cfReport = cf && cf.data ? cf.data.report : null;
    const duReport = du && du.data ? du.data.report : null;
    const duScope  = du && du.data ? du.data.scope : null;

    // What the backfill line says before anything has been run. It can only
    // speak for this screen: the columns may already hold values a shell run
    // wrote months ago, and the report is what tells you how many were
    // already set, so the line says "from this screen" and the drawer says the
    // rest.
    const bfLead = bf && bf.busy ? "Running, which can take a moment on a big table"
      : bf && bf.error ? bf.error
      : bfReport ? `${fmt(bfReport.totals.property_id_written)} website labels and ${fmt(bfReport.totals.section_id_written)} section labels written, over ${fmt(bfReport.totals.rows_in_scope)} rows in scope`
      : "Nothing has been run from this screen yet";

    const cfLead = cf && cf.busy ? "Reading every key, one table at a time"
      : cf && cf.error ? cf.error
      : cfReport ? cfReport.why
      : "Not checked yet";

    // A refusal's own sentence wins over the generic message, because it is
    // the one that says what to do about it: a section whose website has not
    // been worked out yet comes back naming the backfill to run.
    const duLead = du && du.busy ? "Asking the same question twice"
      : du && du.error ? ((du.refusal && du.refusal.why) || du.error)
      : duReport ? `${duReport.table}: ${duReport.why}`
      : "Nothing has been compared yet";

    return (
      <div className="ml-propblock">
        <h2 className="ml-section__h">Moving this website's data to the property</h2>
        <p className="ml-section__sub">
          Every row of data this website holds records which site it arrived through. The property model records two more things beside that: which website the row belongs to, and which section of the website it is about. These three steps fill those in, check whether anything would clash once a row's identity moves to the website, and compare the old answer with the new one. Nothing on any screen reads the new values yet, so running any of this changes nothing you can see.
        </p>
        <div className="ds-lines">

          {/* ── Step 2, the write ─────────────────────────────
              The only line here that changes anything, and it is marked twice:
              the badge says it writes and the button is the primary one.
              The other two lines carry a Read only badge, which is the same
              badge this screen already puts on a site nothing publishes to. */}
          <OpenLine icon="play" tone="todo"
            drawerTitle="Fill in the website and the section"
            drawerSub="The one step on this panel that writes"
            action={mayRun ? (
              <Button variant="primary" leadingIcon={<MlIcon name="play" size={18} />}
                disabled={!!(bf && bf.busy)}
                onClick={() => runBackfill(pending && pending.cursor)}
                title={pending
                  ? "Carry on from where the last run stopped. This writes to your database."
                  : "This writes to your database: it fills in the two new values on every row that has none. It never changes a value that is already there."}>
                {bf && bf.busy ? "Writing…" : (pending ? "Carry on writing" : "Fill them in")}
              </Button>
            ) : null}
            lead={<>
              <span className="ml-propline__name">Fill in the website and the section on every row</span>
              <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />Writes to your data</span>
              <span className="ml-propline__meta">{bfLead}</span>
              {/* From `pending` and not from the last response, so a request
                  that failed afterwards cannot make work that is still
                  outstanding disappear off the line. */}
              {pending
                ? <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />More to do</span> : null}
              {bf && bf.error ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Did not run</span> : null}
              {/* The role, said on the line rather than only in the drawer: a
                  missing button with no sentence beside it reads as a screen
                  that forgot to draw something. */}
              {!mayRun && who && who.error
                ? <span className="ml-propline__meta">Your role could not be read, so this write is not offered</span>
                : !mayRun && who
                  ? <span className="ml-propline__meta">Only the account owner or an admin can run this</span>
                  : !mayRun ? <span className="ml-propline__meta">Checking what your role may run</span> : null}
            </>}>

            {!mayRun && (
              <div style={{ marginBottom: 14 }}>
                {who && who.error
                  ? <Banner tone="danger" title="Your role could not be read">
                      {who.error} The write is not offered rather than offered and refused, because the only thing a button that cannot work can do is waste the one press somebody trusted it with.
                    </Banner>
                  : who
                    ? <Banner tone="info" title="Only the account owner or an admin can run this">
                        Filling these in writes two values on every row of twenty three tables, so it is gated exactly like declaring where a piece publishes. The two reports below read and change nothing, and they are open to you.
                      </Banner>
                    : <Banner tone="info" title="Checking what your role may run">
                        The answer comes from the same place the server reads it, so this screen will not offer a write the endpoint would refuse.
                      </Banner>}
              </div>
            )}

            {bf && bf.error && (
              <div style={{ marginBottom: 14 }}>
                {/* What is NOT claimed here: that nothing was written. A
                    request that never came back with a report cannot say
                    which side of the write it failed on, and asserting the
                    comfortable half would be exactly the invention this
                    migration reports its way around. What can be said is that
                    running it again is safe, which is what idempotent means
                    here: only missing values are ever filled in. */}
                <Banner tone="danger" title="The fill did not run to a report">
                  {bf.error}{bf.code ? ` (${bf.code})` : ""} There is no report, so this screen cannot tell you what was and was not written. Running it again is safe: it only ever fills in values that are missing, and the report counts what was already there separately from what the run wrote.
                </Banner>
              </div>
            )}

            {!bf && (
              <Banner tone="info" title="Nothing has been run from this screen yet">
                This is a state and not a problem. The two values may already hold answers from a run somewhere else, and that is what the report tells you: every table reports what was already set as its own figure, separately from what this run wrote.
              </Banner>
            )}

            {bfReport && (
              <>
                <div className="ml-usage" style={{ gap: 8 }}>
                  <Fact k="Website labels written by this run">{fmt(bfReport.totals.property_id_written)}</Fact>
                  <Fact k="Section labels written by this run">{fmt(bfReport.totals.section_id_written)}</Fact>
                  <Fact k="Rows this property has in these tables">{fmt(bfReport.totals.rows_in_scope)}</Fact>
                  <Fact k="Tables this run wrote something to">{fmt(bfReport.totals.tables_written)}</Fact>
                  <Fact k="Tables read and empty">{fmt(bfReport.totals.tables_empty)}</Fact>
                  <Fact k="Tables not in this database">{fmt(bfReport.totals.tables_absent)}</Fact>
                  <Fact k="Tables cut short by this call">{fmt(bfReport.totals.tables_capped)}</Fact>
                  <Fact k="Tables that could not be read">{fmt(bfReport.totals.tables_error)}</Fact>
                  <Fact k="This run">{bf.resumed ? "Carried on from where the last one stopped" : "Started from the first row of each table"}</Fact>
                  <Fact k="Time it took">{fmt(bfReport.ms)} ms</Fact>
                </div>

                {/* Is there more to do. The sentence is the endpoint's own,
                    because it draws the distinction this report exists for: a
                    value still missing can be a finished, correct answer (a
                    page no section claims, a page two sections both claim, a
                    row whose connection has no property yet) and reading
                    those as work left over would have somebody running this
                    forever against rows that will never change. */}
                {bfCont && (
                  <div style={{ marginTop: 14 }}>
                    <Banner tone={bfCont.more ? "warning" : "info"} title={bfCont.more ? "There is more to do" : "Nothing was cut short"}>
                      {bfCont.why}
                    </Banner>
                  </div>
                )}

                {bfReport.why && (
                  <p className="ml-section__sub" style={{ marginTop: 14 }}>{bfReport.why}</p>
                )}

                {/* What a single property run cannot answer, in the report's
                    own words. Rows attributable to no property at all are
                    facts about a whole table, and a zero that is true by
                    construction would read as reassurance it has not earned. */}
                {bfReport.not_derivable_why && (
                  <p className="ml-section__sub" style={{ marginTop: 14 }}>
                    What this run cannot tell you: {bfReport.not_derivable_why}
                  </p>
                )}

                {/* The second half of the choice the endpoint hands over, with
                    its reason attached. Only when there is actually a cursor
                    to ignore: offering "start again" when nothing is being
                    skipped would be a button that does the same thing as the
                    one on the line. */}
                {pending && pending.cursor && (
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    <p className="ml-section__sub">{bfCont && bfCont.resume_after_why}</p>
                    {mayRun && (
                      <div>
                        <Button variant="secondary" leadingIcon="refresh" disabled={!!(bf && bf.busy)}
                          onClick={() => runBackfill(null)}
                          title="Read every page of the crawl again from the first one, instead of carrying on. Do this after changing where a piece publishes.">
                          Start again from the first page
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Per table, all of them, including the ones with nothing to
                    report. A table left off the list would be indistinguishable
                    from a table nobody looked at. */}
                <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>Table by table</div>
                  {bfReport.tables.map(t => (
                    <div key={t.table} style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                        <b className="ml-propfact__v">{t.table}</b>
                        <span className="ml-propline__meta">{BF_STATE_WORDS[t.state] || t.state} ({t.state})</span>
                        <span className="ml-propline__meta">{t.section_mode === "derived" ? "section worked out from the address" : "section is the site it came through"}</span>
                        {t.state === "error" ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Could not be read</span> : null}
                        {t.state === "capped" ? <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />More to do</span> : null}
                      </div>
                      {/* A table that was never read has no figures, and
                          showing it a row of zeros would say it was read and
                          found empty. Those are different answers, so the
                          zeros are not drawn: the reason is, instead. */}
                      {t.state === "absent" || t.state === "error"
                        ? (t.error ? <div className="ml-section__sub">{t.error}</div> : null)
                        : (
                          <div className="ml-usage" style={{ gap: 6 }}>
                            <Fact k="Rows in scope">{fmt(t.rows_in_scope)}</Fact>
                            <Fact k="Website label">{`${fmt(t.property_id.written)} written, ${fmt(t.property_id.already_set)} already there, ${fmt(t.property_id.remaining)} still blank`}</Fact>
                            <Fact k="Section label">{`${fmt(t.section_id.written)} written, ${fmt(t.section_id.already_set)} already there, ${fmt(t.section_id.remaining)} still blank`}</Fact>
                          </div>
                        )}
                      {t.why ? <div className="ml-section__sub">{t.why}</div> : null}

                      {/* crawl_pages, the one table whose section is worked out
                          rather than copied, and the only interesting rule in
                          the migration. Everything it could not work out is
                          here with its reason. */}
                      {t.derivation && t.derivation.crawls.map(c => (
                        <div key={c.site_id} style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                          <div className="ml-propline__meta">{sectionName(c.site_id)}, read as the website's crawl</div>
                          {c.why ? <div className="ml-section__sub">{c.why}</div> : null}
                          <div className="ml-usage" style={{ gap: 6 }}>
                            <Fact k="Pages looked at">{fmt(c.scanned)}</Fact>
                            <Fact k="Pages given a section">{fmt(c.written)}</Fact>
                            <Fact k="Pages no piece publishes">{fmt(c.no_claim)}</Fact>
                            <Fact k="Pages two pieces both claim">{fmt(c.ties)}</Fact>
                            <Fact k="Stopped by the row limit">{c.capped ? "Yes, so run it again to carry on" : "No"}</Fact>
                          </div>
                          {c.claimers.length > 0 && (
                            <div className="ml-usage" style={{ gap: 6 }}>
                              {c.claimers.map(cl => (
                                <Fact key={cl.site_id} k={cl.name || sectionName(cl.site_id)}>
                                  {cl.published_base ? `publishes at ${hostPath(cl.published_base)}` : "has not said where it publishes, so it can claim nothing"}
                                </Fact>
                              ))}
                            </div>
                          )}
                          {c.claims.length > 0 && (
                            <div className="ml-usage" style={{ gap: 6 }}>
                              {c.claims.map(cl => (
                                <Fact key={cl.section_id} k={sectionName(cl.section_id)}>{`${fmt(cl.pages)} page${cl.pages === 1 ? "" : "s"} given to it by this run`}</Fact>
                              ))}
                            </div>
                          )}
                          {c.no_claim > 0 && c.no_claim_sample.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <div className="ml-section__sub">Pages no piece of this website publishes, so they belong to the website and to no section. That is a legitimate answer and not a hole. Showing up to {fmt(c.sample_cap)} of {fmt(c.no_claim)}.</div>
                              {c.no_claim_sample.map(u => <div key={u} className="ml-propline__meta">{hostPath(u)}</div>)}
                            </div>
                          )}
                          {c.ties > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <div className="ml-section__sub">Pages two pieces both claim. Nothing was written for these on purpose: two pieces declaring the same publish address is a real setup error, and picking a winner would hide it. Showing up to {fmt(c.sample_cap)} of {fmt(c.ties)}.</div>
                              {c.tie_sample.map(u => <div key={u} className="ml-propline__meta">{hostPath(u)}</div>)}
                            </div>
                          )}
                        </div>
                      ))}

                      {t.derivation && t.derivation.section_distribution.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div className="ml-section__sub">Where every crawled page stands now, including what earlier runs wrote:</div>
                          <div className="ml-usage" style={{ gap: 6 }}>
                            {t.derivation.section_distribution.map(d => (
                              <Fact key={String(d.section_id)} k={sectionName(d.section_id)}>{`${fmt(d.pages)} page${d.pages === 1 ? "" : "s"}`}</Fact>
                            ))}
                          </div>
                        </div>
                      )}
                      {t.derivation && t.derivation.stray_rows
                        ? <div className="ml-section__sub">{fmt(t.derivation.stray_rows)} rows: {t.derivation.stray_why}</div>
                        : null}
                    </div>
                  ))}
                </div>

                {bfBounds && (
                  <p className="ml-section__sub" style={{ marginTop: 14 }}>
                    {bfBounds.why} This call was allowed {fmt(bfBounds.max_rows_per_table)} rows per table, out of a ceiling of {fmt(bfBounds.route_ceiling)}.
                  </p>
                )}
              </>
            )}
          </OpenLine>

          {/* ── Step 4, read only ─────────────────────────────
              The report that has to come back empty before a unique key can be
              added at boot. Asked for rather than loaded, because it is ten
              full table aggregates over a live database. */}
          <OpenLine icon="key" tone="neutral"
            drawerTitle="Rows that would clash"
            drawerSub="Read only. This changes nothing."
            action={
              <Button variant="secondary" leadingIcon="search" disabled={!!(cf && cf.busy)}
                onClick={runConflicts}
                title="Read every key this migration will tighten and report which rows would clash. This only reads.">
                {cf && cf.busy ? "Checking…" : cfReport ? "Check again" : "Check now"}
              </Button>
            }
            lead={<>
              <span className="ml-propline__name">Rows that would clash once identity moves to the website</span>
              <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />Read only</span>
              {cfReport ? (
                <span className={"hds-badge hds-badge--" + (VERDICT_WORDS[cfReport.verdict] || VERDICT_WORDS.unknown).badge}>
                  <span className="hds-badge__dot" />{(VERDICT_WORDS[cfReport.verdict] || VERDICT_WORDS.unknown).word}
                </span>
              ) : null}
              <span className="ml-propline__meta">{cfLead}</span>
              {cfReport ? <span className="ml-propline__meta">{fmt(cfReport.summary.conflict_groups)} clashing group{cfReport.summary.conflict_groups === 1 ? "" : "s"}</span> : null}
              {cf && cf.error ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Did not run</span> : null}
            </>}>

            {cf && cf.error && (
              <div style={{ marginBottom: 14 }}>
                <Banner tone="danger" title="The check did not run">
                  {cf.error}{cf.code ? ` (${cf.code})` : ""}
                </Banner>
              </div>
            )}

            {!cf && (
              <Banner tone="info" title="Not checked yet">
                Today one page of this website can sit in two different crawls and be two rows. The property model says it is one page, which means a key on the website rather than on the site, and a key cannot be added until nothing in the database breaks it. This check reads every one of those keys and names what would break. It is not run with the screen because it reads every row of ten tables, and nothing here writes anything either way.
              </Banner>
            )}

            {cfReport && (
              <>
                {/* The badge tone is reused as the banner tone: success,
                    warning and danger are valid for both, so clear cannot
                    read as a warning in one place and a success in the
                    other. */}
                <Banner tone={(VERDICT_WORDS[cfReport.verdict] || VERDICT_WORDS.unknown).badge}
                  title={(VERDICT_WORDS[cfReport.verdict] || VERDICT_WORDS.unknown).word}>
                  {cfReport.why}
                </Banner>
                <div className="ml-usage" style={{ gap: 8, marginTop: 14 }}>
                  <Fact k="Tables checked">{fmt(cfReport.summary.tables)}</Fact>
                  <Fact k="Clear">{fmt(cfReport.summary.clear)}</Fact>
                  <Fact k="With clashes">{fmt(cfReport.summary.blocked)}</Fact>
                  <Fact k="Could not be checked">{fmt(cfReport.summary.unknown)}</Fact>
                  <Fact k="Clashing groups">{fmt(cfReport.summary.conflict_groups)}</Fact>
                  <Fact k="Rows in those groups">{fmt(cfReport.summary.conflict_rows)}</Fact>
                  <Fact k="Read at">{timeAgo(cfReport.checked_at) || cfReport.checked_at}</Fact>
                  <Fact k="Groups listed per table">{`up to ${fmt(cfReport.cap)}, and every count above is a separate count over every row`}</Fact>
                </div>

                {cfReport.partial && (
                  <div style={{ marginTop: 14 }}>
                    <Banner tone="warning" title="This was not a check of every table">
                      Some tables were left out of this run, so nothing here is a verdict on the database as a whole.
                    </Banner>
                  </div>
                )}
                {cfReport.probe_why ? <p className="ml-section__sub" style={{ marginTop: 14 }}>The schema could not be read: {cfReport.probe_why}</p> : null}
                {cfReport.sections_why ? <p className="ml-section__sub" style={{ marginTop: 14 }}>The pieces of this website could not be named: {cfReport.sections_why}. Clashes below carry ids instead of names.</p> : null}

                <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                  {cfReport.tables.map(t => {
                    const v = VERDICT_WORDS[t.verdict] || VERDICT_WORDS.unknown;
                    return (
                      <div key={t.table} style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                          <b className="ml-propfact__v">{t.table}</b>
                          <span className={"hds-badge hds-badge--" + v.badge}><span className="hds-badge__dot" />{v.word}</span>
                          {/* Both counts from their own field. The group list
                              below is capped and its length is never allowed
                              to stand in for either of them. */}
                          <span className="ml-propline__meta">{t.conflict_groups == null ? "no count, because the table was not read" : `${fmt(t.conflict_groups)} clashing group${t.conflict_groups === 1 ? "" : "s"}`}</span>
                          {t.conflict_rows == null ? null : <span className="ml-propline__meta">{fmt(t.conflict_rows)} rows in them</span>}
                        </div>
                        <div className="ml-section__sub">{t.reason || "No reason was given, which is itself a finding."}</div>
                        <div className="ml-propline__meta">Key today: {t.current_key}. Key the model needs: ({t.scoped_key.join(", ")}).</div>
                        {t.section_note ? <div className="ml-section__sub">{t.section_note}</div> : null}
                        {(t.notes || []).map((n, i) => <div key={i} className="ml-section__sub">{n}</div>)}
                        {t.groups_capped && (
                          <div className="ml-section__sub">
                            Showing {fmt(t.groups_shown)} of {fmt(t.conflict_groups)} groups. The figures above are counted over every row, so they do not move when this list is cut.
                          </div>
                        )}
                        {t.groups.map((g, i) => (
                          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 12 }}>
                            <div className="ml-propline__meta">
                              {Object.entries(g.key).map(([k, val]) => `${k}: ${val === null || val === undefined ? "no value" : String(val)}`).join(", ")}
                            </div>
                            <div className="ml-propline__meta">
                              {fmt(g.rows)} rows, from {joinWords(g.sites.map(s => s.name + (s.known ? "" : " (not a piece of this website any more)")))}
                            </div>
                            {g.sections
                              ? <div className="ml-propline__meta">Sections on those rows: {g.sections.length ? g.sections.map(s => s.name + (s.known ? "" : " (not a piece of this website any more)")).join(", ") : "none"}{g.rows_without_section ? `, and ${fmt(g.rows_without_section)} with no section at all` : ""}</div>
                              : null}
                            {g.within_one_connection
                              ? <div className="ml-section__sub">These rows are all from one site, which the key in the database today should make impossible. The finding is the key, not the duplicate.</div>
                              : null}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </OpenLine>

          {/* ── Step 3, read only ─────────────────────────────
              Ask one question twice and show the disagreement. The pickers sit
              in the drawer rather than on the line, which is the one place
              this panel bends the house rule, and the reason is that the
              action needs two answers before it can run: a line can carry a
              button, not a form. The RESULT comes back to the line. */}
          <OpenLine icon="git-branch" tone="neutral"
            drawerTitle="The old answer and the new one, side by side"
            drawerSub="Read only. This changes nothing."
            lead={<>
              <span className="ml-propline__name">Compare the old reading with the new one</span>
              <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />Read only</span>
              {duReport ? (
                <span className={"hds-badge hds-badge--" + (duReport.empty ? "info" : duReport.agree ? "success" : "warning")}>
                  <span className="hds-badge__dot" />{duReport.empty ? "Read and empty" : duReport.agree ? "They agree" : "They disagree"}
                </span>
              ) : null}
              <span className="ml-propline__meta">{duLead}</span>
              {du && du.error ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Did not run</span> : null}
            </>}>

            <p className="ml-section__sub" style={{ marginTop: 0 }}>
              Reads still ask which site a row came from. The property model asks which website and which section instead. While both are true the two should return the same rows, and the only way to know they do is to ask both and compare. A difference is the finding this period exists to catch: two models that coexist quietly are how the last one went wrong.
            </p>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 14 }}>
              <Field label="Table" hint="There is no default: the answer only means something about a table you chose.">
                {tableList
                  ? <Select value={table} onChange={e => setTable(e.target.value)}>
                      <option value="">Choose a table</option>
                      {tableList.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  : tables && tables.error
                    ? <span className="ml-section__sub">{tables.error}</span>
                    : <span className="ml-section__sub">Reading the list of tables…</span>}
              </Field>
              <Field label="Section" hint="The whole website is the unfiltered view, which is the one scope that can see all of it.">
                <Select value={section} onChange={e => setSection(e.target.value)}>
                  <option value="">The whole website</option>
                  {nodes.map(n => <option key={n.site_id} value={n.site_id}>{n.name}</option>)}
                </Select>
              </Field>
              <div>
                <Button variant="secondary" leadingIcon="search"
                  disabled={!table || !!(du && du.busy)}
                  onClick={runReconcile}
                  title={table
                    ? "Read this table both ways and compare. This only reads."
                    : "Choose a table first. There is no default table, because the answer would then be about a table nobody picked."}>
                  {du && du.busy ? "Comparing…" : "Compare"}
                </Button>
              </div>
            </div>

            {du && du.error && (
              <div style={{ marginTop: 14 }}>
                <Banner tone="danger" title="The comparison did not run">
                  {du.refusal && du.refusal.why ? du.refusal.why : du.error}
                </Banner>
              </div>
            )}

            {!du && (
              <div style={{ marginTop: 14 }}>
                <Banner tone="info" title="Nothing has been compared yet">
                  Pick a table and press Compare. Both readings are plain selects and neither writes anything.
                </Banner>
              </div>
            )}

            {duReport && (
              <>
                <div style={{ marginTop: 14 }}>
                  <Banner tone={duReport.empty ? "info" : duReport.agree ? "success" : "warning"}
                    title={duReport.empty ? "Read and empty" : duReport.agree ? "The two readings agree" : "The two readings disagree"}>
                    {duReport.why}
                  </Banner>
                </div>
                <div className="ml-usage" style={{ gap: 8, marginTop: 14 }}>
                  <Fact k="Table">{duReport.table}</Fact>
                  <Fact k="Asked about">{duReport.section_id ? sectionName(duReport.section_id) : "The whole website"}</Fact>
                  <Fact k="Rows the old reading finds">{fmt(duReport.counts.site_scoped)}</Fact>
                  <Fact k="Rows the new reading finds">{fmt(duReport.counts.property_scoped)}</Fact>
                  <Fact k="Rows only one of them finds">{fmt(duReport.difference_count)}</Fact>
                  <Fact k="Differences listed">{duReport.reported_all
                    ? "all of them"
                    : `the first few of ${fmt(duReport.difference_count)}, which is the count above and not the length of this list`}</Fact>
                </div>

                {/* The two statements, because an operator reading a
                    disagreement wants to see what was actually asked rather
                    than take it on trust. */}
                {duScope && (
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div className="ml-propline__meta">The old reading asked:</div>
                    <code style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 13, background: "var(--bg-surface)", padding: "6px 8px", borderRadius: 6, wordBreak: "break-all" }}>{duScope.legacy && duScope.legacy.where}</code>
                    <div className="ml-propline__meta">The new reading asked:</div>
                    <code style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 13, background: "var(--bg-surface)", padding: "6px 8px", borderRadius: 6, wordBreak: "break-all" }}>{duScope.where}</code>
                  </div>
                )}

                {duReport.differences.length > 0 && (
                  <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>The rows that differ</div>
                    {duReport.differences.map((d, i) => (
                      <div key={i} style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
                        <div className="ml-propline__meta">
                          {fmt(d.count)} row{d.count === 1 ? "" : "s"}, {SIDE_WORDS[d.side] || d.side}
                        </div>
                        <pre style={{ margin: 0, background: "var(--bg-surface)", padding: 10, borderRadius: 8, fontSize: 12, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {JSON.stringify(d.row, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </OpenLine>
        </div>
      </div>
    );
  }

  // ── The market on the six business registers ───────────────
  //
  // Step 14 of docs/PROPERTY-DB-MIGRATION.md, and the one piece of it nobody
  // could ASK for. `market_id` shipped on six tables and the three register
  // reads moved onto it, and the fill that sets the column runs unprompted at
  // boot over every property at once. What that cannot do is show somebody what
  // it would write before it writes, run for one website, or run at the one
  // moment it changes its answer, which is when a market is declared.
  //
  // WHAT THIS PANEL MAY NOT PROMISE, and the copy below is written against it:
  // filling the market in does not change what any screen shows. A moved read
  // ANDs the connection onto the market, so the two filters select the same
  // rows and the app proves that per read before it uses the market at all.
  // What it changes is which column answered: while a row is blank the read
  // falls back to the connection and says so, in a sentence that names this
  // very fill as the fix, and until these endpoints existed that sentence named
  // something nobody could run. An empty register on one piece of a website
  // stays empty after this: whether a register should be readable across a
  // whole market is MODULE-SCOPE 6.3 and is not settled.
  //
  // ITS OWN PANEL, BESIDE THE OTHER ONE AND NOT INSIDE IT. The panel above is
  // sections 1 to 5: which website a row belongs to and which part of it. This
  // is section 14: which MARKET of that website the row is about, which is a
  // question about the business and not about the site (PROPERTY-MODEL.md 3.3
  // and 3.4). The two are also deleted on different days, and each one has to
  // stay deletable in one commit, so neither may depend on the other's state.
  //
  // THE REPORT COMES FIRST, which is this codebase's rule and the reason the
  // run button is not drawn until a report is on screen. 14.4 puts a report
  // before the change and the report is the backfill's own code path with
  // writing suppressed, so it cannot describe a different world from the one
  // the fill then writes to. Pressing a write before reading what it would do
  // has cost this project twice.

  // The three outcomes, which are three different things to do next, plus the
  // fourth state a property with no connection is in. Nothing here resolves a
  // property with two markets: is_default marks which market a SCREEN opens on
  // and says nothing about which market a keyword written before the second
  // market existed was about, so a tiebreak here would turn a refusal into a
  // guess that looks exactly like a fact.
  const MARKET_STATE = {
    one: {
      word: "One market, so this is a fact", badge: "success",
      what: "This website has exactly one market, so there is nothing to choose between: every row of the six registers below belongs to it. Filling the column in now is recording an arithmetic fact.",
    },
    no_market: {
      word: "No market declared", badge: "warning",
      what: "This website has declared no market, so there is nothing for these rows to be keyed TO. They keep a blank market until a market is added, which is a state a person fixes by declaring one and never a value to invent. Add a market in this property's setup and read this report again.",
    },
    by_address: {
      word: "Two or more markets, placed by their declared addresses", badge: "success",
      what: "This website has more than one market, and each market says where it is reached while each connected piece says where it publishes. Both of those were typed by you and stored, so matching them is reading your own declarations rather than guessing from the shape of a URL: a piece publishing at /fr/blog belongs to the market declared at /fr, and where two markets both reach a piece the one whose address reaches furthest into it wins. A piece that has not said where it publishes, a piece sitting ABOVE every market's address such as a front end at the root, and two markets claiming one address equally are each left blank and named below with the number of rows they leave blank.",
    },
    refused: {
      word: "Two or more markets, and nothing tells them apart", badge: "danger",
      what: "This website has more than one market, and at least one of them has declared no address a publish address can be compared against. A market addressed as the whole website claims everything, so every other market's match would be ambiguous and no piece of this website can be placed. Nothing is written for it, deliberately: a column filled with a plausible answer is indistinguishable from one filled with the truth, and nothing afterwards could ever tell them apart. Give each market an address in this property's setup and read this report again.",
    },
    no_connection: {
      word: "Nothing connected yet", badge: "info",
      what: "This website has no connected piece, so no row in any of the six registers is in its scope. That is a real state and not a problem: a website can exist here before its sites are attached.",
    },
  };

  // Why one piece of the website could not be placed into a market, in words.
  // Four reasons and four different things to do, which is why they are not one
  // figure: lib/market-place.js reports them per section and the fill counts the
  // rows each one leaves blank.
  const MARKET_UNPLACED = {
    no_publish_address: "Has not said where it publishes",
    above_every_market: "Publishes above every market's address",
    tied              : "Two markets claim it equally",
    property_refused  : "This website's markets cannot be told apart",
    no_market         : "This website has declared no market",
  };

  // What the fill did to one table, in words, with the library's own state name
  // kept beside them in the drawer: an operator comparing the screen with a
  // response should meet the same word twice.
  const MARKET_TABLE_WORDS = {
    done  : "Filled in",
    capped: "Cut short by this call's row limit",
    empty : "Read and empty",
    absent: "Not in this database",
    error : "Could not be read",
  };

  // The six tables as the things they hold, because `bi_relationships` on a
  // screen is a table name and not an answer. The table name stays beside it.
  const MARKET_TABLE_NAMES = {
    keywords         : "Keywords",
    topics           : "Topics",
    faqs             : "Questions buyers ask",
    bi_entities      : "Business profile, products, competitors, industry and partners",
    bi_relationships : "Relations between those",
    research_analyses: "Research runs",
  };

  function MarketDbPanel({ property }) {
    const { Banner, Button } = DS();

    // The role, read the same way and from the same field PropertyDbPanel reads
    // it (that panel's own comment explains the reasoning at length). Asked for
    // again rather than shared, because the two panels are deleted on different
    // days and a shared read would make removing one break the other. A FAILED
    // read is neither permission nor refusal: the line says so and offers no
    // button, because the only thing a button that cannot work can do is waste
    // the one press somebody trusted it with.
    const [who, setWho] = useState(null);          // null while reading | { role } | { error }
    useEffect(() => {
      let alive = true;
      api("/account")
        .then(d => { if (alive) setWho({ role: (d && d.role) || null }); })
        .catch(e => { if (alive) setWho({ error: (e && e.message) || "your role could not be read" }); });
      return () => { alive = false; };
    }, []);
    const mayRun = !!(who && (who.role === "owner" || who.role === "admin"));

    // The report, asked for rather than loaded with the screen: it is two
    // counting statements per table over six tables plus both registers, and a
    // read of that weight firing every time somebody opens a property is a cost
    // nobody asked for. Not read yet is a state and it is said on the line.
    const [mr, setMr] = useState(null);            // null | { busy, data? } | { data } | { error, code }
    const runReport = async () => {
      // The previous report is kept while the next one is being read, the way
      // PropertyScreen's own quiet reload keeps the crawl result on screen: the
      // fill re-reads the report the moment it finishes, and blanking it would
      // make the run button vanish and come back under the hand that pressed it.
      setMr(prev => ({ busy: true, data: prev && prev.data }));
      try {
        const d = await api(`/property-db/properties/${property.id}/market-report`);
        setMr({ data: d });
      } catch (e) {
        setMr({ error: (e && e.message) || "the market report could not be read.", code: e && e.data && e.data.code });
      }
    };

    // The fill. No max_rows_per_table: the route's own default is one chunk per
    // table per call, which is the smallest unit of real progress and a shape
    // of request that always answers, and sending a number from here would be
    // this screen inventing a second default in a third place.
    //
    // `pending` holds whether the LAST run that actually answered was cut short
    // by the per table cap. There is no cursor to hold beside it, and that is a
    // property of this fill rather than an omission: a market is decided for a
    // whole property at once, so a refused property's rows are never selected
    // and the candidate set strictly shrinks. Running again continues by itself.
    const [mb, setMb] = useState(null);            // null | { busy } | { data } | { error, code }
    const [pending, setPending] = useState(false);
    const runFill = async () => {
      setMb({ busy: true });
      try {
        const d = await api(`/property-db/properties/${property.id}/market-backfill`, {
          method: "POST", body: JSON.stringify({}),
        });
        setMb({ data: d });
        setPending(!!(d && d.continuation && d.continuation.more));
        // The report is re-read, not patched, so the figures on screen stay
        // live state rather than this screen's arithmetic about what it just
        // did. It is also the only honest way to show the before and the after
        // of a run that was cut short.
        await runReport();
      } catch (e) {
        // `pending` is left as it was: it describes the last run that answered,
        // and a failed request did not change what is left to do.
        setMb({ error: (e && e.message) || "the market fill could not be run.", code: e && e.data && e.data.code });
      }
    };

    const mrReport = mr && mr.data ? mr.data.report : null;
    const mbReport = mb && mb.data ? mb.data.report : null;
    const mbCont   = mb && mb.data ? mb.data.continuation : null;
    const mbBounds = mb && mb.data ? mb.data.bounds : null;

    // The property's one group, or null when nothing is connected. Read off the
    // report rather than re-derived, so the screen cannot reach a fourth answer
    // about a property the server has already decided.
    const group = mrReport && mrReport.properties.length ? mrReport.properties[0] : null;
    const state = !mrReport ? null : group ? group.state : "no_connection";
    const words = state ? (MARKET_STATE[state] || MARKET_STATE.no_connection) : null;

    // How many rows the fill would write, which is `outstanding` and NOT
    // `remaining`. A row whose property has declared no market, and a row whose
    // property has two, are both permanently null: counting them here would
    // make a finished job offer work on every single run. Added up over the six
    // tables, each of whose figures is its own count over live state and is
    // printed per table below.
    const fillable = !mrReport ? 0
      : mrReport.tables.reduce((n, t) => n + ((t.market_id && t.market_id.outstanding) || 0), 0);

    // The run is offered only when a report has been read AND that report says
    // the fill has something it can do. A button over a property with no market
    // or with two would provably write zero, and the sentence beside it is the
    // answer instead.
    /* The run is offered for the two states that have something to write: one
     * market, where the market is an arithmetic fact, and two or more placed by
     * their declared addresses. Never for a refusal and never for a website
     * with no market, where it would provably write zero.
     *
     * `fillable` is the second half of the condition and it is not redundant: a
     * property placed by address can have every one of its sections unplaced,
     * and a button that would write nothing is a button that teaches somebody
     * to distrust the next one. */
    const canFill = mayRun && (state === "one" || state === "by_address");

    const lead = mr && mr.busy ? "Reading the six registers, which can take a moment"
      : mr && mr.error ? mr.error
      : mbReport ? `${fmt(mbReport.totals.market_id_written)} row${mbReport.totals.market_id_written === 1 ? "" : "s"} written by the last run, ${fmt(fillable)} still to write`
      : mrReport ? `${fmt(fillable)} of ${fmt(mrReport.totals.rows_in_scope)} rows would be filled in`
      : "Nothing has been read yet, and the report comes before the fill";

    // One table, the same shape for the report and for the run: the two differ
    // only in whether `written` can be anything but zero.
    const TableLine = ({ t }) => (
      <div style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <b className="ml-propfact__v">{MARKET_TABLE_NAMES[t.table] || t.table}</b>
          <span className="ml-propline__meta">{t.table}</span>
          <span className="ml-propline__meta">{MARKET_TABLE_WORDS[t.state] || t.state} ({t.state})</span>
          {t.state === "error" ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Could not be read</span> : null}
          {t.state === "capped" ? <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />More to do</span> : null}
        </div>
        {/* A table that was never read has no figures, and a row of zeros would
            say it was read and found empty. Those are different answers, so the
            zeros are not drawn and the reason is. */}
        {t.state === "absent" || t.state === "error"
          ? (t.error ? <div className="ml-section__sub">{t.error}</div> : null)
          : (
            <div className="ml-usage" style={{ gap: 6 }}>
              <Fact k="Rows in scope">{fmt(t.rows_in_scope)}</Fact>
              <Fact k="Market">{`${fmt(t.market_id.written)} written, ${fmt(t.market_id.already_set)} already there, ${fmt(t.market_id.remaining)} still blank`}</Fact>
              <Fact k="Still to write">{fmt(t.market_id.outstanding)}</Fact>
              <Fact k="Blank because no market is declared">{fmt(t.market_id.no_market)}</Fact>
              <Fact k="Blank because nothing places the piece they sit on">{fmt(t.market_id.refused)}</Fact>
              {/* PER MARKET, which is the figure the whole change exists to
                  produce. Every market with a piece under it appears, including
                  one holding no rows: read and empty is a state, and a market
                  left off the list would be indistinguishable from one nobody
                  counted. */}
              {(t.by_market || []).map(m => (
                <Fact key={m.market_id} k={`Market ${m.market_id} holds`}>
                  {`${fmt(m.rows)} row${m.rows === 1 ? "" : "s"}, of which ${fmt(m.remaining)} still blank`}
                </Fact>
              ))}
              {/* AND THE REASONS BEHIND THE REFUSED FIGURE, each with its own
                  count, because the four need four different things done. */}
              {Object.entries(t.market_id.by_reason || {}).filter(([, n]) => n > 0).map(([reason, n]) => (
                <Fact key={reason} k={MARKET_UNPLACED[reason] || reason}>{`${fmt(n)} row${n === 1 ? "" : "s"}`}</Fact>
              ))}
            </div>
          )}
        {t.why ? <div className="ml-section__sub">{t.why}</div> : null}
      </div>
    );

    return (
      <div className="ml-propblock">
        <h2 className="ml-section__h">Which market each register row is about</h2>
        <p className="ml-section__sub">
          Keywords, topics, the questions buyers ask, the business profile and everything beside it, and the research runs are registers of a BUSINESS rather than measurements of a website: a good keyword in the wrong country is the wrong keyword. Each of those rows records which market it is about, and every row that existed before that column did still has it blank. While a row is blank the app answers from the piece of the website it arrived through instead.
        </p>
        <p className="ml-section__sub">
          On a website with ONE market, filling this in changes nothing you can see: the two readings return the same rows, which the app checks every time before it uses the market at all, and what changes is which of the two answered. On a website with MORE THAN ONE market it changes something real, which is the point: until a row records its market, the market control on Keywords and the Topics Bank has nothing to filter by and those screens cannot answer for one market at a time.
        </p>
        <div className="ds-lines">
          <OpenLine icon="flag" tone={state === "refused" ? "neutral" : "todo"}
            drawerTitle="Fill in the market on the six registers"
            drawerSub="The report reads and changes nothing. The fill writes."
            action={
              <span style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" leadingIcon="search" disabled={!!(mr && mr.busy)}
                  onClick={runReport}
                  title="Read what the fill would write, per table, without writing anything.">
                  {mr && mr.busy ? "Reading…" : mrReport ? "Read again" : "Read the report"}
                </Button>
                {canFill ? (
                  <Button variant="primary" leadingIcon={<MlIcon name="play" size={18} />}
                    disabled={!!(mb && mb.busy)}
                    onClick={runFill}
                    title={pending
                      ? "Carry on. The last run was cut short by its row limit, and this writes to your database."
                      : "This writes to your database: it fills in the market on every row that has none. It never changes a market that is already there."}>
                    {mb && mb.busy ? "Writing…" : (pending ? "Carry on writing" : "Fill them in")}
                  </Button>
                ) : null}
              </span>
            }
            lead={<>
              <span className="ml-propline__name">Fill in the market on every register row</span>
              <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />Writes to your data</span>
              {words ? (
                <span className={"hds-badge hds-badge--" + words.badge}><span className="hds-badge__dot" />{words.word}</span>
              ) : null}
              <span className="ml-propline__meta">{lead}</span>
              {pending ? <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />More to do</span> : null}
              {(mr && mr.error) || (mb && mb.error)
                ? <span className="hds-badge hds-badge--danger"><span className="hds-badge__dot" />Did not run</span> : null}
              {/* Why there is no fill button, said on the line rather than only
                  in the drawer: a missing button with no sentence beside it
                  reads as a screen that forgot to draw something. */}
              {!mrReport
                ? <span className="ml-propline__meta">Read the report first, then the fill is offered</span>
                : !mayRun && who && who.error
                  ? <span className="ml-propline__meta">Your role could not be read, so this write is not offered</span>
                  : !mayRun && who
                    ? <span className="ml-propline__meta">Only the account owner or an admin can run this</span>
                    : !mayRun
                      ? <span className="ml-propline__meta">Checking what your role may run</span>
                      : !canFill
                        ? <span className="ml-propline__meta">There is nothing for the fill to write, and the report says why</span>
                        : null}
            </>}>

            {/* ── The report, first, always ── */}
            {!mr && (
              <Banner tone="info" title="Nothing has been read yet">
                The report reads the six registers and says, per table, how many rows carry a market already, how many the fill would write, and how many would stay blank with the reason. It writes nothing at all: it is the fill's own code path with every write switched off, so it cannot describe a different world from the one the fill then writes to. The fill is offered once it has been read.
              </Banner>
            )}

            {mr && mr.error && (
              <div style={{ marginBottom: 14 }}>
                <Banner tone="danger" title="The report could not be read">
                  {mr.error}{mr.code ? ` (${mr.code})` : ""} Nothing was written, because the report never writes. The fill is not offered without a report.
                </Banner>
              </div>
            )}

            {mrReport && words && (
              <>
                <Banner tone={words.badge} title={words.word}>{words.what}</Banner>

                {/* The market itself, named, when there is exactly one. A
                    market id on a screen is not an answer; "France (fr)" is. */}
                {group && group.markets.length > 0 && (
                  <div className="ml-usage" style={{ gap: 6, marginTop: 14 }}>
                    {group.markets.map(m => {
                      // What this market DECLARED, read off the report rather
                      // than from the market row, so the screen shows the value
                      // the matching really used.
                      const decl = (group.market_addresses || []).find(a => Number(a.market_id) === Number(m.market_id)) || null;
                      const takes = (group.placements || []).find(pl => Number(pl.market_id) === Number(m.market_id)) || null;
                      return (
                        <Fact key={m.market_id} k={`Market ${m.market_id}`}>
                          {[m.country_name || m.country, m.language ? languageName(m.language) : null]
                            .filter(Boolean).join(", ") || "no country or language recorded"}
                          {decl && decl.address ? `, reached at ${decl.address}` : ""}
                          {decl && !decl.placeable ? `. ${decl.why}` : ""}
                          {takes ? `. Takes ${takes.site_ids.length} connected piece${takes.site_ids.length === 1 ? "" : "s"}` : ""}
                          {m.is_default ? " (the one screens open on, which is not evidence about a row)" : ""}
                        </Fact>
                      );
                    })}
                  </div>
                )}

                {/* WHICH PIECE GOES WHERE, and which goes nowhere. Only on a
                    website with more than one market, where it is the only
                    thing that decides anything. */}
                {/* Only on a website with more than one market. Below that,
                    every section carries the same answer as the website and the
                    banner above has already given it: listing three connections
                    each saying "this website has declared no market" would be
                    the banner repeated per row. */}
                {group && group.market_count > 1 && group.unplaced && group.unplaced.length > 0 && (
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>
                      Pieces of this website that could not be placed in a market
                    </div>
                    {group.unplaced.map(u => (
                      <div key={u.site_id} className="ml-section__sub">
                        <b className="ml-propfact__v">{u.name || `Connection ${u.site_id}`}</b>
                        {" "}<span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />{MARKET_UNPLACED[u.state] || u.state}</span>
                        {u.publish_base ? <> Publishes at {u.publish_base}.</> : null}
                        {" "}{u.why}
                      </div>
                    ))}
                  </div>
                )}

                <div className="ml-usage" style={{ gap: 8, marginTop: 14 }}>
                  <Fact k="Rows this website has in the six registers">{fmt(mrReport.totals.rows_in_scope)}</Fact>
                  <Fact k="Rows the fill would write">{`${fmt(fillable)}, added up over the six tables below`}</Fact>
                  <Fact k="Rows that stay blank because no market is declared">{fmt(mrReport.totals.rows_without_market)}</Fact>
                  <Fact k="Rows that stay blank because this website has more than one market">{fmt(mrReport.totals.rows_refused)}</Fact>
                  <Fact k="Tables read and empty">{fmt(mrReport.totals.tables_empty)}</Fact>
                  <Fact k="Tables not in this database">{fmt(mrReport.totals.tables_absent)}</Fact>
                  <Fact k="Tables that could not be read">{fmt(mrReport.totals.tables_error)}</Fact>
                  <Fact k="What this report changed">{mrReport.dry_run ? "Nothing. It counts and never writes." : "Unknown, and that is itself a finding: this report should have been a dry run."}</Fact>
                  <Fact k="Time it took">{fmt(mrReport.ms)} ms</Fact>
                </div>

                <p className="ml-section__sub" style={{ marginTop: 14 }}>{mrReport.why}</p>

                {/* What one property's report cannot answer, in its own words.
                    Rows attributable to no website at all are facts about a
                    whole table, and a zero that is true by construction would
                    read as reassurance it has not earned. */}
                {mrReport.not_derivable_why && (
                  <p className="ml-section__sub" style={{ marginTop: 14 }}>
                    What this report cannot tell you: {mrReport.not_derivable_why}
                  </p>
                )}

                <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>Table by table, as the report found them</div>
                  {/* All six, including the empty ones: a table left off the
                      list would be indistinguishable from one nobody read. */}
                  {mrReport.tables.map(t => <TableLine key={t.table} t={t} />)}
                </div>
              </>
            )}

            {/* ── Then the run, and what it did ── */}
            {mb && mb.error && (
              <div style={{ marginTop: 18 }}>
                {/* What is NOT claimed: that nothing was written. A request that
                    never came back with a report cannot say which side of the
                    write it failed on. What can be said is that running it
                    again is safe, because only a blank market is ever filled. */}
                <Banner tone="danger" title="The fill did not run to a report">
                  {mb.error}{mb.code ? ` (${mb.code})` : ""} There is no report, so this screen cannot tell you what was and was not written. Running it again is safe: it only ever fills in a market that is missing, and it never changes one that is already there. Read the report above to see where it stands now.
                </Banner>
              </div>
            )}

            {mbReport && (
              <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>What the last fill did</div>
                <div className="ml-usage" style={{ gap: 8 }}>
                  <Fact k="Rows written by this run">{fmt(mbReport.totals.market_id_written)}</Fact>
                  <Fact k="Rows in scope">{fmt(mbReport.totals.rows_in_scope)}</Fact>
                  <Fact k="Rows refused">{fmt(mbReport.totals.rows_refused)}</Fact>
                  <Fact k="Rows left blank for want of a market">{fmt(mbReport.totals.rows_without_market)}</Fact>
                  <Fact k="Tables this run wrote something to">{fmt(mbReport.totals.tables_written)}</Fact>
                  <Fact k="Tables cut short by this call">{fmt(mbReport.totals.tables_capped)}</Fact>
                  <Fact k="Tables that could not be read">{fmt(mbReport.totals.tables_error)}</Fact>
                  <Fact k="Time it took">{fmt(mbReport.ms)} ms</Fact>
                </div>

                {/* The endpoint's own sentence, because it draws the
                    distinction this report exists for: a market still blank can
                    be a finished, correct answer, and reading those as work left
                    over would have somebody running this forever against rows
                    that cannot change. */}
                {mbCont && (
                  <>
                    <Banner tone={mbCont.more ? "warning" : "info"} title={mbCont.more ? "There is more to do" : "Nothing was cut short"}>
                      {mbCont.why}
                    </Banner>
                    <p className="ml-section__sub">{mbCont.cursor_why}</p>
                    {mbCont.error_tables.length > 0 && (
                      <Banner tone="danger" title="Some tables could not be read">
                        {mbCont.error_tables.map(e => `${e.table}: ${e.error}`).join(". ")} Their rows keep whatever they had. A table that could not be inspected is unknown and never clear.
                      </Banner>
                    )}
                  </>
                )}

                <p className="ml-section__sub">{mbReport.why}</p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {mbReport.tables.map(t => <TableLine key={t.table} t={t} />)}
                </div>

                {mbBounds && (
                  <p className="ml-section__sub">
                    {mbBounds.why} This call was allowed {fmt(mbBounds.max_rows_per_table)} rows per table, out of a ceiling of {fmt(mbBounds.route_ceiling)}.
                  </p>
                )}
              </div>
            )}

            {/* Who may run it, when they may not. Drawn after the report so the
                reading it offers is never gated behind a refusal about writing. */}
            {mrReport && !mayRun && (
              <div style={{ marginTop: 18 }}>
                {who && who.error
                  ? <Banner tone="danger" title="Your role could not be read">
                      {who.error} The fill is not offered rather than offered and refused, because the only thing a button that cannot work can do is waste the one press somebody trusted it with.
                    </Banner>
                  : who
                    ? <Banner tone="info" title="Only the account owner or an admin can run this">
                        Filling the market in writes a column on six tables, so it is gated exactly like declaring where a piece publishes. The report above reads and changes nothing, and it is open to you.
                      </Banner>
                    : <Banner tone="info" title="Checking what your role may run">
                        The answer comes from the same place the server reads it, so this screen will not offer a write the endpoint would refuse.
                      </Banner>}
              </div>
            )}
          </OpenLine>
        </div>
      </div>
    );
  }

  function PropertyScreen({ property, rev, onBack, onEdit, onOpenSite, go, onOfferAssist, onOfferBack }) {
    // Button, not a raw hds-btn. Every other console tab puts design-system
    // Buttons in ml-pagehead__actions, so hand-rolled small buttons here read
    // as a different app: the one thing a console screen must never do.
    const { Banner, Button, StatCard } = DS();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chip, setChip] = useState("all");
    // null | { busy: true } | { result } | { error }
    const [sync, setSync] = useState(null);
    // The one question coverage cannot be computed without: where each piece
    // publishes. Opened from the coverage gap, and from a single line.
    const [askPublish, setAskPublish] = useState(null);   // null | true | site_id
    // The OTHER question a piece of a website can only be asked, not measured:
    // which content type its help articles are. It sits beside the publish
    // address for the reason the comment on helpText below gives.
    const [askHelp, setAskHelp] = useState(null);         // null | site_id
    // null | { site_id, busy } | { site_id, error }: reading a site's content
    // types is a live call to that site, so it reports rather than silently
    // doing nothing.
    const [readTypes, setReadTypes] = useState(null);
    // WHAT THE THREE ADDS OPEN, and they open it HERE.
    //
    // Every one of them adds something to THIS property, so leaving the
    // property to do it is backwards: the screen you were working on, and the
    // property you were working on, both disappear at the moment you act on
    // them. The site add is the same dialog the Sites tab opens, which is why
    // it was lifted out of that tab, and pairing is the same window again.
    const [dialog, setDialog] = useState(null);   // null | "add-site" | "pair" | { notice }
    // `quiet` re-reads without blanking the screen, which is what a finished
    // crawl needs: the report of what it did must not be wiped off the screen
    // by the refresh that proves it.
    const load = useCallback((quiet) => {
      if (!quiet) setLoading(true);
      setErr(null);
      api(`/account/properties/${property.id}/structure`)
        .then(d => { setData(d); setLoading(false); })
        .catch(e => { setErr(e); setLoading(false); });
    }, [property.id]);
    // `rev` is bumped by the Properties tab after the wizard saves, so markets
    // added there are on screen here without a reload.
    useEffect(() => { load(); }, [load, rev]);

    const prop     = (data && data.property) || null;
    const nodes    = (data && data.nodes) || [];
    const markets  = (data && data.markets) || [];
    const needs    = (data && data.needs) || {};
    const crawl    = (data && data.crawl) || null;
    const setups   = (data && data.setups) || [];
    const ownCrawl = prop && prop.own_crawl_site_id;

    // What the assistant is pointed at. A property is not a site and the panel
    // addresses a site, so it gets the primary one and falls back to the crawl
    // the property keeps of itself: that is the one piece a property with
    // nothing connected still has.
    // The server resolves this: the explicit primary, else the bare-root site,
    // else any node at all. Reading the raw primary_site_id here meant a
    // property with none and no own crawl offered nothing and silently lost
    // the assistant, which is every property made before the wizard existed.
    const assistId = prop
      ? (prop.resolved_primary_site_id != null ? prop.resolved_primary_site_id
        : prop.primary_site_id != null ? prop.primary_site_id : prop.own_crawl_site_id)
      : null;
    const assistNode = nodes.find(n => Number(n.site_id) === Number(assistId)) || null;
    useEffect(() => {
      if (!onOfferAssist) return;
      onOfferAssist(assistNode
        ? { site: { site_id: assistNode.site_id, id: assistNode.site_id, name: assistNode.name, url: assistNode.url }, moduleId: "property-structure" }
        : null);
      // Leaving the screen takes the trigger with it: the console's other tabs
      // have no site to point an assistant at.
      return () => onOfferAssist(null);
    }, [onOfferAssist, assistId]);   // eslint-disable-line react-hooks/exhaustive-deps

    // A crawl site already sitting at the public address: the property's pages
    // are being read, they are just not recorded as the property's own crawl.
    // The same test the wizard uses, so the screen and the question agree.
    const trimSlash = (u) => String(u || "").trim().replace(/\/+$/, "").toLowerCase();
    const baseKey = trimSlash(prop && prop.public_base);
    const adoptable = (!prop || prop.own_crawl_site_id || !baseKey) ? null
      : nodes.find(n => n.source === "crawl" && !n.own_crawl && trimSlash(n.url) === baseKey) || null;

    // The title, and whether the address under it would just repeat it.
    const title = prop ? (prop.name || prop.root_domain) : "";
    const addressIsTitle = !!(prop && prop.public_base)
      && hostPath(prop.public_base).replace(/^www\./, "").toLowerCase()
         === String(title).replace(/^www\./, "").toLowerCase();

    // What to put in the box before the person edits it. A SUGGESTION, built
    // from the public address and the path the piece is edited under, and
    // labelled as one on the field itself: the rule this model has already
    // been bitten by twice is that a URL comparison may propose and must
    // never decide.
    const suggestPublish = (n) => {
      if (n.published_base) return n.published_base;
      const base = String((prop && prop.public_base) || "").replace(/\/+$/, "");
      if (!base) return "";
      let p = "";
      try { p = new URL(n.url).pathname.replace(/\/+$/, ""); } catch { p = ""; }
      return base + (p && p !== "/" ? p : "");
    };

    /* ── Which content type this piece's help articles are ──────
       WHY THIS LIVES HERE, one line under "Publishes at". A post and a page
       resolve themselves: WordPress core owns both slugs and the site reports
       the address it serves them at. A HELP ARTICLE does not. Core has no help
       type, no plugin agrees on a slug, and on this property a help article is
       either a page under a path or a post in a category, so nothing but the
       customer can say which. Until somebody says, the content planner can
       plan and brief a help article and cannot publish one, and that refusal is
       deliberate: picking the likely answer would publish somebody's help
       article into the wrong part of their live website.

       AND THERE IS NOTHING TO SUGGEST, which is the one way this differs from
       the publish address beside it. A publish address can be proposed from the
       address we already hold, labelled as a proposal, because the two are
       genuinely related. No fact about a website says which post type its help
       articles are: a /help/ path says a section exists, not what it is made
       of, and inferring from a URL is the mistake this model has been bitten by
       twice. So the selector opens on the empty option, "Not declared yet"
       stays sayable forever, and nothing is pre-chosen. */
    const helpText = (n) => {
      const h = n.help;
      if (!h) return null;
      if (h.state === "resolved") {
        return `Published as this site's "${h.type}" content type`
          + (h.declared_at ? `, declared on ${new Date(h.declared_at).toLocaleDateString()}` : "");
      }
      if (h.reason === "never_asked") return "Not known yet: nothing has asked this site which content types it has, so the choices cannot be listed";
      if (h.reason === "read_failed") return "Not known: reading this site's content types did not succeed, so the choices cannot be listed";
      if (h.reason === "not_declared") {
        return h.candidates.length
          ? `Not declared yet, so a help article can be planned and briefed here and not published. ${h.candidates.length} content type${h.candidates.length === 1 ? "" : "s"} could hold one`
          : "This site exposes no content type a help article could be published into, so there is nothing to declare";
      }
      if (h.reason === "no_cms") return "No CMS is connected here, so nothing is published from this piece at all";
      if (h.reason === "type_not_exposed") return `The "${h.type}" content type declared here is not exposed by this site any more, so nothing can be published into it`;
      if (h.reason === "type_not_writable") return `This site lists the "${h.type}" content type and serves no address for it, so nothing can be written there`;
      if (h.reason === "writability_unknown") return `This site listed the "${h.type}" content type without saying where it is served, so whether a help article can be published is unknown`;
      if (h.reason === "declaration_invalid") return "What is stored here as the home of help articles is not a content type, so it has to be declared again";
      // An answer this screen has never seen. Not read as a refusal and not as
      // a go ahead: an unjudged answer is its own state.
      return "Where a help article would be published here could not be established on this screen";
    };

    // Can a person answer the question right now, or does the site have to be
    // asked first? Two different buttons, because offering a selector with
    // nothing in it is how a screen asks a question it cannot take an answer to.
    const helpCanDeclare = (n) => !!(n.help && n.help.types_state === "read"
      && (n.help.candidates.length > 0 || n.help.declared));
    const helpNeedsRead  = (n) => !!(n.help && n.help.types_state !== "read" && n.help.types_state !== "no_cms");

    // Ask the site what content types it has. The one live call on this screen,
    // and the thing that turns "not known" into a list to choose from.
    const doReadTypes = async (n) => {
      setReadTypes({ site_id: n.site_id, busy: true });
      try {
        await api(`/wp/post-types?siteUrl=${encodeURIComponent(n.url)}`, { method: "POST", body: "{}" });
        setReadTypes(null);
        load(true);
      } catch (e) {
        setReadTypes({ site_id: n.site_id, error: (e && e.message) || "the read did not succeed" });
      }
    };

    const cov       = (data && data.coverage) || null;
    const sm        = (data && data.sitemap) || null;
    const mktPages  = (data && data.market_pages) || null;
    const blogs     = nodes.filter(n => n.section_guess === "blog");
    const connected = nodes.filter(n => n.can_publish === true);
    const readOnly  = nodes.filter(n => n.can_publish === false);
    const CHIPS = [
      { id: "all", label: "All", count: nodes.length },
      { id: "markets", label: "Markets", count: markets.length },
      { id: "blogs", label: "Blogs", count: blogs.length, hint: "detected" },
      { id: "connected", label: "Connected", count: connected.length },
      { id: "readonly", label: "Read only", count: readOnly.length },
    ];
    const shown = chip === "blogs" ? blogs : chip === "connected" ? connected : chip === "readonly" ? readOnly : nodes;
    const showMarkets   = chip === "all" || chip === "markets";
    const showStructure = chip !== "markets";

    // Every crawl under this property, in order, because the figures above are
    // computed over all of them. Refreshing only an adopted one would redraw a
    // screen whose numbers came from somewhere else.
    const syncIds = (crawl && crawl.site_ids && crawl.site_ids.length)
      ? crawl.site_ids
      : (ownCrawl ? [ownCrawl] : []);
    const runSync = async () => {
      if (!syncIds.length) return;
      setSync({ busy: true });
      try {
        let crawled = 0, stored = 0, removed = 0, note = null, sitemapUrl = null;
        for (const id of syncIds) {
          const r = await api(`/crawl/refresh?site_id=${id}`, { method: "POST", body: "{}" });
          crawled += Number(r.crawled || 0);
          stored  += Number(r.stored || 0);
          removed += Number(r.removed || 0);
          // One crawl reading nothing is a finding even when another worked,
          // so the note survives rather than being averaged away.
          if (r.note) note = r.note;
          if (!sitemapUrl && r.sitemapUrl) sitemapUrl = r.sitemapUrl;
        }
        setSync({ result: { crawled, stored, removed, note, sitemapUrl } });
        load(true);
      } catch (e) {
        setSync({ error: (e && e.message) || "The crawl could not be run." });
      }
    };

    useEffect(() => {
      if (!onOfferBack) return;
      onOfferBack({ label: "All properties", onClick: onBack });
      return () => onOfferBack(null);
    }, [onOfferBack, onBack]);

    if (loading) return <Loading label="Reading this property…" />;
    if (err) return <ErrorNote err={err} />;
    if (!prop) return <div className="hds-card hds-card--pad" style={{ textAlign: "center", color: "var(--text-muted)" }}>This property is no longer in the account.</div>;

    return (
      <>
        {/* Opened from this screen, shown over this screen. `load(true)` is the
            quiet re-read: a site connected in that dialog must appear here
            without blanking the property while it arrives. */}
        {dialog === "add-site" && (
          <AddSiteDialog onClose={() => setDialog(null)} onDone={() => load(true)} onPair={() => setDialog("pair")} />
        )}
        {dialog === "pair" && <PairPluginModal onClose={() => setDialog(null)} onPaired={() => load(true)} />}
        {/* The property's public address becomes the URL field's placeholder,
            never its value: this property is where the content is served, and
            a prefilled address is an answer nobody gave. */}
        {dialog === "add-blog" && (
          <AddBlogDialog onClose={() => setDialog(null)} publicBase={prop && prop.public_base} />
        )}
        {dialog === "add-market" && (
          <AddMarketDialog onClose={() => setDialog(null)} publicBase={prop && prop.public_base} />
        )}
        {dialog && dialog.notice && (
          <NoticeModal title={dialog.notice.title} body={dialog.notice.body} onClose={() => setDialog(null)} />
        )}
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            {/* A property whose name was never set falls back to its domain,
                so the title and the address line said the same website twice:
                "capitaineagile.fr" over "www.capitaineagile.fr". When the
                address adds nothing, the title becomes the link instead. */}
            <h2 className="ml-section__h">
              {prop.public_base && addressIsTitle
                ? <a href={prop.public_base} target="_blank" rel="noreferrer" className="ml-proplink">{title}</a>
                : title}
            </h2>
            {!prop.public_base ? (
              <p className="ml-section__sub">No public address on record yet, so nothing knows where this website publishes.</p>
            ) : addressIsTitle ? null : (
              <p className="ml-section__sub">
                <a href={prop.public_base} target="_blank" rel="noreferrer" className="ml-proplink">{hostPath(prop.public_base)}</a>
              </p>
            )}
            <p className="ml-section__sub" style={{ marginTop: 4 }}>{setupSentence(setups, nodes, prop.public_base)}</p>
          </div>
          <div className="ml-pagehead__actions">
            {/* Markets have LEFT the setup window, so this must no longer
                open it: a window that no longer asks about markets is not
                where a market is added. It says what it is waiting for until
                the market workflow exists, and it says it here rather than on
                another screen. */}
            <Button variant="secondary" leadingIcon={<MlIcon name="flag" size={16} />}
              onClick={() => setDialog("add-market")}
              title="Declare a market this property serves: national, meaning a country, or local, meaning a city, and where it is served.">
              Add market
            </Button>
            {/* THREE ADDS, ONE PER COVERAGE (see COVERAGES further down):
                a site is publishing coverage, a blog is content type coverage,
                a market is language and locality coverage. They were two
                buttons while a blog WAS a site, and that is exactly the
                conflation being undone, so the site add is now named for
                itself instead of being reached through the word blog. */}
            <Button variant="secondary" leadingIcon={<MlIcon name="globe" size={16} />}
              onClick={() => setDialog("add-site")}
              title="Connect a site under this domain, here: one WordPress, a SiteAtlas install, a multisite network, or a public URL with no CMS. This is publishing coverage, and a site added under this domain joins this property by itself.">
              Add site
            </Button>
            {/* Deliberately NOT the connect window any more. A blog is a kind
                of content covering a share of the property, so this opens the
                coverage it belongs to, which says plainly that it measures
                nothing yet. Nothing is lost: Add site above is the connect
                window this button used to borrow. */}
            <Button variant="secondary" leadingIcon={<MlIcon name="folder" size={16} />}
              onClick={() => setDialog("add-blog")}
              title="Declare that a kind of content is served at an address on this property: blog, news, changelog, docs or help.">
              Add blog
            </Button>
            <Button variant="primary" leadingIcon="refresh" onClick={runSync}
              disabled={!syncIds.length || !!(sync && sync.busy)}
              title={syncIds.length
                ? (syncIds.length === 1
                    ? "Read this website's sitemap again and store what it serves."
                    : `Read all ${syncIds.length} sitemaps of this website again and store what they serve.`)
                : "Nothing crawls this website yet, so there is no sitemap to read. Saving the property's setup creates its crawl."}>
              {sync && sync.busy ? "Crawling…" : "Sync sitemap"}
            </Button>
          </div>
        </div>

        {/* The repair, named rather than implied: a property made before the
            wizard existed has no public base and no markets, and no screen can
            make up either. */}
        {(needs.public_base || needs.markets) && (
          <div style={{ marginBottom: 16 }}>
            <Banner tone="info" title={needs.public_base && needs.markets
              ? "This property has no public address and no markets yet."
              : needs.public_base ? "This property has no public address yet." : "This property has no markets yet."}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {/* TWO REPAIRS, AND ONLY ONE OF THEM STILL HAS A WINDOW. The
                    setup window no longer asks about markets, so offering
                    "answer them now" for a missing market would open a window
                    that cannot fix it. Each sentence now names the repair that
                    exists, and the market half says it is waiting. */}
                <span>{needs.public_base
                  ? "It was created before that question was asked, so nothing here knows where its pages are published. Answering it is the repair, and it also gives the property the crawl of its own pages it should have had."
                  : "Its country was never part of anything it reported, so every market figure on this property reads as unplaced. Declaring a market is the repair: national means a country, local means a city, and the window says what it can and cannot yet save."}</span>
                {needs.public_base
                  ? <Button variant="secondary" leadingIcon="edit" onClick={onEdit}>Answer it now</Button>
                  : <Button variant="secondary" leadingIcon={<MlIcon name="flag" size={16} />} onClick={() => setDialog("add-market")}>Declare a market</Button>}
              </div>
            </Banner>
          </div>
        )}

        {/* The crawl's own outcome, reported as what it did rather than as a
            cheer. A run that read nothing returns a `note` and is NOT a
            success: the inventory was left exactly as it was. */}
        {sync && (sync.result || sync.error) && (
          <div style={{ marginBottom: 16 }}>
            {sync.error
              ? <Banner tone="danger" title="The crawl did not run">{sync.error}</Banner>
              : sync.result.note
                ? <Banner tone="warning" title="The crawl read nothing">{sync.result.note}</Banner>
                : <Banner tone="success" title="Sitemap read">
                    {`${fmt(sync.result.crawled)} page${Number(sync.result.crawled) === 1 ? "" : "s"} read, ${fmt(sync.result.stored)} stored, ${fmt(sync.result.removed)} no longer served and removed.`}
                    {sync.result.sitemapUrl ? ` Sitemap: ${hostPath(sync.result.sitemapUrl)}` : ""}
                  </Banner>}
          </div>
        )}

        {/* ── Coverage ────────────────────────────────────────
            The figures first, because this is what the screen is for: a
            property has pages, and there are two questions about every one of
            them. Can MarketLens manage it, and is Search Console tracking it.
            They are independent, and each gap wants a different thing done
            about it, so one blended "covered" number would hide whichever
            half is actually missing.

            Shown whenever there are pages to count. It used to live inside
            the crawl block, which only renders once the property has a crawl
            flagged as its OWN, so a property whose crawl the customer added
            themselves showed banners and no data at all. own_crawl decides
            what is billable and what is deleted with the property. It has
            nothing to do with whether we can count. */}
        {chip === "all" && cov && cov.universe > 0 && (
          <div className="ml-propblock">
            <h2 className="ml-section__h">Coverage</h2>
            <p className="ml-section__sub">
              Two questions about every page of this website, counted over the {fmt(cov.universe)} page{cov.universe === 1 ? "" : "s"} its crawl holds: can MarketLens manage it, and is Search Console tracking it. A page can be one without the other.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16, marginTop: 14 }}>
              <StatCard label="Pages" value={fmt(cov.universe)} icon="file-text"
                deltaNote={sm && sm.capped && sm.listed != null ? `of ${fmt(sm.listed)} in the sitemap` : "from the sitemap"} />
              <StatCard label="Content management" value={cov.publishable_pct != null ? cov.publishable_pct + "%" : "-"}
                icon="edit" deltaNote={`${fmt(cov.publishable)} of ${fmt(cov.universe)} can be edited here`} />
              <StatCard label="SEO tracking" value={cov.gsc_connected ? (cov.tracked_pct + "%") : "Not set up"}
                icon="trending-up" deltaNote={cov.gsc_connected
                  ? `${fmt(cov.tracked)} of ${fmt(cov.universe)} have Search Console data`
                  : "Search Console is not connected"} />
              <StatCard label="Both" value={cov.both_pct != null ? cov.both_pct + "%" : "-"}
                icon="check-circle" deltaNote={cov.both_pct != null
                  ? `${fmt(cov.both)} managed and tracked`
                  : "needs Search Console to measure"} />
            </div>

            {/* The four coverages, under the four cards that already are two of
                them. Nothing here is measured yet and every card says so. */}
            <CoverageFrame go={go} universe={cov.universe} fromProperty={{ id: prop.id, name: title }} />

            <div className="hds-card hds-card--pad" style={{ marginTop: 14 }}>
              <div className="ml-usage" style={{ gap: 8 }}>
                {/* NOT "all of them". The crawl reads the sitemap and follows
                    no links, so this is every page the sitemap lists and the
                    cap allowed. */}
                <Fact k="Pages measured">{fmt(cov.universe)} from the sitemap</Fact>
                <Fact k="Managed in MarketLens">{cov.publishable === 0
                  ? "None, because no piece with a CMS publishes these pages"
                  : `${fmt(cov.publishable)} (${cov.publishable_pct}%)`}</Fact>
                <Fact k="Edited by hand">{`${fmt(cov.measured_only)} (${cov.measured_only_pct}%), read and reported on, never written to`}</Fact>
                <Fact k="Tracked in Search Console">{cov.gsc_connected
                  ? `${fmt(cov.tracked)} (${cov.tracked_pct}%)`
                  : "Search Console is not connected for this website"}</Fact>
                {cov.neither ? <Fact k="Neither managed nor tracked">{fmt(cov.neither)}</Fact> : null}
                {cov.conflicting > 0 ? <Fact k="Claimed by two pieces">{fmt(cov.conflicting)}</Fact> : null}
              </div>

              {/* What the pages DO. A page count alone does not describe a
                  website: 140 blog posts and 140 product pages are different
                  websites. Detected from the path, so it says so. */}
              {cov.sections && cov.sections.length > 1 && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                  <div className="ml-usage" style={{ gap: 8 }}>
                    {cov.sections.map(x => (
                      <Fact key={x.section} k={SECTION_WORDS[x.section] || "Everything else"}>
                        {`${fmt(x.pages)} page${x.pages === 1 ? "" : "s"} (${Math.round((x.pages / cov.universe) * 100)}%)`}
                      </Fact>
                    ))}
                  </div>
                  <p className="ml-section__sub" style={{ marginTop: 12 }}>
                    What these pages are is recognised from their addresses, not declared, so read it as a reading of your website rather than a setting. Everything else is a real answer: a page outside a blog, documentation, help or shop area still belongs to this website.
                  </p>
                </div>
              )}
            </div>

            {/* ── What would close the gap ────────────────────
                The point of the figures. Each line is a number and the one
                thing that changes it, so "we are at 64%" turns into something
                a person can act on rather than feel bad about. Only the lines
                that are actually true for this property appear. */}
            {(cov.measured_only > 0 || cov.undeclared.length > 0 || !cov.gsc_connected
              || (cov.untracked != null && cov.untracked > 0)
              || (mktPages && mktPages.unassigned > 0)) && (
              <div className="hds-card hds-card--pad" style={{ marginTop: 14 }}>
                <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)", marginBottom: 10 }}>
                  What would close the gap
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {cov.undeclared.length > 0 && (
                    <GapLine
                      n={cov.universe}
                      what={`${cov.undeclared.length === 1 ? "One piece" : cov.undeclared.length + " pieces"} of this website have not said where they publish`}
                      why={`${joinWords(cov.undeclared.map(u => u.name))} ${cov.undeclared.length === 1 ? "is" : "are"} edited on a CMS, and a CMS address is not where pages are served from, so none of ${cov.undeclared.length === 1 ? "its" : "their"} pages can be attributed to ${cov.undeclared.length === 1 ? "it" : "them"}. This is the single biggest thing holding the content management figure down.`}
                      action={<Button variant="secondary" leadingIcon="edit" onClick={() => setAskPublish(true)}>Say where they publish</Button>} />
                  )}
                  {cov.measured_only > 0 && cov.undeclared.length === 0 && (
                    <GapLine
                      n={cov.measured_only}
                      what={`${fmt(cov.measured_only)} page${cov.measured_only === 1 ? "" : "s"} nothing can publish to`}
                      why="No piece with a CMS behind it publishes these, so MarketLens measures them and recommends changes without ever writing to them. Connect a site or a blog that publishes them to manage them here, or leave them as read and report, which on a hand-built front end is the normal answer."
                      action={<Button variant="secondary" leadingIcon="plus" onClick={() => setDialog("add-site")}>Add site</Button>} />
                  )}
                  {!cov.gsc_connected && (
                    <GapLine
                      n={cov.universe}
                      what="No page of this website is tracked in Search Console"
                      why="Search Console is what tells you which of these pages people actually see, so without it half of this screen cannot be measured. Connect it under Performance on any site of this property."
                      action={<Button variant="secondary" leadingIcon="trending-up" onClick={() => onOpenSite && assistId && onOpenSite(assistId)}>Open a site to connect it</Button>} />
                  )}
                  {cov.gsc_connected && cov.untracked > 0 && (
                    <GapLine
                      n={cov.untracked}
                      what={`${fmt(cov.untracked)} page${cov.untracked === 1 ? "" : "s"} with no Search Console data`}
                      why="Either they are new and have had no impressions in the last 28 days, which is normal, or a site's page scope leaves them out of what we pull. The scope is under Performance, and narrowing it too far is the usual cause." />
                  )}
                  {mktPages && mktPages.unassigned > 0 && (
                    <GapLine
                      n={mktPages.unassigned}
                      what={`${fmt(mktPages.unassigned)} page${mktPages.unassigned === 1 ? "" : "s"} outside every market`}
                      why="No market declared on this property claims these addresses, so nothing researched for a market applies to them: no competitor set, no industry read, no search volumes. Add the market they belong to, or widen the address of one you already have."
                      action={<Button variant="secondary" leadingIcon="edit" onClick={onEdit}>Add market</Button>} />
                  )}
                </div>
              </div>
            )}

            {/* ── Is the universe the whole website? ──────────
                The crawl is the sitemap, so coverage of the crawl is coverage
                of the sitemap. These are the only honest ways to say how big
                the difference is. */}
            {sm && (
              <div className="hds-card hds-card--pad" style={{ marginTop: 14 }}>
                <div className="ml-usage" style={{ gap: 8 }}>
                  <Fact k="Your sitemap lists">{sm.listed == null
                    ? "Not recorded yet, so run Sync sitemap once"
                    : `${fmt(sm.listed)} URL${sm.listed === 1 ? "" : "s"}`}</Fact>
                  <Fact k="Read into the inventory">{fmt(sm.read)}{sm.capped && sm.cap != null ? ` (the limit is ${fmt(sm.cap)} per crawl)` : ""}</Fact>
                  <Fact k="Getting impressions but not in the sitemap">{sm.known === 0
                    ? "No Search Console data to compare against yet"
                    : fmt(sm.missing)}</Fact>
                </div>
                <p className="ml-section__sub" style={{ marginTop: 12 }}>
                  The crawl reads your sitemap and follows no links, so a page this website serves but does not list is invisible to it, and every figure above counts the pages it could see. Search Console is the independent check: a URL Google showed to somebody exists whatever the sitemap says. It only sees the last 28 days, so that figure is a floor on what the sitemap is missing and never the whole of it.
                </p>
              </div>
            )}

            {sm && sm.capped && (
              <div style={{ marginTop: 14 }}>
                <Banner tone="warning" title={`Your sitemap lists ${fmt(sm.listed)} URLs and this inventory holds ${fmt(sm.read)}`}>
                  A crawl reads up to {fmt(sm.cap)} URLs at a time, so every figure on this screen describes the part of your website that was read, not the whole of it.
                </Banner>
              </div>
            )}
            {sm && sm.missing > 0 && (
              <div style={{ marginTop: 14 }}>
                <Banner tone="warning" title={`${fmt(sm.missing)} page${sm.missing === 1 ? "" : "s"} getting search impressions ${sm.missing === 1 ? "is" : "are"} not in your sitemap`}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <span>Search Console showed {sm.missing === 1 ? "this page" : "these pages"} to real people in the last 28 days, and the sitemap this crawl reads does not list {sm.missing === 1 ? "it" : "them"}, so {sm.missing === 1 ? "it is" : "they are"} not counted anywhere above. Add {sm.missing === 1 ? "it" : "them"} to your sitemap and press Sync sitemap.</span>
                    {sm.missing_sample.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {sm.missing_sample.slice(0, 6).map(x => (
                          <div key={x.url} className="ml-propline__meta" style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                            <a href={x.url} target="_blank" rel="noreferrer" className="ml-proplink">{hostPath(x.url)}</a>
                            <span>{fmt(x.impressions)} impression{x.impressions === 1 ? "" : "s"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Banner>
              </div>
            )}
          </div>
        )}

        <div className="ml-chipset" style={{ marginBottom: 16 }}>
          {CHIPS.map(c => <FilterChip key={c.id} {...c} active={chip === c.id} onPick={setChip} />)}
        </div>

        {showMarkets && (
          <div className="ml-propblock">
            <h2 className="ml-section__h">Markets</h2>
            <p className="ml-section__sub">{markets.length === 1 ? "1 country" : markets.length + " countries"}</p>
            {/* A market is a COUNTRY, and the language rides along (3.8). Said
                here because the two are a single word in most products. */}
            <p className="ml-section__sub">A market is a country, because a search, a competitor set and a SERP are all answers to "in which country". The language it is served in is a separate axis.</p>
            {/* Pages no declared market covers, and pages two of them both
                cover. Neither is rounding: both are findings about the setup,
                so both are said out loud under the list they came from. */}
            {mktPages && markets.length > 0 && (mktPages.unassigned > 0 || mktPages.ambiguous > 0) && (
              <p className="ml-section__sub">
                {mktPages.unassigned > 0 && `${fmt(mktPages.unassigned)} of this website's ${fmt(cov ? cov.universe : 0)} pages fall outside every market declared here. `}
                {mktPages.ambiguous > 0 && `${fmt(mktPages.ambiguous)} are claimed by more than one market, which means two addresses overlap.`}
              </p>
            )}
            {markets.length === 0
              ? <div className="hds-card hds-card--pad" style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>No market is declared for this property yet.</div>
              : <div className="ds-lines">
                  {markets.map(m => (
                    <OpenLine key={m.id} icon="flag" tone={m.is_default ? "opportunity" : "neutral"}
                      drawerTitle={m.country_name || m.country || "Market"}
                      drawerSub="One market of this property"
                      lead={<>
                        <span className="ml-propline__name">{m.country_name || m.country}</span>
                        {m.language && <span className="ml-propline__meta">{languageName(m.language)}</span>}
                        <span className="ml-propline__meta">{(ADDRESSING_WORDS[m.addressing] || m.addressing)}{m.address ? " " + m.address : ""}</span>
                        {m.pages != null && <span className="ml-propline__meta">{fmt(m.pages)} page{m.pages === 1 ? "" : "s"}</span>}
                        {m.is_default ? <span className="hds-badge hds-badge--primary">Default</span> : null}
                      </>}>
                      <div className="ml-usage" style={{ gap: 8 }}>
                        <Fact k="Country">{m.country_name || m.country || "Not named"}</Fact>
                        <Fact k="Language">{m.language ? `${languageName(m.language)} (${m.language})` : "Not recorded"}</Fact>
                        <Fact k="How it is reached">{ADDRESSING_WORDS[m.addressing] || m.addressing || "Not recorded"}</Fact>
                        {m.address ? <Fact k="Address">{m.address}</Fact> : null}
                        {/* Counted off the property crawl through the address
                            this market declared, so a market can finally be
                            asked how big it is. Null when there is no crawl:
                            no universe to count against is a different thing
                            from an empty market. */}
                        <Fact k="Pages in this market">{m.pages == null
                          ? "No crawl to count against yet"
                          : `${fmt(m.pages)} of ${fmt(cov ? cov.universe : 0)}${m.claims_whole_property ? ", because it claims the whole website" : ""}`}</Fact>
                        {m.lang_mismatch ? (
                          <Fact k="Pages whose own language disagrees">{fmt(m.lang_mismatch)}</Fact>
                        ) : null}
                        <Fact k="Default market">{m.is_default ? "Yes, this is where the property opens" : "No"}</Fact>
                      </div>
                      {m.lang_mismatch ? (
                        <p className="ml-section__sub" style={{ marginTop: 14 }}>{fmt(m.lang_mismatch)} of these pages declare a different language in their own HTML than this market does. The market still owns them: a language is an attribute of a market and not the thing that decides it, so this is reported and never reassigned.</p>
                      ) : null}
                      <p className="ml-section__sub" style={{ marginTop: 14 }}>Markets are a declaration, not a scope: keywords, topics and business intelligence are still kept per site, so nothing on this property is filtered by this market yet. Markets are changed in the property setup window.</p>
                    </OpenLine>
                  ))}
                </div>}
          </div>
        )}

        {showStructure && (
          <div className="ml-propblock">
            <h2 className="ml-section__h">Structure</h2>
            <p className="ml-section__sub">{shown.length === nodes.length ? (nodes.length === 1 ? "1 piece" : nodes.length + " pieces") : shown.length + " of " + nodes.length}</p>
            {/* Said once, at the top of the list the guess appears in. */}
            <p className="ml-section__sub">Sections are not modelled yet: a blog, documentation, help or shop area is recognised from the address and labelled detected, never declared.</p>
            {shown.length === 0
              ? <div className="hds-card hds-card--pad" style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
                  {nodes.length === 0
                    ? "Nothing is connected to this property yet. Add site opens the connect window, and a site added under this domain joins this property by itself."
                    : "Nothing under this property matches that filter."}
                </div>
              : <div className="ds-lines">
                  {shown.map(n => (
                    <OpenLine key={n.site_id} tone={n.can_publish ? "opportunity" : "neutral"}
                      icon={n.setup === "property_crawl" || n.setup === "standalone" ? "eye" : "globe"}
                      drawerTitle={n.name}
                      drawerSub={SETUP_WORDS[n.setup] || n.setup}
                      action={onOpenSite ? (
                        <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" onClick={() => onOpenSite(n.site_id)}>
                          Open site<MlIcon name="arrow-right" size={16} />
                        </button>
                      ) : null}
                      lead={<>
                        <span className="ml-propline__name">{n.name}</span>
                        {/* A headless connection stores where it is EDITED.
                            Presenting that as the public address is the exact
                            mistake PROPERTY-MODEL.md 4.3 is about. */}
                        <span className="ml-propline__url">{n.can_publish ? "edited at " + hostPath(n.url) : hostPath(n.url)}</span>
                        <span className="ml-propline__meta">{SETUP_WORDS[n.setup] || n.setup}</span>
                        <span className="ml-propline__meta">{fmt(n.pages)} page{Number(n.pages) === 1 ? "" : "s"}</span>
                        {/* Where it publishes, or the fact that it has not
                            said. The badge is the gap made visible on the
                            line it belongs to, rather than only in a banner
                            about the property as a whole. */}
                        {n.can_publish && (n.published_base
                          ? <span className="ml-propline__meta">publishes {n.published_pct != null ? n.published_pct + "% of the website, " : ""}at {hostPath(n.published_base)}</span>
                          : <span className="hds-badge hds-badge--warning" title="Nothing can attribute this piece's published pages to it until it says where they are served.">
                              <span className="hds-badge__dot" />Publish address missing
                            </span>)}
                        {n.section_guess && (
                          <span className="hds-badge hds-badge--outline" title="Detected from this address, not declared">
                            <MlIcon name={SECTION_ICONS[n.section_guess] || "file"} size={12} />{SECTION_WORDS[n.section_guess] || n.section_guess} (detected)
                          </span>
                        )}
                        <span className={"hds-badge hds-badge--" + (n.can_publish ? "success" : "info")}>
                          <span className="hds-badge__dot" />{n.can_publish ? "Can publish" : "Read only"}
                        </span>
                      </>}>
                      <div className="ml-usage" style={{ gap: 8 }}>
                        <Fact k={n.can_publish ? "Edited at" : "Address"}>{hostPath(n.url)}</Fact>
                        <Fact k="Setup">{SETUP_WORDS[n.setup] || n.setup}</Fact>
                        {n.can_publish ? (
                          <Fact k="Publishes at">{n.published_base
                            ? hostPath(n.published_base)
                            : "Not declared yet, so its published pages cannot be attributed to it"}</Fact>
                        ) : null}
                        {/* The second declaration this piece can make, in the
                            same words as the first: what it is, and what it
                            costs while nobody has said. */}
                        {n.help ? <Fact k="Help articles">{helpText(n)}</Fact> : null}
                        {n.published_pages != null ? (
                          <Fact k="Pages of this website it publishes">{n.published_pct != null
                            ? `${fmt(n.published_pages)} (${n.published_pct}% of the website)`
                            : fmt(n.published_pages)}</Fact>
                        ) : null}
                        <Fact k="Pages on record">{fmt(n.pages)}</Fact>
                        <Fact k="Publishing">{n.can_publish
                          ? (n.has_credentials ? "Connected, with credentials stored" : "Connected, but no credentials are stored, so saving is unavailable until it is reconnected")
                          : "Read only. MarketLens measures it and recommends changes, and never publishes to it."}</Fact>
                        {n.section_guess ? <Fact k="Looks like">{(SECTION_WORDS[n.section_guess] || n.section_guess) + ", detected from the address"}</Fact> : null}
                      </div>
                      {n.can_publish && !n.published_base && (
                        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                          <p className="ml-section__sub">The address above is where this piece's content is edited. On a headless setup that is a CMS host nobody visits, so it cannot say which published pages belong here. One answer fixes it.</p>
                          <div>
                            <Button variant="secondary" leadingIcon="edit" onClick={() => setAskPublish(n.site_id)}>
                              Say where it publishes
                            </Button>
                          </div>
                        </div>
                      )}
                      {n.can_publish && n.published_base && n.publishes_elsewhere && (
                        <p className="ml-section__sub" style={{ marginTop: 14 }}>Edited in one place and published in another, which is what makes this one website rather than two: its pages are served at {hostPath(n.published_base)} and read there by the property crawl.</p>
                      )}
                      {n.setup === "property_crawl" && (
                        <p className="ml-section__sub" style={{ marginTop: 14 }}>This is the crawl the property keeps of itself. It is the page universe everything else is measured against, which is why coverage is arithmetic rather than configuration.</p>
                      )}
                      {/* The answer, or the read that makes answering possible.
                          A piece that exposes nothing a help article could live
                          in gets the sentence above and NO button: a selector
                          with nothing in it asks a question it cannot take an
                          answer to. */}
                      {n.help && (helpCanDeclare(n) || helpNeedsRead(n)) && (
                        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                          <p className="ml-section__sub">{n.help.declared
                            ? "Help articles planned for this piece are published into the content type named above. Change it or clear it here: cleared, the planner goes back to briefing a help article and refusing to publish one, which is the safe answer and never a guess."
                            : "A post and a page resolve themselves, because WordPress names both. A help article does not: it is pages under a path on some websites and a category of posts on others, and nothing about a website says which. Answer once and the planner can publish one."}</p>
                          {readTypes && Number(readTypes.site_id) === Number(n.site_id) && readTypes.error && (
                            <Banner tone="danger" title="This site's content types could not be read">
                              {readTypes.error} Nothing was stored, so what this piece can publish is unknown rather than empty.
                            </Banner>
                          )}
                          <div>
                            {helpCanDeclare(n) ? (
                              <Button variant="secondary" leadingIcon="edit" onClick={() => setAskHelp(n.site_id)}>
                                {n.help.declared ? "Change where help articles go" : "Say which type help articles are"}
                              </Button>
                            ) : (
                              <Button variant="secondary" leadingIcon="refresh"
                                disabled={!!(readTypes && Number(readTypes.site_id) === Number(n.site_id) && readTypes.busy)}
                                onClick={() => doReadTypes(n)}>
                                {readTypes && Number(readTypes.site_id) === Number(n.site_id) && readTypes.busy
                                  ? "Asking the site…"
                                  : "Ask this site what content types it has"}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </OpenLine>
                  ))}
                </div>}
          </div>
        )}

        {/* The crawl is the property's whole page universe, so it belongs to
            the unfiltered view: under a filter it would be a total that does
            not answer to what is on screen. */}
        {chip === "all" && (
          <div className="ml-propblock">
            <h2 className="ml-section__h">The property crawl</h2>
            {/* Zeros would read as a measurement. They are an absence, so they
                are not shown: the button that fixes it is named instead. */}
            {needs.own_crawl && !(crawl && crawl.pages > 0) ? (
              <Banner tone="warning" title="This property keeps no crawl of its own">
                {/* Two different situations, and telling someone to save the
                    setup is only true in ONE of them. When a crawl site is
                    already at the public address, saving creates nothing: it
                    is adopting that site which does the job, and the earlier
                    copy promised otherwise to somebody who had just saved. */}
                {adoptable ? (
                  <>Every property should keep one, because that crawl IS its complete page universe.
                  {" "}<b>{adoptable.name}</b> already crawls {hostPath(prop.public_base)}, so nothing new needs to be made.
                  {" "}Open the setup and tick <b>Use it as this property's own crawl</b>, and it stops counting against your plan at the same time.</>
                ) : (
                  <>Every property should keep one, because that crawl IS its complete page universe. There is nothing to read yet, and Sync sitemap has nothing to point at: save the property's setup once and the crawl is created and started.</>
                )}
              </Banner>
            ) : needs.first_crawl ? (
              <Banner tone="info" title="The crawl has not read anything yet">
                The crawl exists but holds no pages, so there is nothing to report rather than nothing to find. Press <b>Sync sitemap</b> above to read the sitemap now.
              </Banner>
            ) : crawl ? (
              <>
                <div className="hds-card hds-card--pad">
                  <div className="ml-usage" style={{ gap: 8 }}>
                    <Fact k="Pages">{fmt(crawl.pages)}</Fact>
                    <Fact k="Indexable">{fmt(crawl.indexable)}</Fact>
                    <Fact k="With a title and a description">{crawl.coverage == null ? "Not measurable yet" : crawl.coverage + "%"}</Fact>
                    <Fact k="Last crawled">{crawl.crawled_at ? timeAgo(crawl.crawled_at) : "Never"}</Fact>
                  </div>
                  {/* The figures are real either way, so this is a note about
                      billing and deletion, not a warning that data is
                      missing. Saying "no crawl" over a screen full of numbers
                      read from one is what made this screen look broken. */}
                  {needs.own_crawl && (
                    <p className="ml-section__sub" style={{ marginTop: 12 }}>
                      {adoptable
                        ? <>Everything above was read by <b>{adoptable.name}</b>, a crawl you added yourself. It is not recorded as this property's own crawl, so it still counts against your plan, and it would survive this property being deleted. Adopting it in the setup changes those two things and nothing else.</>
                        : <>This was read by a crawl you added yourself rather than one the property keeps of itself, so it counts against your plan. The figures are the same either way.</>}
                    </p>
                  )}
                </div>

              </>
            ) : null}
          </div>
        )}

        {/* ── Moving this website's data to the property ───
            The migration in docs/PROPERTY-DB-MIGRATION.md, run and read from
            the screen the property lives on.
            NOT behind the chip filter, unlike the crawl block above, and the
            reason is state rather than taste: a backfill that reported more to
            do hands back a cursor this panel holds in memory, and hiding the
            panel under a filter would unmount it and throw that cursor away
            mid run with nothing on screen to say it had happened. The chips
            filter the pieces of the website; this panel never claims to answer
            them, it is always about the property as a whole. */}
        <PropertyDbPanel property={prop} nodes={nodes} />

        {/* ── Which market each register row is about ──────
            Step 14 of the same document, and a separate panel rather than a
            fourth line in the one above: that panel answers which WEBSITE and
            which PART of it a row belongs to, this one answers which MARKET of
            the business the row is about, and PROPERTY-MODEL.md 3.3 keeps those
            two apart on purpose. They are also deleted on different days, and
            each has to stay deletable in one commit.
            Not behind the chip filter, for the reason the panel above gives:
            the fill holds "was the last run cut short" in memory, and a filter
            that unmounted the panel would throw that away mid run. */}
        <MarketDbPanel property={prop} />

        {askPublish && (
          <PromptModal
            title="Where does each piece publish?"
            sub="A CMS address is where content is edited. This is where its pages are actually served, which is the only thing that can tell MarketLens which published pages belong to which piece."
            confirmLabel="Save addresses"
            fields={(askPublish === true ? nodes.filter(n => n.can_publish) : nodes.filter(n => Number(n.site_id) === Number(askPublish)))
              .map(n => ({
                key: "s" + n.site_id,
                label: n.name,
                value: suggestPublish(n),
                icon: "globe",
                placeholder: prop.public_base || "https://",
                // Said on every field, because a pre-filled suggestion that
                // does not announce itself is how a guess becomes a record.
                hint: n.published_base
                  ? "Edited at " + hostPath(n.url)
                  : "Suggested from where it is edited (" + hostPath(n.url) + "). Change it if that is not where these pages appear.",
              }))}
            onClose={() => setAskPublish(null)}
            onSubmit={async (vals) => {
              const list = Object.entries(vals).map(([k, v]) => ({ site_id: Number(k.slice(1)), published_base: v }));
              await api(`/account/properties/${prop.id}/publishes`, {
                method: "PATCH", body: JSON.stringify({ publishes: list }),
              });
              setAskPublish(null);
              load(true);
            }} />
        )}

        {/* ── Which content type a piece's help articles are ────
            ONE SELECTOR, whose options are THE SITE'S OWN CONTENT TYPES, read
            from the site and filtered to the ones it serves a writable address
            for. Never a hard coded list of two: an install that grows a help
            plugin with its own `docs` type appears in here with no code
            change, and an install that reports nothing writable never opens
            this dialog at all.

            THE EMPTY OPTION IS FIRST AND IT IS A REAL ANSWER. "Not declared
            yet" has to stay sayable forever, because it is what distinguishes
            a question nobody has answered from an answer somebody gave: a
            default would be a guess indistinguishable from the customer's own
            words, and this one redirects writes to their live website. Picking
            it clears the declaration, which returns the planner to briefing a
            help article and refusing to publish one. */}
        {askHelp && (() => {
          const n = nodes.find(x => Number(x.site_id) === Number(askHelp));
          if (!n || !n.help) return null;
          const h = n.help;
          // The declared type is listed even when the site's latest read no
          // longer offers it: a selector that silently shows something other
          // than what is stored is how a declaration gets changed by accident.
          const extra = h.declared && h.type && !h.candidates.some(c => c.slug === h.type)
            ? [{ slug: h.type, name: h.type }] : [];
          const options = [
            { value: "", label: "Not declared yet" },
            ...extra.map(c => ({ value: c.slug, label: `${c.name || c.slug} (${c.slug}, declared here and not in this site's latest read)` })),
            ...h.candidates.map(c => ({ value: c.slug, label: c.name && c.name !== c.slug ? `${c.name} (${c.slug})` : c.slug })),
          ];
          return (
            <PromptModal
              title="Which content type are this site's help articles?"
              sub={`Read from ${hostPath(n.url)} on ${h.types_read_at ? new Date(h.types_read_at).toLocaleDateString() : "an unrecorded date"}. Nothing is suggested: no fact about a website says which of its content types holds its help articles, so this is yours to state and it stays blank until you do.`}
              confirmLabel="Save"
              fields={[{
                key: "type",
                label: "Help articles are",
                type: "select",
                value: h.declared && h.type ? h.type : "",
                options,
                hint: "Leaving this as Not declared yet clears any answer already here, and the planner goes back to briefing a help article rather than publishing one.",
              }]}
              onClose={() => setAskHelp(null)}
              onSubmit={async (vals) => {
                const slug = String(vals.type || "").trim();
                const q = `siteUrl=${encodeURIComponent(n.url)}`;
                if (slug) {
                  await api(`/wp/publish-target?${q}`, {
                    method: "POST", body: JSON.stringify({ kind: "help", type: slug }),
                  });
                } else {
                  // Clearing is the other half of declaring, not a gap: a wrong
                  // answer that cannot be undone is worse than no answer.
                  await api(`/wp/publish-target?${q}&kind=help`, { method: "DELETE" });
                }
                setAskHelp(null);
                load(true);
              }} />
          );
        })()}
      </>
    );
  }

  function Properties({ onOpenSite, go, onOfferAssist, onOfferBack, focus }) {
    const { Button } = DS();
    const [props, setProps] = useState([]);
    const [metrics, setMetrics] = useState({});
    const [propMetrics, setPropMetrics] = useState({});
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    // {type:"wizard"} to add, {type:"wizard",p} to edit that property: the same
    // five questions either way, because repairing a property made before they
    // were asked is just answering them late.
    const [modal, setModal] = useState(null);   // {type:"wizard",p?} | {type:"delete-property",p} | {notice}
    // Which property is OPEN. Clicking a card used to leave the console for
    // the Sites tab; a property is a thing in its own right, so it opens here
    // and the back affordance returns to this list.
    const [openId, setOpenId] = useState(null);
    // Bumped when the wizard saves, so the open property re-reads its
    // structure: markets added in that window appear without a reload.
    const [rev, setRev] = useState(0);
    // Arriving from a coverage tab's Back to property: reopen that property
    // rather than the list, because that is the screen the person left.
    useEffect(() => {
      if (focus && focus.open != null) setOpenId(Number(focus.open));
    }, [focus]);
    const load = useCallback(() => {
      setLoading(true); setErr(null);
      Promise.all([
        api("/account/properties"),
        api("/account/reports").catch(() => ({ sites: [] })),
      ]).then(([p, r]) => {
        const m = {}; ((r && r.sites) || []).forEach(s => { m[Number(s.site_id)] = s; });
        // The DEDUPLICATED per property figures, kept apart from the per site
        // ones. /account/reports answers both, and this card was reading the
        // wrong half: see propRollup below.
        const pm = {}; ((r && r.properties) || []).forEach(x => { pm[Number(x.id)] = x; });
        setPropMetrics(pm);
        setProps((p && p.properties) || []); setMetrics(m); setLoading(false);
      }).catch(e => { setErr(e); setLoading(false); });
    }, []);
    useEffect(() => { load(); }, [load]);
    // CLICKS ARE READ, NEVER SUMMED, and this card is where that error reached a
    // human. A Search Console DOMAIN property covers a whole domain, so two
    // connections of one property can hold a row for the SAME page on the same
    // day: measured on a real account, 6,104 impressions reported for a
    // property that had 3,487, a 75% inflation that reads as growth. Summing
    // the per site rollups here rebuilt exactly that, while the deduplicated
    // union sat unused in `properties` of the same response.
    //
    // The other three ARE summed, and that is right: a plan, a keyword and a
    // to-do belong to one connection and are never shared with another, which
    // is why routes/account.js sums those three per property too.
    const rollup = (p) => {
      const summed = (p.sites || []).reduce((a, s) => {
        const m = metrics[Number(s.site_id)] || {};
        a.plans += m.plans || 0; a.keywords += m.keywords || 0; a.open_todos += m.open_todos || 0;
        return a;
      }, { plans: 0, keywords: 0, open_todos: 0 });
      // Absent is not zero: with no property row in the answer the clicks are
      // UNKNOWN, and null renders as no figure rather than as none.
      const u = propMetrics[Number(p.id)];
      return {
        clicks: u && u.clicks != null ? u.clicks : null,
        plans: summed.plans, keywords: summed.keywords, open_todos: summed.open_todos,
      };
    };
    const primaryOf = (p) => p.sites.find(s => Number(s.site_id) === Number(p.primary_site_id)) || p.sites[0];
    const open = (p) => { const s = primaryOf(p); if (s && onOpenSite) onOpenSite(s.site_id); };
    // Clicking the card opens the property's own screen inside the console;
    // only the explicit "Open property" button enters the app on the primary
    // site. The open property is looked up in the list every render, so a
    // delete (or a reload that drops it) falls back to the list by itself.
    const opened = openId == null ? null : (props.find(p => Number(p.id) === Number(openId)) || null);
    return (
      <div className="ml-section">
        {!opened && <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h2 className="ml-section__h">Properties</h2><p className="ml-section__sub">{props.length} propert{props.length === 1 ? "y" : "ies"}, one per domain. Open a property to see what it is made of; the explicit Open property button enters the app on its primary site.</p></div>
          <div className="ml-pagehead__actions"><Button variant="primary" leadingIcon="plus" onClick={() => setModal({ type: "wizard" })}>Add a property</Button></div>
        </div>}
        {/* The dialogs sit outside the list/screen branch: Add market on the
            property screen opens this same wizard, and it must survive the
            screen it was opened from re-rendering underneath it. */}
        {modal && modal.type === "wizard" && <AddPropertyWizard
          property={modal.p || null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); setRev(r => r + 1); }}
          onDelete={() => setModal({ type: "delete-property", p: modal.p })} />}
        {/* Deleting a property removes a GROUPING, never content. Saying so is
            the point of the copy: the word "delete" next to something holding
            1,330 keywords is alarming, and it should be clear that none of
            that is at stake. A property with sites is refused by the server,
            with its own sentence shown as-is. */}
        {/* The property's OWN crawl is not a site in the way: the property
            made it without being asked, and deleting the property takes it.
            Counting it here would tell the owner to remove something they
            never added, to delete something that created it. */}
        {modal && modal.type === "delete-property" && <ConfirmDialog danger
          title={`Delete ${modal.p.name || modal.p.root_domain}`}
          body={addedSites(modal.p).length
            ? `${modal.p.name || modal.p.root_domain} still has ${addedSites(modal.p).length} site${addedSites(modal.p).length === 1 ? "" : "s"}. Remove or move ${addedSites(modal.p).length === 1 ? "it" : "them"} under Sites first: a property is where sites are grouped, so it cannot be deleted while any still belong to it.`
            : `Delete this property, the markets set up for it, and the crawl it keeps of its own pages? No connected site, content, keywords or history is affected. You can add it again at any time.`}
          confirmLabel={addedSites(modal.p).length ? "Go to Sites" : "Delete property"}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            if (addedSites(modal.p).length) { setModal(null); go && go("sites"); return; }
            try {
              await api(`/account/properties/${modal.p.id}`, { method: "DELETE" });
              setModal(null); load();
            } catch (e) {
              // The server refuses a property that still has sites, and its
              // sentence names them, so it is shown rather than paraphrased.
              setModal({ notice: true, title: "Not deleted", body: (e && e.message) || "Could not delete that property." });
            }
          }} />}
        {modal && modal.notice && <NoticeModal title={modal.title} body={modal.body} onClose={() => setModal(null)} />}
        {opened ? (
          <PropertyScreen property={opened} rev={rev}
            onBack={() => setOpenId(null)}
            onEdit={() => setModal({ type: "wizard", p: opened })}
            onOpenSite={onOpenSite} go={go} onOfferAssist={onOfferAssist} onOfferBack={onOfferBack} />
        ) : loading ? <Loading /> : err ? <ErrorNote err={err} /> : props.length === 0 ? (
          <div className="hds-card hds-card--pad" style={{ textAlign: "center", color: "var(--text-muted)" }}>No properties yet, click <b>Add a property</b> and answer a few questions about your website, or connect a site under Sites and its domain becomes a property automatically.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {props.map(p => {
              const u = rollup(p);
              const primary = primaryOf(p);
              return (
                <div className="ml-sitecard" key={p.id} onClick={() => setOpenId(p.id)} title="Open this property: its markets, what it is made of, and the crawl it keeps of itself" style={{ cursor: "pointer" }}>
                  <div className="ml-sitecard__top">
                    <span className="ml-sitecard__flag"><MlIcon name="layers" size={16} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}><div className="ml-sitecard__name">{p.name || p.root_domain}</div><div className="ml-sitecard__url">{p.root_domain}</div></div>
                  </div>
                  <div className="ml-sitecard__conns">
                    <span className="ml-conn-pill ml-conn-pill--ok"><MlIcon name="globe" size={13} />{p.sites.length} site{p.sites.length === 1 ? "" : "s"}</span>
                    {/* Markets are the declaration the wizard saves, so a property
                        that has them says so here rather than only in its record. */}
                    {(p.markets || []).length > 0 && (
                      <span className="ml-conn-pill" title={p.markets.map(m => m.country_name + (m.language ? " (" + m.language + ")" : "")).join(", ")}>
                        <MlIcon name="flag" size={13} />{p.markets.length === 1 ? p.markets[0].country_name : p.markets.length + " markets"}
                      </span>
                    )}
                    {primary && <span className="ml-conn-pill" title="Primary site: opens first"><MlIcon name="star" size={13} />{primary.name || String(primary.url || "").replace(/^https?:\/\//, "")}</span>}
                  </div>
                  <div className="ml-usage" style={{ gap: 8 }}>
                    <div className="ml-usage__row"><span>Clicks (30d)</span><b title="Counted once per page across this website's connections, never summed, because two connections of one website can report the same page on the same day">{u.clicks == null ? "not measured" : fmt(u.clicks)}</b></div>
                    <div className="ml-usage__row"><span>Content plans</span><b>{fmt(u.plans)}</b></div>
                    <div className="ml-usage__row"><span>Keywords</span><b>{fmt(u.keywords)}</b></div>
                    <div className="ml-usage__row"><span>Open to-dos</span><b>{fmt(u.open_todos)}</b></div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: "auto" }}>
                    {p.sites.length
                      ? <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" style={{ flex: 1, justifyContent: "center" }} onClick={(e) => { e.stopPropagation(); open(p); }}>Open property<MlIcon name="arrow-right" size={16} /></button>
                      : <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" style={{ flex: 1, justifyContent: "center" }} onClick={(e) => { e.stopPropagation(); go && go("sites"); }}>Add a site<MlIcon name="plus" size={16} /></button>}
                    {/* The pencil opens the full setup, not a rename box. A
                        property is its public address and its markets, so the
                        place to change any of that is the window that asked for
                        it, and a property that was never asked gets asked here. */}
                    <button type="button" className="hds-btn hds-btn--ghost hds-btn--sm" title="Edit this property's setup, or delete it" aria-label="Edit property" style={{ flex: "0 0 auto" }} onClick={(e) => { e.stopPropagation(); setModal({ type: "wizard", p }); }}><MlIcon name="edit" size={16} /></button>
                    {/* Deleting lives inside that window, not here. On the card
                        it was a second icon sitting one slip away from Open,
                        for an action that cannot be undone. */}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Add a site, from wherever it is asked for ──────────────────────────────
  //
  // Lifted out of the Sites tab so the property page can open it IN PLACE. On
  // a property, the site is being added TO that property, so navigating away
  // to add one loses the screen the person was working on, and the property
  // they were working on with it.
  //
  // It owns its own success notice and hands pairing back to its caller: both
  // of those were the Sites tab's `modal` state, which is the one thing that
  // could not travel.
  function AddSiteDialog({ onClose, onDone, onPair }) {
    const [notice, setNotice] = useState(null);
    const hostOf = (u) => String(u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    // The reload happens at once and the notice stays until it is read: the
    // screen behind must be true the moment the site exists, and the sentence
    // saying what happened must not be swept away by that refresh.
    const done = (n) => { setNotice(n); if (onDone) onDone(); };
    if (notice) return <NoticeModal title={notice.title} body={notice.body} onClose={onClose} />;
    return (
      <PromptModal title="Add a site"
          sub={v => (v.type === "crawl"
            ? "MarketLens will read this site by fetching its sitemap and then each page, the way a search engine does. It measures the site and recommends changes, and it cannot publish to it, so every change stays yours to make."
            : v.type === "siteatlas"
              ? "A SiteAtlas install connects by pairing, with no password typed anywhere: you enter a short code in the plugin, the plugin creates its own Application Password, and every directory arrives as its own site."
              : v.type === "wp_network"
                ? "One login for the whole network. In a WordPress multisite the network admin credential authenticates every blog, so each site is connected without a password of its own."
                : "Connect one WordPress site with its own username and Application Password. Prefer not to copy a password? Use Pair plugin instead.")}
          confirmLabel={v => (v.type === "crawl" ? "Add site" : v.type === "siteatlas" ? "Pair plugin" : "Connect")}
          fields={[
            { key: "type", label: "What are you adding?", type: "select", value: "single", options: [
              { value: "single",     label: "A single WordPress site" },
              { value: "siteatlas",  label: "A SiteAtlas install (directories behind one WordPress)" },
              { value: "wp_network", label: "A WordPress multisite network (all sites, one login)" },
              { value: "crawl",      label: "A site with no CMS (read only, never published to)" },
            ] },
            // Pairing is started FROM the plugin, so there is no address to
            // type: a URL field here would be collected and never used.
            { key: "url", label: v => (v.type === "crawl" ? "Public URL" : "URL"), placeholder: "https://example.com", icon: "globe",
              when: v => v.type !== "siteatlas",
              hint: v => (v.type === "crawl"
                ? "The address visitors use. There is nothing to sign in to, so no password is needed."
                : v.type === "wp_network"
                  ? "The main WordPress URL of the network, which is where the network admin signs in."
                  : "The WordPress address, which is where you sign in to edit.") },
            { key: "crawl_name", label: "Display name", placeholder: "Optional", icon: "tag",
              when: v => v.type === "crawl", hint: "Optional. Left empty, the site is named after its domain." },
            { key: "wp_username", label: "WordPress username", icon: "user", when: v => WP_KINDS.includes(v.type) },
            { key: "wp_app_password", label: "Application Password", type: "password", icon: "key", when: v => WP_KINDS.includes(v.type) },
          ]}
        onClose={onClose}
          onSubmit={async (v) => {
            const url = (v.url || "").trim();
            // A site with no CMS: no credentials are read, and the server's own
            // sentence is what surfaces for 402 SITE_LIMIT and 409 SITE_EXISTS,
            // because the plan limit and the duplicate are its facts to state.
            if (v.type === "crawl") {
              if (!url) throw new Error("A public URL is required.");
              const name = (v.crawl_name || "").trim();
              const r = await api("/account/sites/crawl", { method: "POST", body: JSON.stringify(name ? { url, name } : { url }) });
              const added = (r && r.site) || {};
              const label = added.name || hostOf(added.site_url) || hostOf(url);
              done({ title: "Site added", body: `${label} is in your account as a crawl-only site. Open it, go to Page Inventory and press Refresh to read its pages. MarketLens will measure this site and recommend changes, and will never publish to it.` });
              return;
            }
            // SiteAtlas: nothing to submit here, because pairing is the
            // connect. Handing over to the pairing window rather than asking
            // for a password keeps the one install type that needs no password
            // from being the only one that is asked for one.
            if (v.type === "siteatlas") { onPair && onPair(); return; }
            if (!url) throw new Error("URL is required.");
            if (v.type === "wp_network") {
              if (!v.wp_username || !v.wp_app_password) throw new Error("Username + Application Password are required for a multisite network.");
              const r = await api("/account/sites/connect-network", { method: "POST", body: JSON.stringify({ url, wp_username: v.wp_username.trim(), wp_app_password: v.wp_app_password.trim() }) });
              done({ title: "Network connected", body: `Connected ${(r.added || 0) + (r.updated || 0)} of ${r.total || 0} network site(s) with one login.` });
            } else {
              const creds = { wp_username: (v.wp_username || "").trim(), wp_app_password: (v.wp_app_password || "").trim() };
              try {
                await api("/account/sites", { method: "POST", body: JSON.stringify({ url, ...creds }) });
                done({ title: "Site connected", body: "It may take a moment to appear in the site switcher - reload the app to see it there." });
              } catch (e) {
                // Multi-site install connected as "single": upgrade to the
                // network connect automatically instead of showing the 409 and
                // making the user re-open the modal to flip the select. Same
                // creds, and the plan's site limit still guards on the server.
                if (!(e && e.data && e.data.code === "USE_NETWORK_CONNECT" && creds.wp_username && creds.wp_app_password)) throw e;
                const n = e.data.count;
                const r = await api("/account/sites/connect-network", { method: "POST", body: JSON.stringify({ url, ...creds }) });
                done({ title: "Network connected", body: `This install contains ${n} sites (a multisite network, or SiteAtlas directories), so they were all connected with the one login you gave: ${(r.added || 0) + (r.updated || 0)} of ${r.total || n}. Choosing A WordPress multisite network, or pairing a SiteAtlas install, reaches the same place without the retry.` });
              }
            }
          }} />
    );
  }

  function Sites({ onExit, onOpenSite, focus }) {
    const { Button, IconButton } = DS();
    const [rows, setRows] = useState([]);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("grid");
    const [modal, setModal] = useState(null);   // null | "add" | "restore" | {type:"delete",site} | {notice}
    const load = useCallback(() => {
      setLoading(true); setErr(null);
      Promise.all([
        api("/account/sites").catch(() => ({ sites: [] })),   // id, url, has_creds (for management)
        api("/account/reports").catch(() => ({ sites: [] })),  // per-site metrics
      ]).then(([s, r]) => {
        const metrics = {}; ((r && r.sites) || []).forEach(m => { metrics[Number(m.site_id)] = m; });
        setRows(((s && s.sites) || []).map(site => {
          const m = metrics[Number(site.site_id)] || {};
          return { ...m, ...site, name: site.name || m.name };
        }));
        setLoading(false);
      }).catch(e => { setErr(e); setLoading(false); });
    }, []);
    useEffect(() => { load(); }, [load]);
    // Arriving from a property card: scroll to that property's group and
    // flash it, so "this property's connected sites" is visibly answered.
    useEffect(() => {
      if (loading || !focus || !focus.property) return;
      const el = document.getElementById("ml-prop-group-" + focus.property);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ml-aiflash");
      const t = setTimeout(() => el.classList.remove("ml-aiflash"), 1500);
      return () => clearTimeout(t);
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps
    // Arriving from Add blog on the property screen: a blog IS a connected
    // site today, so the honest destination is this tab's own connect window,
    // opened rather than merely pointed at.
    useEffect(() => { if (focus && focus.add) setModal("add"); }, [focus]);
    const remove = async (site) => { await api(`/account/sites/${site.id}`, { method: "DELETE" }); setModal(null); load(); };
    const count = rows.length;
    const hostOf = (u) => String(u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    // A crawl-only site: a public website with no CMS behind it, which
    // MarketLens measures and never publishes to.
    const isCrawl = (s) => !!(s && s.source === "crawl");
    const CRAWL_HINT = "MarketLens reads this site by crawling its public pages. It measures and recommends, and never publishes to it.";
    // Honest credential status: 'ok' | 'unreadable' (stored but won't decrypt) |
    // 'none'. Falls back to the legacy has_creds flag if the API is older.
    //
    // A crawl-only site is answered BEFORE the credential question is asked,
    // and this ordering is the whole point. Such a site has no CMS, so it has
    // no credentials and never will: the backend reports creds 'none' quite
    // truthfully, and running that through the branches below badged a
    // perfectly healthy site "No credentials" in a WARNING tone, inviting its
    // owner to repair something that was never meant to exist. The crawl badge
    // REPLACES the credential badge rather than sitting beside it, because two
    // badges where one of them says "no credentials" is still confusing.
    const credInfo = (s) => {
      if (isCrawl(s)) return { tone: "info", pill: "crawl", icon: "eye", label: "Crawl only", title: CRAWL_HINT };
      const c = s.creds || (s.has_creds ? "ok" : "none");
      if (c === "ok") return { tone: "success", pill: "ok", icon: "check", label: "Connected" };
      if (c === "unreadable") return { tone: "warning", pill: "off", icon: "alert-triangle", label: "Reconnect needed" };
      return { tone: "warning", pill: "off", icon: "x", label: "No credentials" };
    };
    const connBadge = (s) => { const i = credInfo(s); return <span className={"hds-badge hds-badge--" + i.tone} title={i.title}><span className="hds-badge__dot" />{i.label}</span>; };
    // SiteAtlas-backed sites: their SEO title + meta description sync to the
    // live page head on save (the backend writes the SiteAtlas override keys).
    const atlasBadge = (s) => s.source === "siteatlas"
      ? <span className="hds-badge" title="A SiteAtlas directory. SEO titles and meta descriptions saved here publish to the live page head."><span className="hds-badge__dot" />SiteAtlas</span>
      : null;
    // Open THIS site in the app (falls back to plain exit if the shell didn't
    // pass a handler: e.g. an older mount).
    const open = (s) => (onOpenSite ? onOpenSite(s.site_id) : onExit());
    // Group by property (domain root); property fields come from /account/sites.
    const groups = [];
    {
      const byProp = new Map();
      rows.forEach(r => {
        const k = r.property_root || hostOf(r.url);
        let g = byProp.get(k);
        if (!g) { g = { key: k, name: r.property_name || k, rows: [] }; byProp.set(k, g); groups.push(g); }
        g.rows.push(r);
      });
    }
    const GroupHead = ({ g }) => (
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "4px 0 10px" }}>
        <MlIcon name="layers" size={14} />
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{g.name}</span>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{g.rows.length} site{g.rows.length === 1 ? "" : "s"}</span>
      </div>
    );
    return (
      <div className="ml-section">
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h2 className="ml-section__h">Connected sites</h2><p className="ml-section__sub">{count} site{count === 1 ? "" : "s"} across your network. Counts are read live from your account.</p></div>
          <div className="ml-pagehead__actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div className="bi-viewtoggle" role="tablist" aria-label="View mode">
              <button className={view === "grid" ? "on" : ""} title="Grid view" aria-pressed={view === "grid"} onClick={() => setView("grid")}><MlIcon name="grid" size={16} /></button>
              <button className={view === "list" ? "on" : ""} title="List view" aria-pressed={view === "list"} onClick={() => setView("list")}><MlIcon name="list" size={16} /></button>
            </div>
            {/* Restoring credentials is account-wide, so it stays as long as
                ONE site could use it. An account whose every site is crawl-only
                has no credential to restore, and the button would be an offer
                to fix nothing. */}
            {rows.some(s => !isCrawl(s)) && <Button variant="secondary" leadingIcon="refresh" onClick={() => setModal("restore")}>Restore network credentials</Button>}
            <Button variant="secondary" leadingIcon="link" onClick={() => setModal("pair")}>Pair plugin</Button>
            {/* No separate button for a site with no CMS: it is a kind of site,
                so it is a choice inside Add site. As its own button it was
                invisible to anyone who opened Add site looking for it. */}
            <Button variant="primary" leadingIcon="plus" onClick={() => setModal("add")}>Add site</Button>
          </div>
        </div>
        {modal === "restore" && <PromptModal title="Restore WordPress credentials" sub="Enter your network (main-site) admin username and an Application Password. In a multisite network this one credential authenticates every blog, so it's applied to all your sites." confirmLabel="Restore"
          fields={[
            { key: "wp_username", label: "WordPress username", icon: "user" },
            { key: "wp_app_password", label: "Application Password", type: "password", icon: "key" },
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => {
            if (!v.wp_username || !v.wp_app_password) throw new Error("Both fields are required.");
            const r = await api("/account/sites/backfill-network-creds", { method: "POST", body: JSON.stringify({ wp_username: v.wp_username.trim(), wp_app_password: v.wp_app_password.trim(), overwrite: true }) });
            setModal({ notice: true, title: "WordPress credentials restored", body: `Stored credentials for ${r.updated} site${r.updated === 1 ? "" : "s"}. You can now save posts and pages.` });
            load();
          }} />}
        {modal === "pair" && <PairPluginModal onClose={() => setModal(null)} onPaired={load} />}
        {/* ONE DIALOG, FOUR KINDS OF SITE, and what separates them is what
            they can be asked for.

            A single WordPress and a WordPress multisite both authenticate with
            a username and an Application Password, so those two ask for one.
            The other two cannot be asked: a SiteAtlas install connects by
            PAIRING, where the plugin creates its own password and nobody types
            one, and a site with no CMS has nothing to sign in to at all. So the
            credential fields are gated on the WordPress kinds rather than on
            "not crawl", which is what they were gated on while SiteAtlas and
            multisite were one option.

            SiteAtlas and multisite were one option because one endpoint
            connects both. That is a fact about the server and it was never a
            fact about the customer: somebody running SiteAtlas directories on
            a single WordPress was being asked to recognise their install in
            the word "network", and somebody running a real multisite was being
            offered SiteAtlas in the same breath. They are now two options with
            two different answers about passwords. */}
        {modal === "add" && (
          <AddSiteDialog onClose={() => setModal(null)} onDone={load} onPair={() => setModal("pair")} />
        )}
        {/* Guarded as well as hidden: a crawl-only site reaching the reconnect
            modal could only ever ask for credentials it has no use for. */}
        {modal && modal.type === "reconnect" && !isCrawl(modal.site) && <PromptModal title={`Reconnect ${modal.site.name || hostOf(modal.site.url) || "site"}`} sub="Re-enter this site's WordPress username and a fresh Application Password. This updates only this site and keeps all its data (keywords, SEO, plans)." confirmLabel="Reconnect"
          fields={[
            { key: "wp_username", label: "WordPress username", icon: "user" },
            { key: "wp_app_password", label: "Application Password", type: "password", icon: "key" },
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => {
            if (!v.wp_username || !v.wp_app_password) throw new Error("Both fields are required.");
            await api(`/account/sites/${modal.site.id}/reconnect`, { method: "POST", body: JSON.stringify({ wp_username: v.wp_username.trim(), wp_app_password: v.wp_app_password.trim() }) });
            setModal({ notice: true, title: "Site reconnected", body: "Credentials updated. You can now read drafts and save posts and pages." });
            load();
          }} />}
        {modal && modal.notice && <NoticeModal title={modal.title} body={modal.body} onClose={() => setModal(null)} />}
        {/* "Its content stays in WordPress" is true of a connected site and
            nonsense about a crawl-only one, which has no WordPress: what it
            loses is the crawl record MarketLens keeps. */}
        {modal && modal.type === "delete" && <ConfirmDialog danger title="Remove site" body={`Remove ${modal.site.name || hostOf(modal.site.url) || "this site"} from your account? ${isCrawl(modal.site) ? "The site itself is untouched. Only what MarketLens has read and stored about it is removed." : "Its content stays in WordPress. Only the connection here is removed."}`} confirmLabel="Remove site"
          onClose={() => setModal(null)}
          onConfirm={async () => { try { await remove(modal.site); } catch (e) { setModal({ notice: true, title: "Couldn't remove site", body: (e && e.message) || "Action failed." }); } }} />}

        {loading ? <Loading /> : err ? <ErrorNote err={err} /> : rows.length === 0 ? (
          <div className="hds-card hds-card--pad" style={{ textAlign: "center", color: "var(--text-muted)" }}>No sites connected to this account yet.</div>
        ) : view === "grid" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {groups.map(g => (
              <div key={g.key} id={"ml-prop-group-" + g.key}>
                <GroupHead g={g} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                  {g.rows.map(s => (
                    <div className="ml-sitecard" key={s.id || s.site_id} onClick={() => open(s)} style={{ cursor: "pointer" }}>
                      <div className="ml-sitecard__top">
                        <span className="ml-sitecard__flag"><MlIcon name="globe" size={16} /></span>
                        <div style={{ flex: 1, minWidth: 0 }}><div className="ml-sitecard__name">{s.name}</div>{s.url && <div className="ml-sitecard__url">{hostOf(s.url)}</div>}</div>
                      </div>
                      <div className="ml-sitecard__conns">
                        {(() => { const i = credInfo(s); return <span className={"ml-conn-pill ml-conn-pill--" + i.pill} title={i.title}><MlIcon name={i.icon} size={13} />{i.label}</span>; })()}
                        {s.source === "siteatlas" && <span className="ml-conn-pill" title="A SiteAtlas directory. SEO titles and meta descriptions saved here publish to the live page head."><MlIcon name="layers" size={13} />SiteAtlas</span>}
                      </div>
                      <div className="ml-usage" style={{ gap: 8 }}>
                        <div className="ml-usage__row"><span>Clicks (30d)</span><b>{fmt(s.clicks)}</b></div>
                        <div className="ml-usage__row"><span>Content plans</span><b>{fmt(s.plans)}</b></div>
                        <div className="ml-usage__row"><span>Keywords</span><b>{fmt(s.keywords)}</b></div>
                        <div className="ml-usage__row"><span>Open to-dos</span><b>{fmt(s.open_todos)}</b></div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: "auto" }}>
                        <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" style={{ flex: 1, justifyContent: "center" }} onClick={(e) => { e.stopPropagation(); open(s); }}>Open site<MlIcon name="arrow-right" size={16} /></button>
                        {/* A crawl-only site has nothing to reconnect: offering
                            the control would promise a repair for a connection
                            that does not exist. */}
                        {!isCrawl(s) && <button type="button" className="hds-btn hds-btn--ghost hds-btn--sm" title="Reconnect / update password" aria-label="Reconnect site" style={{ flex: "0 0 auto" }} onClick={(e) => { e.stopPropagation(); setModal({ type: "reconnect", site: s }); }}><MlIcon name="key" size={16} /></button>}
                        <button type="button" className="hds-btn hds-btn--ghost hds-btn--sm" title="Remove site" aria-label="Remove site" style={{ flex: "0 0 auto" }} onClick={(e) => { e.stopPropagation(); setModal({ type: "delete", site: s }); }}><MlIcon name="trash" size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {groups.map(g => (
              <div key={g.key} id={"ml-prop-group-" + g.key}>
                <GroupHead g={g} />
                <div className="hds-table-wrap">
                  <table className="hds-table">
                    <thead><tr><th>Site</th><th>Connection</th><th>Clicks (30d)</th><th>Plans</th><th>Keywords</th><th>To-dos</th><th style={{ width: 80 }}></th></tr></thead>
                    <tbody>
                      {g.rows.map(s => (
                        <tr key={s.id || s.site_id} style={{ cursor: "pointer" }} onClick={() => open(s)}>
                          <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="ml-sitecard__flag" style={{ width: 28, height: 28 }}><MlIcon name="globe" size={14} /></span><div><div className="ml-rowtitle">{s.name}</div>{s.url && <div className="ml-rowsub">{hostOf(s.url)}</div>}</div></div></td>
                          <td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{connBadge(s)}{atlasBadge(s)}</div></td>
                          <td className="hds-table__muted">{fmt(s.clicks)}</td>
                          <td className="hds-table__muted">{fmt(s.plans)}</td>
                          <td className="hds-table__muted">{fmt(s.keywords)}</td>
                          <td className="hds-table__muted">{fmt(s.open_todos)}</td>
                          <td><div className="ml-rowactions" onClick={(e) => e.stopPropagation()}><IconButton icon="external-link" label="Open site" size="sm" onClick={() => open(s)} />{!isCrawl(s) && <IconButton icon="key" label="Reconnect / update password" size="sm" onClick={() => setModal({ type: "reconnect", site: s })} />}<IconButton icon="trash" label="Remove site" size="sm" onClick={() => setModal({ type: "delete", site: s })} /></div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function Reports() {
    const { StatCard, Select } = DS();
    const { data, err, loading } = useApi("/account/reports");
    // Which property the figures are shown for. "" is the whole account, which
    // stays the default: a tenant with one property should never have to
    // choose, and one with several wants the rollup first.
    const [scope, setScope] = useState("");
    if (loading) return <Loading />;
    if (err) return <ErrorNote err={err} />;
    const props = (data && data.properties) || [];
    const picked = props.find(p => String(p.id) === scope) || null;
    // Figures come from whichever scope is selected. Both are UNIONS computed
    // server-side, never sums of their sites: a domain property covers a whole
    // domain, so two sites under it can hold the same page and adding them up
    // double counts it.
    const t = picked ? { ...picked, sites: picked.sites } : ((data && data.totals) || {});
    const allSites = (data && data.sites) || [];
    const inScope = picked ? allSites.filter(s => picked.site_ids.includes(s.site_id)) : allSites;
    const bySite = inScope.filter(s => s.clicks > 0);
    const max = Math.max(1, ...bySite.map(s => s.clicks));
    const ctrPct = t.ctr != null ? (t.ctr * 100).toFixed(1) + "%" : "-";
    const pos = t.position != null ? Number(t.position).toFixed(1) : "-";
    const label = picked ? picked.name : "All properties";
    return (
      <div>
        <div className="ml-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
            <div>
              <h2 className="ml-section__h">{picked ? "Property rollup" : "Account rollup"}</h2>
              <p className="ml-section__sub">
                {picked
                  ? <>The whole of <b>{picked.root_domain}</b> · {fmt(picked.sites)} site{picked.sites === 1 ? "" : "s"} counted once</>
                  : <>Across all {fmt(t.sites)} sites in {fmt(t.properties)} propert{t.properties === 1 ? "y" : "ies"}</>}
                {" "}· Search Console, last ~28 days
              </p>
            </div>
            {props.length > 0 && (
              <div style={{ minWidth: 240 }}>
                <Select value={scope} onChange={e => setScope(e.target.value)} aria-label="Scope">
                  <option value="">All properties</option>
                  {props.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </Select>
              </div>
            )}
          </div>
          <div style={{ height: 14 }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16 }}>
            <StatCard label="Clicks" value={compact(t.clicks)} icon="trending-up" deltaNote="GSC" />
            <StatCard label="Impressions" value={compact(t.impressions)} icon="bar-chart" deltaNote="GSC" />
            <StatCard label="Avg. CTR" value={ctrPct} icon="target" />
            <StatCard label="Avg. position" value={pos} icon="hash" />
            <StatCard label="Content plans" value={fmt(t.plans)} icon="file-text" />
            <StatCard label="Keywords" value={fmt(t.keywords)} icon="search" />
            <StatCard label="Competitors" value={fmt(t.competitors)} icon="users" />
            <StatCard label="Open to-dos" value={fmt(t.open_todos)} icon="check-square" />
          </div>
        </div>
        {/* The whole website as a reportable unit. Until this existed the
            console went from account totals straight to per-site rows, so a
            property, which is what a customer calls "my website", could not be
            read anywhere. Shown whenever there is more than one property, or
            more than one site inside one: with a single site it would just
            repeat the rollup above. */}
        {props.length > 0 && !picked && (props.length > 1 || props.some(p => p.sites > 1)) && (
          <div className="ml-section">
            <h2 className="ml-section__h">By property</h2>
            <p className="ml-section__sub">Each row counts its whole website once, however many sites it is split into.</p>
            <div className="hds-table-wrap">
              <table className="hds-table">
                <thead><tr>
                  <th style={{ textAlign: "left" }}>Property</th>
                  <th>Sites</th><th>Pages</th><th>Clicks</th><th>Impressions</th><th>CTR</th><th>Avg. pos</th><th>Plans</th><th>Keywords</th>
                </tr></thead>
                <tbody>
                  {props.map(p => (
                    <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setScope(String(p.id))}>
                      <td style={{ textAlign: "left" }}>
                        <b>{p.name}</b>
                        {/* A page claimed by two sites of one property means their
                            scopes overlap. Reported rather than quietly deduped:
                            the figures are right now, the scope behind them is not. */}
                        {p.overlap_pages > 0 && (
                          <span className="hds-badge hds-badge--warning" style={{ marginLeft: 8 }}
                            title="These pages are claimed by more than one site in this property, so some work and some reporting is being done twice. Narrow a site's page scope under Performance.">
                            <span className="hds-badge__dot" />{p.overlap_pages} page{p.overlap_pages === 1 ? "" : "s"} counted twice
                          </span>
                        )}
                      </td>
                      <td>{fmt(p.sites)}</td><td>{fmt(p.pages)}</td><td>{compact(p.clicks)}</td><td>{compact(p.impressions)}</td>
                      <td>{p.ctr != null ? (p.ctr * 100).toFixed(1) + "%" : "-"}</td>
                      <td>{p.position != null ? Number(p.position).toFixed(1) : "-"}</td>
                      <td>{fmt(p.plans)}</td><td>{fmt(p.keywords)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="ml-section">
          <div className="hds-card hds-card--pad">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-primary)" }}>Clicks by site{picked ? " · " + label : ""}</div>
              <div className="ml-legendrow"><span><i style={{ background: "var(--primary)" }} />Clicks (28d)</span></div>
            </div>
            {bySite.length === 0 ? (
              <div style={{ padding: "20px 0", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>No Search Console clicks recorded yet. Connect & sync GSC under Performance to populate this.</div>
            ) : (
              <div className="ml-bars">
                {bySite.map(s => (
                  <div className="ml-bars__col" key={s.site_id}><span className="ml-bars__val">{compact(s.clicks)}</span><div className="ml-bars__bar" style={{ height: (s.clicks/max*100) + "%" }} /><span className="ml-bars__label">{s.name}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function Users() {
    const { Button, IconButton } = DS();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);   // {type:"invite"} | {type:"remove",m} | {notice}
    const load = useCallback(() => {
      setLoading(true); setErr(null);
      api("/account/members").then(d => { setData(d); setLoading(false); }).catch(e => { setErr(e); setLoading(false); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const canManage = data && (data.role === "owner" || data.role === "admin");
    const isOwner = data && data.role === "owner";
    const ROLE_OPTS = [{ value: "owner", label: "Owner" }, { value: "admin", label: "Admin" }, { value: "editor", label: "Editor" }, { value: "member", label: "Member" }, { value: "viewer", label: "Viewer" }];

    if (loading) return <Loading />;
    if (err) return <ErrorNote err={err} />;
    const members = (data && data.members) || [];
    const invites = (data && data.invites) || [];
    const seats = (data && data.seats) || {};
    const limitTxt = seats.limit == null ? "Unlimited" : seats.limit;
    const pct = seats.limit ? Math.min(100, Math.round((seats.used / seats.limit) * 100)) : 0;
    const initialsOf = (m) => ((m.name || m.email || "?").match(/\b\w/g) || ["?"]).join("").slice(0, 2).toUpperCase();
    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h2 className="ml-section__h">Members &amp; seats</h2><p className="ml-section__sub">Manage who can access this account.</p></div>
          {canManage && <div className="ml-pagehead__actions"><Button variant="primary" leadingIcon="plus" onClick={() => setModal({ type: "invite" })}>Invite member</Button></div>}
        </div>
        <div className="hds-card hds-card--pad" style={{ marginBottom: 20 }}>
          <div className="ml-seats">
            <div><div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>{fmt(seats.used)} of {limitTxt} seat{seats.used === 1 ? "" : "s"} used</div><div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textTransform: "capitalize" }}>{seats.plan || "-"} plan</div></div>
            <div className="ml-seats__bar"><div className="ml-progress"><div className="ml-progress__bar" style={{ width: pct + "%" }} /></div></div>
          </div>
        </div>
        <div className="hds-table-wrap">
          <table className="hds-table">
            <thead><tr><th>Member</th><th>Role</th><th>Status</th><th style={{ width: 60 }}></th></tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="hds-avatar hds-avatar--sm">{initialsOf(m)}</span><div><div className="ml-rowtitle">{m.name || m.email}{m.self && <span className="hds-badge hds-badge--primary" style={{ marginLeft: 6 }}>You</span>}</div><div className="ml-rowsub">{m.email}</div></div></div></td>
                  <td>{roleBadge(m.role.charAt(0).toUpperCase() + m.role.slice(1))}</td>
                  <td><span className="hds-badge hds-badge--success"><span className="hds-badge__dot" />Active</span></td>
                  <td>{canManage && !m.self && <div className="ml-rowactions">{isOwner && <IconButton icon="edit" label="Change type" size="sm" onClick={() => setModal({ type: "role", m })} />}<IconButton icon="trash" label="Remove" size="sm" onClick={() => setModal({ type: "remove", m })} /></div>}</td>
                </tr>
              ))}
              {invites.map((iv) => (
                <tr key={"i" + iv.id} style={{ opacity: 0.75 }}>
                  <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="hds-avatar hds-avatar--sm"><MlIcon name="mail" size={14} /></span><div><div className="ml-rowtitle">{iv.email}</div><div className="ml-rowsub">Pending invitation</div></div></div></td>
                  <td>{roleBadge(iv.role.charAt(0).toUpperCase() + iv.role.slice(1))}</td>
                  <td><span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />Invited</span></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {modal && modal.type === "invite" && <PromptModal title="Invite a member" sub="They'll get an email invite to join this account." confirmLabel="Send invite"
          fields={[
            { key: "email", label: "Email", type: "email", icon: "mail", placeholder: "name@company.com" },
            { key: "role", label: "User type", type: "select", value: "member", options: [{ value: "member", label: "Member" }, { value: "editor", label: "Editor" }, { value: "viewer", label: "Viewer" }, { value: "admin", label: "Admin" }] },
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { if (!v.email || !v.email.trim()) throw new Error("Email is required."); await api("/account/members/invite", { method: "POST", body: JSON.stringify({ email: v.email.trim(), role: v.role || "member" }) }); setModal(null); load(); }} />}
        {modal && modal.type === "role" && <PromptModal title="Change user type" sub={modal.m.email} confirmLabel="Update"
          fields={[{ key: "role", label: "User type", type: "select", value: modal.m.role, options: ROLE_OPTS }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { try { await api(`/account/members/${modal.m.user_id}`, { method: "PATCH", body: JSON.stringify({ role: v.role }) }); setModal(null); load(); } catch (e) { throw new Error((e && e.message) || "Couldn't update."); } }} />}
        {modal && modal.type === "remove" && <ConfirmDialog danger title="Remove member" body={`Remove ${modal.m.email} from this account? They'll lose access immediately.`} confirmLabel="Remove"
          onClose={() => setModal(null)}
          onConfirm={async () => { try { await api(`/account/members/${modal.m.user_id}`, { method: "DELETE" }); setModal(null); load(); } catch (e) { setModal({ notice: true, title: "Couldn't remove member", body: (e && e.message) || "Action failed." }); } }} />}
        {modal && modal.notice && <NoticeModal title={modal.title} body={modal.body} onClose={() => setModal(null)} />}
      </div>
    );
  }

  function Approvals() {
    const { Button } = DS();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const load = useCallback(() => { setLoading(true); setErr(null); api("/account/approvals").then(d => { setData(d); setLoading(false); }).catch(e => { setErr(e); setLoading(false); }); }, []);
    useEffect(() => { load(); }, [load]);
    const decide = async (id, action) => {
      setBusy(true);
      try { await api(`/account/approvals/${id}/${action}`, { method: "POST", body: "{}" }); load(); }
      catch (e) { setNotice({ title: "Action failed", body: (e && e.message) || "Please try again." }); }
      finally { setBusy(false); }
    };
    if (loading) return <Loading />;
    if (err) return <ErrorNote err={err} />;
    const items = (data && data.approvals) || [];
    const canDecide = data && data.can_decide;
    return (
      <div className="ml-section">
        {notice && <NoticeModal title={notice.title} body={notice.body} onClose={() => setNotice(null)} />}
        <h2 className="ml-section__h">Pending approvals</h2><p className="ml-section__sub">Delegated actions captured for your sign-off. Approving authorizes the action.</p>
        {items.length === 0 ? (
          <div className="hds-card hds-card--pad" style={{ textAlign: "center", color: "var(--text-muted)" }}>No pending approvals.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 760 }}>
            {items.map(it => {
              const isDelete = /delete|trash/i.test((it.method || "") + " " + (it.path || ""));
              return (
                <div className="ml-approval" key={it.id}>
                  <span className="ml-approval__icon"><MlIcon name={isDelete ? "trash" : "check-circle"} size={18} /></span>
                  <div className="ml-approval__meta"><div className="ml-approval__title">{it.description || ((it.method || "") + " " + (it.path || ""))}</div><div className="ml-approval__sub">{it.requested_email ? "Requested by " + it.requested_email : "Requested"} · {timeAgo(it.created_at)}</div></div>
                  {canDecide && <div className="ml-approval__actions"><Button variant="ghost" size="sm" disabled={busy} onClick={() => decide(it.id, "reject")}>Reject</Button><Button variant="primary" size="sm" disabled={busy} onClick={() => decide(it.id, "approve")}>Approve</Button></div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function Settings({ go }) {
    const { Button, Field, Input } = DS();
    const [acct, setAcct] = useState(null);
    const [key, setKey] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [modal, setModal] = useState(null);   // {type} | {notice,title,body}
    const load = useCallback(async () => {
      const [a, k, s] = await Promise.all([
        api("/account").catch(() => null),
        api("/account/ai-key").catch(() => null),
        api("/account/sessions").catch(() => ({ sessions: [] })),
      ]);
      setAcct(a); setKey(k); setSessions((s && s.sessions) || []);
      if (a && a.account) setName(a.account.name || "");
    }, []);
    useEffect(() => { load(); }, [load]);
    const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
    const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { setModal({ notice: true, title: "Action failed", body: (e && e.message) || "Please try again." }); } finally { setBusy(false); } };
    const saveName = () => run(async () => { await api("/account", { method: "PATCH", body: JSON.stringify({ name }) }); flash("Account name saved."); });

    const canManage = acct && (acct.role === "owner" || acct.role === "admin");
    const twofaOn = acct && acct.twofa && acct.twofa.enabled;
    return (
      <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 24 }}>
        {msg && React.createElement(DS().Banner, { tone: "success", title: msg })}
        <div className="hds-card">
          <div className="hds-card__header"><div className="hds-card__title">Account</div></div>
          <div className="hds-card__body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Account name"><Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} /></Field>
            {canManage && <div><Button variant="primary" size="sm" disabled={busy} onClick={saveName}>Save</Button></div>}
            <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div><div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Password</div><div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Change the password for your sign-in.</div></div>
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => setModal({ type: "password" })}>Change password</Button>
            </div>
          </div>
        </div>

        <div className="hds-card">
          <div className="hds-card__header"><div className="hds-card__title">Credentials</div></div>
          <div className="hds-card__body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className={"ml-connect" + (key && key.set && (key.readable || key.masked) ? " ml-connect--done" : "")}><span className="ml-connect__logo"><MlIcon name="sparkles" size={22} /></span><div className="ml-connect__meta"><div className="ml-connect__name">Anthropic API key</div><div className="ml-connect__sub">{key && key.set ? ((key.readable || key.masked) ? (key.masked || "Key stored") : "Stored but unreadable. Re-save your key.") : "Not set - add your own key to run AI actions"}</div></div>{canManage && <div style={{ display: "flex", gap: 8 }}>{key && key.set && <Button variant="ghost" size="sm" disabled={busy} onClick={() => setModal({ type: "removeKey" })}>Remove</Button>}<Button variant="secondary" size="sm" disabled={busy} onClick={() => setModal({ type: "key" })}>{key && key.set ? ((key.readable || key.masked) ? "Replace" : "Re-save key") : "Add key"}</Button></div>}</div>
            <div className="ml-connect ml-connect--done"><span className="ml-connect__logo"><MlIcon name="globe" size={22} /></span><div className="ml-connect__meta"><div className="ml-connect__name">WordPress connections</div><div className="ml-connect__sub">{acct ? fmt(acct.sites) + " site" + (acct.sites === 1 ? "" : "s") + " connected" : "-"}</div></div><Button variant="secondary" size="sm" onClick={() => go && go("sites")}>Manage</Button></div>
          </div>
        </div>

        <div className="hds-card">
          <div className="hds-card__header"><div className="hds-card__title">Sessions &amp; 2FA</div></div>
          <div className="hds-card__body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {twofaOn ? <span className="hds-badge hds-badge--success"><span className="hds-badge__dot" />2FA on</span> : <span className="hds-badge hds-badge--neutral"><span className="hds-badge__dot" />2FA off</span>}
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Authenticator app (TOTP)</span>
              <div style={{ flex: 1 }} />
              {twofaOn ? <Button variant="ghost" size="sm" disabled={busy} onClick={() => setModal({ type: "disable2fa" })}>Disable</Button> : <Button variant="secondary" size="sm" disabled={busy} onClick={() => setModal({ type: "enable2fa" })}>Enable 2FA</Button>}
            </div>
            <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}><div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Active sessions ({sessions.length})</div><Button variant="ghost" size="sm" disabled={busy || sessions.length < 2} onClick={() => setModal({ type: "signout" })}>Sign out others</Button></div>
              {sessions.map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
                  <span className="ml-connect__logo" style={{ width: 32, height: 32 }}><MlIcon name="globe" size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>{(s.user_agent || "Unknown device").slice(0, 60)}{s.current && <span className="hds-badge hds-badge--primary" style={{ marginLeft: 6 }}>This device</span>}</div><div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{s.ip || "-"} · started {timeAgo(s.created_at)}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {modal && modal.type === "key" && <PromptModal title={key && key.set ? "Replace Anthropic API key" : "Add Anthropic API key"} sub="Stored encrypted and never shown again." confirmLabel="Save key"
          fields={[{ key: "k", label: "Anthropic API key", placeholder: "sk-ant-…", icon: "sparkles" }]}
          onClose={() => setModal(null)}
          onSubmit={async ({ k }) => { if (!k || !k.trim()) throw new Error("Paste your key."); const r = await api("/account/ai-key", { method: "POST", body: JSON.stringify({ key: k.trim() }) }); setKey({ set: true, masked: r.masked }); setModal(null); flash("API key saved."); }} />}
        {modal && modal.type === "removeKey" && <ConfirmDialog danger title="Remove API key" body="Remove the stored Anthropic API key? AI actions will stop working until you add one again." confirmLabel="Remove"
          onClose={() => setModal(null)}
          onConfirm={async () => { try { await api("/account/ai-key", { method: "DELETE" }); setKey({ set: false, masked: null }); setModal(null); } catch (e) { setModal({ notice: true, title: "Couldn't remove key", body: (e && e.message) || "Action failed." }); } }} />}
        {modal && modal.type === "password" && <PromptModal title="Change password" confirmLabel="Update password"
          fields={[{ key: "current_password", label: "Current password", type: "password", icon: "key" }, { key: "new_password", label: "New password", type: "password", hint: "At least 8 characters" }]}
          onClose={() => setModal(null)}
          onSubmit={async (v) => { if (!v.current_password || !v.new_password) throw new Error("Both fields are required."); await api("/account/password", { method: "POST", body: JSON.stringify(v) }); setModal(null); flash("Password changed."); load(); }} />}
        {modal && modal.type === "signout" && <ConfirmDialog title="Sign out other sessions" body="Sign out of every other device? Your current session stays signed in." confirmLabel="Sign out others"
          onClose={() => setModal(null)}
          onConfirm={async () => { try { await api("/account/sessions/revoke-others", { method: "POST", body: "{}" }); setModal(null); load(); flash("Other sessions signed out."); } catch (e) { setModal({ notice: true, title: "Couldn't sign out sessions", body: (e && e.message) || "Action failed." }); } }} />}
        {modal && modal.type === "disable2fa" && <PromptModal title="Disable two-factor authentication" sub="Enter a current code from your authenticator to turn 2FA off." confirmLabel="Disable 2FA"
          fields={[{ key: "code", label: "6-digit code", placeholder: "123456" }]}
          onClose={() => setModal(null)}
          onSubmit={async ({ code }) => { if (!code || !code.trim()) throw new Error("Enter your current code."); await api("/account/2fa/disable", { method: "POST", body: JSON.stringify({ code: code.trim() }) }); setModal(null); load(); flash("2FA disabled."); }} />}
        {modal && modal.type === "enable2fa" && <TwoFaSetupModal onClose={() => setModal(null)} onDone={() => { load(); flash("2FA enabled."); }} />}
        {modal && modal.notice && <NoticeModal title={modal.title} body={modal.body} onClose={() => setModal(null)} />}
      </div>
    );
  }

  const DOC_TOPICS = {
    "getting-started": ["Getting started", <><h2>Getting started with Marketlens</h2><p>Marketlens connects your WordPress network, your Anthropic API key and Google Search Console into one content-operations console. This guide walks through your first week.</p><h3>The two engines</h3><p>Everything runs on two engines: <strong>Business Intelligence</strong> (your permanent knowledge graph of products, competitors, industry and partners) and <strong>Research</strong> (the content engine that consumes BI data to plan and produce content).</p><ul><li>Set your <code>Business Profile</code> and Market first.</li><li>Grow your BI modules from your site or the live web.</li><li>Plan content from the gaps Research surfaces.</li></ul></>],
    "draft-first": ["Draft-first publishing", <><h2>Draft-first publishing</h2><p>Every save lands in <strong>Draft</strong>. Publishing is always an explicit action, so nothing ships by accident.</p><blockquote>AI is assistive, not autonomous - every suggestion is reviewed by a human before it persists.</blockquote></>],
    "ai-keys": ["Bring your own AI key", <><h2>Bring your own AI key</h2><p>Marketlens never bundles AI usage. Add your Anthropic key under <code>Console → Settings → Credentials</code>. It's encrypted at rest and never logged.</p></>],
  };
  function Documentation() {
    const { Input } = DS();
    const [topic, setTopic] = useState("getting-started");
    return (
      <div className="ml-doclib">
        <div className="ml-doclib__nav">
          <div style={{ marginBottom: 10 }}><Input leadingIcon="search" placeholder="Search docs" /></div>
          <div className="ml-doclib__group">Basics</div>
          {Object.entries(DOC_TOPICS).map(([id, [label]]) => (
            <button key={id} className={"ml-doclib__item" + (topic === id ? " ml-doclib__item--active" : "")} onClick={() => setTopic(id)}>{label}</button>
          ))}
        </div>
        <div className="ml-prose">{DOC_TOPICS[topic][1]}</div>
      </div>
    );
  }

  // Full pricing ladder, mirrored from docs/PRICING.md (App · AI Agents · Fully
  // Managed). App-line plan ids match backend/lib/entitlements.js.
  const PRICING_LINES = [
    { key: "app", label: "App", tagline: "Do it yourself.", note: "Self-serve software. Bring your own Anthropic API key.", plans: [
      { id: "starter", name: "Starter", price: "€29", per: "/mo", feats: ["1 site (single)", "Posts & Pages, Keywords Planner, Content Planner", "200 keywords · 3 competitors / site", "5 research runs / month", "3 months GSC history", "Bring your own AI key"] },
      { id: "growth", name: "Growth", price: "€79", per: "/mo", popular: true, feats: ["1 site (single)", "Everything in Starter, plus:", "Full SEO & Performance (Audit, Bulk Meta, Internal Linking)", "Search Console + Business Intelligence + Research", "1,000 keywords · 15 competitors / site", "30 research runs / month · 16 months GSC"] },
      { id: "scale", name: "Scale", price: "€319", per: "/mo", note: "+ €69/mo per extra site", feats: ["Up to 5 sites (same network)", "Everything in Growth + priority support", "Paid extra users (scale with sites, €79/mo)", "5,000 keywords · 50 competitors / site", "150 research runs / month"] },
      { id: "enterprise", name: "Enterprise", price: "Custom", feats: ["Unlimited sites & users", "All features", "Custom limits, SLAs & onboarding"] },
    ]},
    { key: "agents", label: "AI Agents", tagline: "Let AI run it.", note: "Add-on, billed on top of an App plan and priced per site. Agents run the workflows on a schedule, with human approval gates.", plans: [
      { id: "copilot", name: "Co-Pilot", price: "€199", per: "/mo per site", feats: ["Scheduled research, capture & classification", "Weekly content suggestions", "Opportunity → plan drafts (you approve)", "Requires an active App plan"] },
      { id: "autopilot", name: "Autopilot", price: "€399", per: "/mo per site", popular: true, feats: ["Everything in Co-Pilot, plus:", "Autonomous drafting", "On-page & GSC optimisation loops", "Guardrails & approval gates"] },
    ]},
    { key: "managed", label: "Fully Managed", tagline: "We do it for you.", note: "Done-for-you by IMYBOSS, strategy, content production, SEO and reporting.", plans: [
      { id: "managed", name: "Managed", price: "€399", per: "/mo", feats: ["One single site, fully done-for-you", "App included", "Strategy + content + SEO + reporting"] },
      { id: "managed_plus", name: "Managed+", price: "€1,599", per: "/mo", popular: true, note: "+ €349/mo per extra site (same network)", feats: ["WordPress network, up to 5 sites included", "Everything in Managed across the network", "Dedicated content & SEO operations"] },
      { id: "managed_ent", name: "Enterprise", price: "Custom", feats: ["Custom scope, SLAs & dedicated team", "Contact us"] },
    ]},
  ];
  function Pricing() {
    const { Button } = DS();
    const { data } = useApi("/account");
    const currentPlan = (data && data.entitlements && data.entitlements.plan) || (data && data.account && data.account.plan) || null;
    const [line, setLine] = useState("app");
    const active = PRICING_LINES.find(l => l.key === line) || PRICING_LINES[0];
    const contact = (sub) => "mailto:hello@imyboss.com?subject=" + encodeURIComponent(sub);
    return (
      <div className="ml-section">
        <h2 className="ml-section__h">Plans</h2>
        <p className="ml-section__sub">A do-it-yourself → done-for-you ladder. Same platform underneath; the difference is who runs it. Prices in EUR.</p>

        <div className="ml-pricing-tabs">
          {PRICING_LINES.map(l => (
            <button key={l.key} className={"ml-pricing-tab" + (line === l.key ? " ml-pricing-tab--on" : "")} onClick={() => setLine(l.key)}>
              <span className="ml-pricing-tab__label">{l.label}</span>
              <span className="ml-pricing-tab__tag">{l.tagline}</span>
            </button>
          ))}
        </div>

        <p className="ml-section__sub" style={{ marginTop: 14 }}>{active.note}</p>
        <div className="ml-plans">
          {active.plans.map(p => {
            const isCurrent = line === "app" && currentPlan === p.id;
            const custom = p.price === "Custom";
            return (
              <div className={"ml-plan" + (isCurrent ? " ml-plan--current" : "") + (p.popular ? " ml-plan--popular" : "")} key={p.id}>
                {isCurrent ? <span className="ml-plan__badge">Current plan</span> : p.popular ? <span className="ml-plan__badge ml-plan__badge--popular">Popular</span> : null}
                <div className="ml-plan__name">{p.name}</div>
                <div className="ml-plan__price">{p.price}{!custom && <span> {p.per || "/mo"}</span>}</div>
                {p.note && <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: -6, marginBottom: 6 }}>{p.note}</div>}
                <ul className="ml-plan__feats">{p.feats.map(f => <li key={f}><MlIcon name="check" size={16} />{f}</li>)}</ul>
                {isCurrent
                  ? <Button variant="secondary" fullWidth disabled>Current plan</Button>
                  : <Button variant="primary" fullWidth onClick={() => { window.location.href = contact("MarketLens - " + active.label + " - " + p.name); }}>{custom ? "Contact sales" : "Contact us"}</Button>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function Billing({ go }) {
    const { Button } = DS();
    const { data, loading } = useApi("/account");
    if (loading) return <Loading />;
    const ent = data && data.entitlements;
    const planLabel = (ent && ent.label) || (data && data.account && data.account.plan) || "-";
    const status = data && data.account && data.account.status;
    const sites = data ? data.sites : 0;
    const sitesLimit = ent && ent.sites_limit;
    const seatsLimit = ent && ent.seats_limit;
    return (
      <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="hds-card hds-card--pad">
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginBottom: 4 }}>Current plan</div>
            <div className="ml-bignum" style={{ textTransform: "capitalize" }}>{planLabel}</div>
            {status && <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", margin: "2px 0 14px", textTransform: "capitalize" }}>Status: {status}</div>}
            <Button variant="secondary" size="sm" onClick={() => go && go("pricing")}>See plans</Button>
          </div>
          <div className="hds-card hds-card--pad">
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginBottom: 10 }}>Plan limits</div>
            <div className="ml-usage" style={{ gap: 12 }}>
              <div className="ml-usage__row"><span>Sites</span><b>{fmt(sites)}{sitesLimit != null ? " / " + fmt(sitesLimit) : " / ∞"}</b></div>
              <div className="ml-usage__row"><span>Seats</span><b>{seatsLimit != null ? fmt(seatsLimit) : "∞"}</b></div>
              {ent && ent.limits && ent.limits.keywords != null && <div className="ml-usage__row"><span>Keywords / site</span><b>{fmt(ent.limits.keywords)}</b></div>}
              {ent && ent.limits && ent.limits.competitors != null && <div className="ml-usage__row"><span>Competitors / site</span><b>{fmt(ent.limits.competitors)}</b></div>}
            </div>
          </div>
        </div>
        {React.createElement(DS().Banner, { tone: "info", title: "Online billing isn't connected yet" }, "Invoices, payment method and self-serve plan changes will appear here once billing is connected. To change your plan now, contact hello@imyboss.com.")}
      </div>
    );
  }

  // The org is owned by the ImyBoss panel, not by MarketLens: it is where the
  // name and the subscriptions live, and SD-005 maps one org to one account
  // here. So this shows the real org read-only and says who to go to.
  //
  // It used to be an editable form pre-filled with "Acme Co" / "acme.com", a
  // data-region selector for a choice that does not exist, and a Delete button
  // with no handler under the words "Permanently removes all sites, content and
  // members". Name editing already lives on the Account tab and is wired there.
  function Organisation() {
    const { Button, Field, Input, Badge } = DS();
    const [org, setOrg] = useState(undefined);   // undefined = loading, null = none
    useEffect(() => {
      let cancel = false;
      api("/auth/me").then(me => { if (!cancel) setOrg((me && me.org) || null); }).catch(() => { if (!cancel) setOrg(null); });
      return () => { cancel = true; };
    }, []);

    return (
      <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 24 }}>
        <div className="hds-card">
          <div className="hds-card__header"><div className="hds-card__title">Organisation</div></div>
          <div className="hds-card__body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {org === undefined ? <div className="ml-muted">Loading…</div>
              : !org ? React.createElement(DS().Banner, { tone: "info", title: "No ImyBoss organisation linked" },
                  "This account is not attached to a panel organisation, so there is nothing to show here yet.")
              : <>
                  <Field label="Organisation name" hint="Managed in the ImyBoss panel."><Input value={org.name || ""} readOnly disabled /></Field>
                  <Field label="Identifier" hint="Used in launch links and the workspace switcher."><Input value={org.slug || ""} readOnly disabled /></Field>
                  <Field label="MarketLens subscription">
                    {org.entitled
                      ? <Badge tone="success" dot>Active</Badge>
                      : <Badge tone="warning" dot>No active subscription</Badge>}
                  </Field>
                  {!org.entitled && React.createElement(DS().Banner, { tone: "warning", title: "No MarketLens subscription on this organisation" },
                    "Access is granted per organisation in the ImyBoss panel. Add MarketLens there to keep using it.")}
                </>}
          </div>
        </div>
      </div>
    );
  }


  // -- THE FOUR COVERAGES, named in one place --------------------------------
  //
  // One website, four different questions, and EVERY ONE of them is measured
  // against 100% OF THE PROPERTY. That is the correction this scaffold exists
  // to make visible: a site is one of the ways a property is covered, and it
  // was never the unit the coverage should have been measured in.
  //
  //   site    PUBLISHING coverage. How much of the property do we content
  //           manage through MarketLens, and how much do we only measure?
  //   blog    CONTENT TYPE coverage. Which kinds of content (the SiteAtlas
  //           categories) cover the property, and what is left uncovered?
  //   market  MARKET coverage. Language, and locality. Locality is not yet
  //           defined by the owner, so nothing here measures it.
  //   seo     SEO TRACKING coverage. How much of the property is tracked in
  //           search at all?
  //
  // SCAFFOLD, and deliberately numberless. Nothing measures any of these four
  // yet, so each reads "not measured yet" rather than 0%: a percentage a screen
  // made up would be read as a measurement, which is the exact failure the six
  // screens that stopped reporting zero as a fact were fixed for.
  // ── THE KINDS OF CONTENT A PROPERTY CAN HAVE ──────────────────────────────
  //
  // Content type coverage asks which kinds of content cover a property and
  // which kinds it has none of, so the kinds have to be a NAMED LIST: "what
  // this property does not have" is unanswerable against an open vocabulary.
  //
  // These five are the owner's, and they are not the same list as
  // SECTION_WORDS further down. That one (blog, documentation, help, shop) is
  // what the crawl GUESSES from a path, and it is a reading of a website.
  // This one is what a customer DECLARES. Keeping them apart is the same rule
  // the whole property model runs on: a declaration beats a derivation, and
  // the two must never be stored in the same field.
  const BLOG_KINDS = [
    ["blog",      "Blog"],
    ["news",      "News"],
    ["changelog", "Changelog"],
    ["docs",      "Docs"],
    ["help",      "Help"],
  ];
  const blogKindWord = (k) => (BLOG_KINDS.find(x => x[0] === k) || [null, "content"])[1];

  /**
   * Add a blog, which is to say: declare that a kind of content is served at an
   * address on this property.
   *
   * TWO FIELDS, in the order they are asked: the KIND first, because it decides
   * what the second field means and what the button says, then the URL where
   * that kind is served.
   *
   * NOTHING IS STORED YET, and the dialog says so before the typing rather
   * than after it. There is no table for a declared content type: what it is
   * counted over, and whether a kind is declared by the customer or read from
   * the SiteAtlas categories already on the install, are the owner's open
   * questions, and inventing a schema to hold an answer nobody has given is
   * how a guess becomes a migration. So this collects the declaration, shows
   * it back exactly as it would be saved, and names what it is waiting for.
   */
  function AddBlogDialog({ onClose, onDone, publicBase = null }) {
    const [notice, setNotice] = useState(null);
    if (notice) return <NoticeModal title={notice.title} body={notice.body} onClose={onClose} />;
    return (
      <PromptModal title="Add a blog"
        sub={v => `Declare that this property serves ${blogKindWord(v.kind).toLowerCase()} at an address. Nothing is stored yet: content type coverage has no table until two questions are answered, and this shows you exactly what it would save.`}
        confirmLabel={v => "Add " + blogKindWord(v.kind).toLowerCase()}
        fields={[
          { key: "kind", label: "What are you adding?", type: "select", value: "blog",
            options: BLOG_KINDS.map(([value, label]) => ({ value, label })),
            hint: "A kind of content, not a site. One property can serve several kinds, and the ones it serves none of are the gap this coverage is for." },
          { key: "url", label: "URL", icon: "globe",
            // The property's address as the PLACEHOLDER and never as the value:
            // a prefilled URL is an answer the customer did not give, and this
            // model has been bitten twice by a URL that decided something on
            // its shape alone.
            placeholder: publicBase ? String(publicBase).replace(/\/+$/, "") + "/blog" : "https://example.com/blog",
            hint: v => `Where ${blogKindWord(v.kind).toLowerCase()} is served to the public, which is the address its pages are under.` },
        ]}
        onClose={onClose}
        onSubmit={async (v) => {
          const kind = String(v.kind || "").trim();
          const url = String(v.url || "").trim();
          if (!BLOG_KINDS.some(([k]) => k === kind)) throw new Error("Choose what you are adding.");
          if (!url) throw new Error("A URL is required: it is where this content is served.");
          if (!/^https?:\/\//i.test(url)) throw new Error("The URL has to start with http:// or https://, because it is an address we fetch.");
          // Shown back rather than stored, and the sentence says which of the
          // two it is. A dialog that answered "added" would be the one lie
          // this whole scaffold exists to avoid.
          setNotice({
            title: `${blogKindWord(kind)} at ${url.replace(/^https?:\/\//, "")} is not saved yet`,
            body: `This is the declaration, exactly as it will be stored:\n\n  kind: ${kind}\n  url:  ${url}\n\n`
              + "What it is waiting on, both of them the owner's call. FIRST, what content type coverage is counted over: every page of the property, or every page a kind could apply to. SECOND, where a kind comes from: declared here by you, or read from the SiteAtlas categories already on the install, with this dialog correcting them.\n\n"
              + "Until those are answered there is nowhere to put this that would not be a guess about the model, so nothing was written. Connecting a site that PUBLISHES this content is Add site on the property, and that does work today.",
          });
          if (onDone) onDone();
        }} />
    );
  }

  // ── NATIONAL OR LOCAL, which is what locality turned out to mean ──────────
  //
  // Market coverage was always "language and locality", with locality undefined
  // and therefore unmeasurable. The owner's answer, 2026-10-23: a market is
  // either NATIONAL, scoped to a country, or LOCAL, scoped to a city. That is
  // the whole of it, and it is the first definition locality has had.
  //
  // It also sharpens what a market IS. A country was already the unit because
  // search is scoped to one: competitors, volumes and results all answer "in
  // which country". A city is the same argument one level down, and it is the
  // level a search like "accountant in Lyon" actually competes at.
  const MARKET_SCOPES = [
    ["national", "National", "A country. Search results, competitors and volumes all answer \u201cin which country\u201d, which is why this is the usual answer."],
    ["local",    "Local",    "A city, in a country. The level a search like \u201caccountant in Lyon\u201d competes at, which a national market cannot see."],
  ];
  const marketScopeWord = (v) => (MARKET_SCOPES.find(x => x[0] === v) || [null, "market"])[1];

  /**
   * Add a market: the scope, then the place that scope needs, then where it is
   * served.
   *
   * THE SCOPE COMES FIRST BECAUSE IT DECIDES THE SECOND FIELD. National asks
   * for a country, from the same list the property setup used; local asks for a
   * city, typed, because no list of cities is worth shipping and the customer
   * knows theirs.
   *
   * NOTHING IS STORED YET, and here the reason is sharper than it was for Add
   * blog, because a markets table DOES exist and writing to it would be worse
   * than having none:
   *
   *   `property_markets` has no scope and no city. A local market saved into it
   *   would silently become a national one with the city dropped, which is a
   *   lie about data the customer just typed.
   *
   *   It also carries `addressing` (none, path, subdomain, domain) plus an
   *   address, where this dialog asks for a URL. Turning one into the other
   *   means deciding, from the shape of a URL, whether it is a path under the
   *   property, a subdomain of it, or a different domain. That is precisely the
   *   inference this model has twice been burned by, and the market dimension
   *   is the thing it burned.
   *
   *   And the live property has two markets placing 1,610 keywords by longest
   *   declared address. A third row with no usable address does not fail
   *   loudly: it changes what the register answers, on the owner's own data.
   *
   * So it collects the declaration, shows it back exactly as it would be saved,
   * and names the one decision it waits on.
   */
  function AddMarketDialog({ onClose, onDone, publicBase = null }) {
    const [notice, setNotice] = useState(null);
    if (notice) return <NoticeModal title={notice.title} body={notice.body} onClose={onClose} />;
    return (
      <PromptModal title="Add a market"
        sub={v => (v.scope === "local"
          ? "A local market is a city, in a country: a search is run in a country, so the city alone cannot be searched. Nothing is stored yet, because the market register has no scope and no city column and saving one would quietly turn it into a national market."
          : "A national market is a country. Nothing is stored yet: this property's markets are placing live figures, and the register needs one more decision before a new row can join them.")}
        confirmLabel={v => "Add " + marketScopeWord(v.scope).toLowerCase() + " market"}
        fields={[
          { key: "scope", label: "National or local?", type: "select", value: "national",
            options: MARKET_SCOPES.map(([value, label]) => ({ value, label })),
            hint: v => (MARKET_SCOPES.find(x => x[0] === v.scope) || [null, null, ""])[2] },
          // THE COUNTRY IS ASKED FOR BOTH SCOPES, and the scope only decides
          // whether a CITY is asked for on top of it.
          //
          // Owner's correction, 2026-10-23: a local market needs its country
          // too. A city does not identify a market on its own, because a city
          // name is not unique and, more to the point, a search has to be RUN
          // somewhere: "accountant in Lyon" is a French search, and the same
          // query in another country returns another country's results. So a
          // local market is a city IN a country, and the country is the half
          // that makes it searchable at all.
          { key: "country", label: "Country", type: "select", value: "",
            options: [{ value: "", label: "Choose a country\u2026" }].concat(COUNTRIES.map(c => ({ value: c.code, label: c.name }))),
            hint: v => (v.scope === "local"
              ? "The country the city is in. A search is run in a country, so a city on its own cannot be searched: this is the half that makes it findable."
              : "The country this market serves. Its language rides along and is not what decides the market.") },
          { key: "city", label: "City", icon: "compass", when: v => v.scope === "local",
            placeholder: "Lyon",
            hint: "The city this market serves, as a customer there would name it." },
          { key: "url", label: "URL", icon: "globe",
            placeholder: publicBase ? String(publicBase).replace(/\/+$/, "") + "/fr" : "https://example.com/fr",
            hint: v => `Where this ${marketScopeWord(v.scope).toLowerCase()} market is served. A row learns its market from a declared address like this one, never from the shape of a URL.` },
        ]}
        onClose={onClose}
        onSubmit={async (v) => {
          const scope = String(v.scope || "").trim();
          if (!MARKET_SCOPES.some(([k]) => k === scope)) throw new Error("Say whether this market is national or local.");
          const url = String(v.url || "").trim();
          const country = String(v.country || "").trim();
          const city = String(v.city || "").trim();
          // The country is required either way. A local market without one is
          // a city nobody can search for, which is worse than no market.
          if (!country) throw new Error(scope === "local"
            ? "A local market needs its country too: a search is run in a country, so a city on its own cannot be searched."
            : "A national market needs its country.");
          if (scope === "local" && !city) throw new Error("A local market needs its city.");
          if (!url) throw new Error("A URL is required: a market is placed by its declared address.");
          if (!/^https?:\/\//i.test(url)) throw new Error("The URL has to start with http:// or https://, because it is an address we fetch.");
          const countryName = (COUNTRIES.find(c => c.code === country) || {}).name || country;
          const place = scope === "national" ? countryName : `${city}, ${countryName}`;
          setNotice({
            title: `${marketScopeWord(scope)} market for ${place} is not saved yet`,
            body: `This is the declaration, exactly as it will be stored:\n\n`
              + `  scope:   ${scope}\n`
              + `  country: ${country} (${countryName})\n`
              + (scope === "local" ? `  city:    ${city}\n` : "")
              + `  url:     ${url}\n\n`
              + "What it waits on, and it is one decision rather than two. The register this would join (property_markets) has no scope and no city, and it records how a market is reached as a KIND plus an address (a path, a subdomain, a domain) rather than as a URL. So it has to gain a scope and a city, and it has to be settled whether a declared URL replaces that pair or is stored beside it.\n\n"
              + (scope === "local"
                  ? "Saving this one today would drop the city and call it a national market, which is a lie about what you just typed.\n\n"
                  : "")
              + "This property's existing markets place 1,610 keywords by longest declared address, so a new row joining them before that is settled would change live answers quietly rather than failing loudly. Everything already declared is untouched.",
          });
          if (onDone) onDone();
        }} />
    );
  }

  const COVERAGES = [
    ["site",   "Publishing",   "globe",     "sites",   "How much of this property do we content manage through MarketLens, and how much do we only measure?"],
    ["blog",   "Content type", "folder",    "blogs",   "Which kinds of content cover this property, and what is left uncovered?"],
    ["market", "Market",       "flag",      "markets", "Which languages and which localities cover this property?"],
    ["seo",    "SEO tracking", "bar-chart", null,      "How much of this property is tracked in search at all?"],
  ];

  /** The four coverages as one strip. With no `active` every card is level,
      which is the property page's case: all four are coverages OF it and none
      of them is the screen you are on. Carries no figures, because none of the
      four is measured yet; `universe` only names the denominator they will all
      be counted over, and is omitted when nothing has counted it. */
  function CoverageFrame({ active = null, go, universe = null, fromProperty = null }) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, margin: "16px 0 24px" }}>
        {COVERAGES.map(([id, label, icon, tab, question]) => {
          // Level when nothing is active, which is the property page: all four
          // are coverages OF it. A card with a screen is clickable unless it IS
          // the screen you are on.
          const on = active == null || id === active;
          const clickable = !!(go && tab && (active == null || id !== active));
          return (
            <div key={id}
              className="hds-card hds-card--pad"
              onClick={clickable ? () => go(tab, fromProperty ? { fromProperty } : null) : undefined}
              style={{
                cursor: clickable ? "pointer" : "default",
                opacity: on ? 1 : 0.72,
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <MlIcon name={icon} size={16} />
                <b style={{ fontSize: "var(--text-sm)" }}>{label}</b>
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.5 }}>{question}</div>
              <div style={{ marginTop: 8, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                {/* The denominator is named and the figure is not, which is the
                    honest pair: the rows exist and have been counted, the four
                    facts about each row have not. */}
                {universe != null ? `Not measured yet, of ${fmt(universe)} pages` : "Not measured yet"}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Blogs: CONTENT TYPE coverage of the property. Empty on purpose. Wiring it
  // to today's data would mean listing connected sites again under a new name,
  // which is the confusion being undone rather than a measurement of anything.
  function Blogs({ go }) {
    const { Banner, Button } = DS();
    // This screen stands above every property, so it has no public base to
    // offer the URL field as a placeholder: that is a fact about one property
    // and this is not on one. The property's own Add blog does offer it.
    const [adding, setAdding] = useState(false);
    return (
      <div className="ml-section">
        {adding && <AddBlogDialog onClose={() => setAdding(false)} />}
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h2 className="ml-section__h">Blogs</h2>
            <p className="ml-section__sub">Content type coverage, measured against the whole property: which kinds of content cover it, and which kinds it has none of.</p>
          </div>
          <div className="ml-pagehead__actions">
            <Button variant="primary" leadingIcon="plus"
              onClick={() => setAdding(true)}
              title="Declare that a kind of content is served at an address on this property.">
              Add blog
            </Button>
          </div>
        </div>
        <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 12 }}>
          <Banner tone="info" title="Nothing is measured here yet, and that is the honest state.">
            A blog is a connected site today, which is why Add blog opened the connect window. Under the four coverages a blog is not a site at all: it is a KIND of content covering some share of the property, the share a SiteAtlas category stands for. Until that is settled this screen measures nothing, rather than renaming the site list.
          </Banner>
          <Banner tone="warning" title="Two questions to answer before this can measure anything.">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>What is the denominator of content type coverage: every page of the property, or every page a content type could apply to?</li>
              <li>Is a content type declared by the customer, or read from the SiteAtlas categories already on the install?</li>
            </ul>
            <p style={{ margin: "10px 0 0" }}>
              <b>Add blog asks its two questions already</b>, the kind and the URL, and shows the declaration back instead of saving it: the second question above is what decides where it goes, and a table built before that answer would be a guess with a migration attached.
            </p>
          </Banner>
          {go && <div><Button variant="secondary" leadingIcon="globe" onClick={() => go("sites")}>See the connected sites instead</Button></div>}
        </div>
      </div>
    );
  }

  // Markets: LANGUAGE and LOCALITY coverage of the property. Markets already
  // exist and are already declared (two addresses on the property's setup,
  // longest match wins), so this screen does NOT invent a second register for
  // them: it points at the one that exists and says what is missing from it.
  function Markets({ go }) {
    const { Banner, Button } = DS();
    // No property here, so no public base to offer the URL field: this screen
    // stands above every property. The property's own Add market offers it.
    const [adding, setAdding] = useState(false);
    return (
      <div className="ml-section">
        {adding && <AddMarketDialog onClose={() => setAdding(false)} />}
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h2 className="ml-section__h">Markets</h2>
            <p className="ml-section__sub">Market coverage, measured against the whole property: which languages and which localities it reaches.</p>
          </div>
          <div className="ml-pagehead__actions">
            {/* A market is declared on ONE property's setup, as an address, and
                this screen stands above every property, so the button cannot
                open that window without first being told which property. It
                goes where that choice is made, and the property page's own Add
                market opens the window directly. */}
            <Button variant="primary" leadingIcon="plus"
              onClick={() => setAdding(true)}
              title="Declare a market: national, meaning a country, or local, meaning a city, and where it is served.">
              Add market
            </Button>
          </div>
        </div>
        <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 12 }}>
          <Banner tone="info" title="The markets themselves already exist. The COVERAGE does not.">
            A property declares its markets as addresses, and a row learns its market by the longest matching address, never from the shape of a URL. What has never been measured is the share: how much of the property each market actually covers, and how much of it belongs to no market at all.
          </Banner>
          <Banner tone="warning" title="Locality now has a definition: a local market is a CITY, in a country.">
            Decided 2026-10-23, which is the first definition it has had. A market is either national, scoped to a country, because search results and competitors answer &ldquo;in which country&rdquo;, or local, scoped to a city, which is the level a search like &ldquo;accountant in Lyon&rdquo; competes at and a national market cannot see. The country is required for both: a search is RUN in a country, so a city on its own cannot be searched at all. <b>Add market asks for all of it.</b> What is still missing is the register: <code>property_markets</code> has neither a scope nor a city, and it records how a market is reached as a kind plus an address rather than as a URL, so a local market saved into it today would silently become a national one with the city dropped.
          </Banner>
        </div>
      </div>
    );
  }

  const TABS = [
    ["properties", "Properties", Properties],
    ["sites", "Sites", Sites], ["blogs", "Blogs", Blogs], ["markets", "Markets", Markets],
    ["reports", "Reports", Reports], ["users", "Users", Users],
    ["approvals", "Approvals", Approvals], ["settings", "Settings", Settings],
    ["docs", "Documentation", Documentation], ["pricing", "Pricing", Pricing],
    ["billing", "Billing", Billing], ["org", "Organisation", Organisation],
  ];

  const NAV = [
    { label: "Manage", items: [["properties", "Properties", "layers"], ["sites", "Sites", "globe"],
      // Under Sites on purpose: publishing, content type and market are three
      // coverages of the SAME property, so they sit together rather than one per
      // group. See COVERAGES above.
      ["blogs", "Blogs", "folder"], ["markets", "Markets", "flag"],
      ["reports", "Reports", "bar-chart"], ["users", "Users", "users"], ["approvals", "Approvals", "check-square"]] },
    { label: "Account", items: [["settings", "Settings", "settings"], ["billing", "Billing", "credit-card"], ["pricing", "Pricing", "shopping-bag"], ["org", "Organisation", "building"]] },
    { label: "Help", items: [["docs", "Documentation", "file-text"]] },
  ];

  function ConsoleApp({ onExit, onOpenSite, initialTab, onTab, user, onLogout, onFoundations, readOnly, panelWs, panelWsList, onPickPanelWs }) {
    const [tab, setTab] = useState(initialTab || "properties");
    // Optional payload for the target tab (e.g. { property: rootDomain } so
    // Sites lands on that property's group). Cleared on every switch, so only
    // the navigation that set it sees it.
    const [focus, setFocus] = useState(null);
    // The property you were last looking at, so Back to property on a coverage
    // tab returns to THAT property and not to the list of them. Remembered
    // from the navigation that left it, which is the only place that knows: a
    // coverage tab is account wide and holds no property of its own.
    const [lastProp, setLastProp] = useState(null);   // { id, name } | null
    // onTab tells the shell which tab is showing, so the URL hash (refresh
    // persistence) follows console navigation too.
    const go = (id, f) => {
      if (f && f.fromProperty && f.fromProperty.id != null) setLastProp(f.fromProperty);
      setFocus(f || null); setTab(id); if (onTab) onTab(id);
    };
    // The three coverages of a property. Named here because the topbar's way
    // out depends on it: see `up` below.
    const COVERAGE_TABS = ["sites", "blogs", "markets"];
    // Back to property, on exactly those three. With a remembered property it
    // reopens that one; with none it opens the properties, which is where a
    // property is chosen. The title says which of the two will happen rather
    // than leaving the label to carry both.
    const up = COVERAGE_TABS.includes(tab)
      ? {
          label: "Back to property",
          title: lastProp
            ? `Back to ${lastProp.name || "the property"}, whose coverage this is.`
            : "Open the properties: this is a coverage of a property, and no property is open yet.",
          onClick: () => go("properties", lastProp ? { open: lastProp.id } : null),
        }
      : null;
    const Active = TABS.find(t => t[0] === tab)[2];
    // AI assistance in the console. The assistant addresses a SITE, and most
    // console tabs are account-level with no site to address, so a screen
    // OFFERS a context and the topbar trigger exists only while one is on
    // offer. The property screen offers its primary site; leaving it clears
    // the offer and closes the panel with it.
    const [back, setBack] = useState(null);            // { label, onClick } | null
    const offerBack = useCallback((b) => setBack(b || null), []);
    const [assist, setAssist] = useState(null);        // { site, moduleId } | null
    const [assistOpen, setAssistOpen] = useState(false);
    const offerAssist = useCallback((ctx) => {
      setAssist(ctx || null);
      if (!ctx) setAssistOpen(false);
    }, []);
    const AiAssistPanel = window.MlAiDrawer && window.MlAiDrawer.AiAssistPanel;
    const showAssist = !!(assistOpen && assist && AiAssistPanel && !readOnly);
    // The suite workspace switcher lives in the app sidebar; mirror it here so
    // the console (where an org launch / workspace switch lands, SD-005) can
    // also switch workspaces without first crossing back into the app.
    const PanelWsSwitcher = window.MlShell && window.MlShell.PanelWsSwitcher;
    return (
      <div className={"ml-shell" + (readOnly ? " ml-readonly" : "") + (showAssist ? " ml-shell--assist" : "")}>
        <aside className="hds-sidebar ml-sidebar" style={{ width: 264 }}>
          <div className="hds-sidebar__brand"><button type="button" onClick={onExit} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}><Brand /></button></div>
          {PanelWsSwitcher && <PanelWsSwitcher list={panelWsList} active={panelWs} onPick={onPickPanelWs} />}
          <nav className="hds-sidebar__nav" style={{ marginTop: 12 }}>
            {NAV.map((g) => (
              <div className="hds-navgroup" key={g.label}>
                <div className="hds-navgroup__label">{g.label}</div>
                {g.items.map(([id, label, icon]) => (
                  <a key={id} href="#" className={"hds-navitem " + (tab === id ? "hds-navitem--active" : "")} aria-current={tab === id ? "page" : undefined} onClick={(e) => { e.preventDefault(); go(id); }}>
                    <MlIcon name={icon} size={18} /><span>{label}</span>
                  </a>
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <div className="ml-shell__main">
          <ConsoleTopBar onExit={onExit} user={user} onLogout={onLogout} onFoundations={onFoundations} go={go} readOnly={readOnly}
            assist={assist} assistOpen={assistOpen} onToggleAssist={() => setAssistOpen(v => !v)} back={back} up={up} />
          <main className="ml-shell__content" style={{ paddingTop: 28 }}>
            <Active onExit={onExit} onOpenSite={onOpenSite} go={go} focus={focus} onOfferAssist={offerAssist} onOfferBack={offerBack} />
          </main>
        </div>
        {/* A column, not a modal: the screen beside it stays live, so a
            suggestion about this website's structure can be acted on while it
            is still on screen. */}
        {showAssist && <AiAssistPanel site={assist.site} moduleId={assist.moduleId} onClose={() => setAssistOpen(false)} />}
      </div>
    );
  }

  window.MlConsole = { ConsoleApp };
})();
